/**
 * DmpConnect-JS JavaScript wrapper.
 * Copyright (c) 2016, icanopee SAS. All right reserved.
 *
 * Version 0.9.9
 */

/** Return a string holding the object properties in HTML.
 *
 * @param {object} o     The object to dump.
 * @param {string} shift Offset to apply to the display.
 */
function printObject( o, shift )
{
    shift = shift || "";
    var defaultSpacing = "  ";

    var out = shift + '{\n';
    for ( var p in o )
    {
        var value = o[p];

        if( o[p] !== null && typeof o[p] === 'object' )
            // Recursive call to get the object properties
            value = printObject( o[p], shift + defaultSpacing );

        out += defaultSpacing + shift + '"' + p + '" : ' + value + '\n';
    }
    out += shift + "}\n";
    return out
}


const INITIAL_STATE = {
    session: false,
    PcscReaders: false,
    cpxCard: false,
    cpxCardRead: false,
    vitaleCard: false,
    vitaleCardRead: false,
    DmpConnector: false,
};
const INITIAL_DMP_STATE = {
    certifiedIdentity: false,
    directAuthenticationStatus: false,
};


/*
    Indicate if commands should be sent using WebSocket or using POST method.

    @retval 1   If commands should be sent using WebSocket 
    @retval 0   If commands should be sent using POST method to the /api entry point.

 */
function sendCommandUsingWebSocket( )
{
    var sendUsingWsBtn = document.getElementById( "btnSendCommandUsingWebSocket" );
    if( sendUsingWsBtn )
    {
        return sendUsingWsBtn.checked ? 1 : 0 ;
    }
    else 
    {
        // Default to true if button is not present.
        return 1; 
    }
}


/**
 * DMPConnect class.
 *
 * Parameter names conventions
 *    Prefix "s_": string.
 *    Prefix "i_": integer.
 *    Prefix "f_": float.
 *
 * Common input command parameters:
 *   "s_name"      : Command identifier. Mandatory.
 *   "s_sessionId" : Input parameter that is mandatory for all commands except hl_openSession(). Returned by hl_openSession().
 *
 * Output parameters:
 *   "s_status" : Always returned. Can be "OK", "ERROR" or "API_ERROR".
 *
 *   If s_status == "ERROR" or s_status == "API_ERROR"
 *     "s_errorDescription" : A description of the error.
 *
 *   If status == "API_ERROR"
 *     "i_apiErrorType"                 : See ErrorType in dmp_api_errors.h.
 *     "s_apiErrorTypeDescription"      : It is the a textual description of the error type.
 *     "i_apiErrorCode"                 : See ApiErrorCode in dmp_api_errors.h.
 *     "s_apiErrorDescription"          : Description of the error.
 *     "s_apiErrorContext"              : DmpConnect function that raised the error.
 *     "s_apiErrorExtendedInformations" : More informations about the problem.
 *
 *   If s_status == "OK" and the command wraps an API function:
 *     "i_returnValue" : The return value of the original function.
 */

/**
 * Constructor.
 *
 * @param    {function}     logCallbackFunc     Log function to use during methods execution.
 * @param    {function}     refreshCallbackFunc Function called when the internal object state has been updated.
 *                                               i.e. for example, when the session id has been set after handling hl_openSession
 *                                               or hl_closeSession.
 * @param    {function}     errorHandler        Function called when an error is raised.
 * @param    {string}       localServerPort     Optional. Websockets server port number. Default: "9982".
 * @param    {string}       localServerDomain   Websockets server domain name. Eg. "localhost.icanopee.net".
 */
function DMPConnect( logCallbackFunc, refreshCallbackFunc, errorHandler, initCallback, localServerPort, localServerDomain )
{
    // Default value.
    localServerPort = localServerPort || 9982;

    this._port            = localServerPort;
    this._sessionId       = "";
    this._logCallback     = logCallbackFunc;
    this._refreshCallback = refreshCallbackFunc;
    this._errorHandler    = errorHandler;
    this._serverDomain    = localServerDomain;
    this._serverPort      = localServerPort;

    if( localServerDomain )
    {
        this._serverUrl = "wss://" + localServerDomain + ":" + localServerPort;
        if( initCallback )
            initCallback( this._serverUrl );
    }

    this._state = { ...INITIAL_STATE };
    this._dmpState = { ...INITIAL_DMP_STATE };
};

DMPConnect.prototype.getState = function() {
  return { ...this._state, dmp: { ...this._dmpState } };
};
DMPConnect.prototype.setState = function(key, value) {
  this._state[key] = value;
};
DMPConnect.prototype.setDmpState = function(key, value) {
  this._dmpState[key] = value;
};
DMPConnect.prototype.resetState = function() {
  this._dmpState = { ...INITIAL_DMP_STATE };
  this._state = { ...INITIAL_STATE };
};


DMPConnect.prototype.getSessionId = function()
{
    return this._sessionId;
};

/**
 * Send a command to DmpConnect-JS server.
 * If the answer is not received within the given time frame (@see timeoutInSeconds), or if there is a network communication error,
 *  the error handler is called.
 *
 * If the answer is received and is correctly formatted, the answer callback is called.

 * The callback is given one parameter: an object holding the server answer in its properties.
 *
 * About the commands:
 *   DmpConnect-JS expects JSON-formatted input commands.
 *   The command is built (JSON.stringify) from the properties of the 'command' object given in parameter.
 *   The server answer is also JSON-formatted. It is deserialized into an object (with JSON.parse) that is given to the 'success callback'
 *    that should process the answer.
 *
 * @param {object}   command          Will be serialized to a JSON string to create the command.
 *
 * @param {int}      timeoutInSeconds Timeout for the server answer.
 *
 * @param {function} answerCallback   The callback that will receive the response from the local server.
 *                                    When the command is sent with a persistent socket (see next parameter), this
 *                                     function is expected to return true or false.
 *                                    If false is returned, the socket is closed. Otherwise the socket remains open.
 *
 * @param {boolean}  persistentSocket Set to false by default.
 *                                    If set to true, answerCallback will receive asynchronous messages from the local server and
 *                                     the socket will not be closed after having received the answer. See answerCallback.
 *                                    If set to false, the command is considered synchronous. In this case, the following operations are performed:
 *                                     (1) the command is sent, (2) the answer is received, (3) the socket is closed.
 * @param {function} wrapperCallback  DMPConnect inner function for object related handling on command response.
 *
 * @return {object} The websocket object.
 */
DMPConnect.prototype.sendCommand = function(command, timeoutInSeconds, answerCallback, persistentSocket, wrapperCallback )
{
    if( !sendCommandUsingWebSocket() )
    {
        this.sendPostApiCommand( command, undefined, timeoutInSeconds, answerCallback, wrapperCallback );
        return;
    }

    

    // The case below occur if getDomain() failed or have not yet retreived the domain name, or if an empty string has been given to the contructor.
    if( ! this._serverUrl )
    {
        var error = this.createWrapperError( DMPConnect.WrapperErrorCode.InitializationError, "DmpConnect-JS server address is not defined." );
        return this._errorHandler( error, command, timeoutInSeconds, answerCallback);
    }

    if( ! window.hasOwnProperty("WebSocket") )
    {
        // The browser doesn't support WebSocket
        var error = this.createWrapperError(DMPConnect.WrapperErrorCode.BrowserNotSupported, "Websocket is not supported by your browser");
        return this._errorHandler( error, command, timeoutInSeconds, answerCallback);
    }

    persistentSocket = persistentSocket || false;

    this._logCallback( "Opening a connection on [" + this._serverUrl + "].");

    var ws = new WebSocket( this._serverUrl )

    var timer = null;

    // Socket opened callback.
    ws.onopen = (function(self, ws, timeoutInSeconds, answerCallback, persistentSocket, wrapperCallback)
    {
        return function ()
        {
            var message = JSON.stringify(command);

            try
            {
                var forceCloseSocket = (function(ws, self, command, timeoutInSeconds, answerCallback, wrapperCallback)
                {
                    return function ()
                    {
                        // Close the connection.
                        ws.close();
                        var error = self.createWrapperError(DMPConnect.WrapperErrorCode.ServerTimeout, 'Server timeout reached.');
                        if (wrapperCallback)
                            wrapperCallback(error);

                        return self._errorHandler( error, command, timeoutInSeconds, answerCallback);
                    }
                })(ws, self, command, timeoutInSeconds, answerCallback, wrapperCallback);

                ws.send( message );
                self._logCallback( "Message sent [" + message + "].", command );
                // Set a timer for the server answer timeout.
                if (!persistentSocket)
                    timer = setTimeout(forceCloseSocket, timeoutInSeconds * 1000);
            }
            catch( exception )
            {
                // Close the connection.
                ws.close();
                var error = self.createWrapperError(DMPConnect.WrapperErrorCode.SocketError, 'Websocket has thrown an unhandled exception.');
                if (wrapperCallback) wrapperCallback(error);
                return self._errorHandler( error);
            }
        }
    })( this, ws, timeoutInSeconds, answerCallback, persistentSocket, wrapperCallback );

    // Change the message handler since we expect an answer from the server.
    if( persistentSocket )
    {
        // Here, we just handled a command that is expected to return multiple answers from time to time,
        //  so we don't close the websocket and change its message handling callback to the following.
        ws.onmessage = (function(self, command, timeoutInSeconds, answerCallback, persistentSocket, wrapperCallback)
        {            
            return function (event)
            {
                self._logCallback( "Asynchronous data received\n");
                self._logCallback( "Async data:\n" + event.data + "\n");

                if( event.data.length > 0 )
                {
                    var receivedData = JSON.parse( event.data );
                    if (wrapperCallback) wrapperCallback(receivedData);

                    // Directly end the dialog if an error occurred while trying to start the monitoring session.
                    if( receivedData.s_status != "OK" )
                    {
                        // Close the connection.
                        ws.close();
                        return self._errorHandler( receivedData, command, timeoutInSeconds, answerCallback, persistentSocket );
                    }

                    // Call the async callback.
                    // The callback is expected to return true or false.
                    // - false will close the socket and stop the server from sending notifications.
                    // - true indicate that the user still need notifications.
                    answerCallback( receivedData, (function (ws, receivedData, self, answerCallback )
                    {
                        return function( keepSending )
                        {
                            if( keepSending )
                            {
                                self._logCallback( "-- Async data handled.");
                            }
                            else
                            {
                                self._logCallback( "-- Async data handled. Closing the websocket.");
                                ws.close();

                                // Call the user answer callback.
                                answerCallback( receivedData );
                            }
                        }
                    })( ws, receivedData, self, answerCallback ));
                }
            }
        })(this, command, timeoutInSeconds, answerCallback, persistentSocket, wrapperCallback);
    }
    else
    {
        // Non persistent socket: parse the answer, close the socket and call the error handler (if needed) then the user callback.
        ws.onmessage = (function(self, command, timeoutInSeconds, answerCallback, wrapperCallback)
        {
            return function (event)
            {
                var receivedData = JSON.parse(event.data);
                self._logCallback( "Synchronous answer received.");
                self._logCallback( "Answer:\n" + event.data + "\n");

                // Disable the timeout timer.
                clearTimeout(timer);

                // Close the socket.
                ws.close();

                if( receivedData.s_status != 'OK' )
                {
                    return self._errorHandler( receivedData, command, timeoutInSeconds, answerCallback );
                }

                if (wrapperCallback) wrapperCallback(receivedData);
                answerCallback( receivedData );
            }
        })(this, command, timeoutInSeconds, answerCallback, wrapperCallback);
    }

    ws.onclose = (function(self, command, timeoutInSeconds, answerCallback, wrapperCallback)
    {
        return function (event)
        {
            var code     = event.code;
            var reason   = event.reason;
            var wasClean = event.wasClean;

            if (!reason) {
                // See http://tools.ietf.org/html/rfc6455#section-7.4.1
                     if (code == 1000) reason = "Normal closure, meaning that the purpose for which the connection was established has been fulfilled.";
                else if (code == 1001) reason = "An endpoint is \"going away\", such as a server going down or a browser having navigated away from a page.";
                else if (code == 1002) reason = "An endpoint is terminating the connection due to a protocol error";
                else if (code == 1003) reason = "An endpoint is terminating the connection because it has received a type of data it cannot accept (e.g., an endpoint that understands only text data MAY send this if it receives a binary message).";
                else if (code == 1004) reason = "Reserved. The specific meaning might be defined in the future.";
                else if (code == 1005) reason = "No status code was actually present.";
                else if (code == 1006) reason = "The connection was closed abnormally, e.g., without sending or receiving a Close control frame";
                else if (code == 1007) reason = "An endpoint is terminating the connection because it has received data within a message that was not consistent with the type of the message (e.g., non-UTF-8 [http://tools.ietf.org/html/rfc3629] data within a text message).";
                else if (code == 1008) reason = "An endpoint is terminating the connection because it has received a message that \"violates its policy\". This reason is given either if there is no other sutible reason, or if there is a need to hide specific details about the policy.";
                else if (code == 1009) reason = "An endpoint is terminating the connection because it has received a message that is too big for it to process.";
                // Note that this status code is not used by the server, because it can fail the WebSocket handshake instead.
                else if (code == 1010) reason = "An endpoint (client) is terminating the connection because it has expected the server to negotiate one or more extension, but the server didn't return them in the response message of the WebSocket handshake. <br /> Specifically, the extensions that are needed are: " + event.reason;
                else if (code == 1011) reason = "A server is terminating the connection because it encountered an unexpected condition that prevented it from fulfilling the request.";
                else if (code == 1015) reason = "The connection was closed due to a failure to perform a TLS handshake (e.g., the server certificate can't be verified).";
                else
                    reason = "Unknown reason";
            }

            // Websocket closed with an error?
            if (code != 1000)
            {
                var errorDesc = "Connection closed with an error. Status: " + code + ". Reason: " + reason + ". wasClean: " + wasClean + ".";
                var error     = self.createWrapperError(DMPConnect.WrapperErrorCode.ConnectionClosedError, errorDesc);

                if (wrapperCallback) wrapperCallback(error);
                return self._errorHandler( error, command, timeoutInSeconds, answerCallback);
            } else
            {
                self._logCallback( "Connection closed.");
            }
        }
    })(this, command, timeoutInSeconds, answerCallback, wrapperCallback);

    ws.onerror = (function(self, command, timeoutInSeconds, answerCallback, wrapperCallback)
    {
        return function ()
        {
            var error = self.createWrapperError( DMPConnect.WrapperErrorCode.SocketError, "An error occurred on the socket" );
            if (wrapperCallback) wrapperCallback(error);
            return self._errorHandler( error, command, timeoutInSeconds, answerCallback);
        }
    })( this, command, timeoutInSeconds, answerCallback, wrapperCallback );

    return ws;
};

function getXhr() 
{
    if(window.XMLHttpRequest || window.ActiveXObject)
    {
        if(window.ActiveXObject)
        {
            try
            {
                return new ActiveXObject("Msxml2.XMLHTTP");
            }catch(e)
            {
                return new ActiveXObject("Microsoft.XMLHTTP");
            }
        }
        else
        {
            return new XMLHttpRequest(); 
        }
    }
    else
    {
        alert("Your browser does not support XMLHTTPRequest" );
        return null;
    }
}

/**
  * Send a POST request to the /api entry point.
  */
DMPConnect.prototype.sendPostApiCommand = function( aCommandData, aCommandName, aTimeoutInSecond, aCallBackFunction, wrapperCallback )
{
    var xhr = getXhr();

    var message = JSON.stringify( aCommandData );

    if( aCommandName === undefined )
    {
        aCommandName = aCommandData.s_commandName.toLowerCase();
    }

    var url = "https://" + this._serverDomain + ":" + this._serverPort + "/api/" + aCommandName;

    console.log( "Sending to '" + url + "' the following message '" + message + "'." );


    xhr.onreadystatechange = function()
    {
        if( xhr.readyState == 4 ) // Complete
        {
            try 
            {
                jsonData = JSON.parse(xhr.responseText);
                console.log("POST command answer : " + JSON.stringify(jsonData));

                if (wrapperCallback) 
                    wrapperCallback(jsonData);

                if (aCallBackFunction) 
                {
                    if (jsonData.s_status != "OK") 
                    {
                        aCallBackFunction({ error: jsonData });
                    } 
                    else 
                    {
                        aCallBackFunction(jsonData);
                    }
                }

            }
            catch (e) 
            {
                aCallBackFunction({ error: e });
            }
        }
    }

    xhr.ontimeout = function()
    {
        console.error( "Timeout encountered during transfert" );
    }

    xhr.open( "POST", url, true );
    xhr.timeout = aTimeoutInSecond * 1000 ;
    xhr.send( message );
}

/**
  * Send a POST request to the /entry point 
    with Content-Type: application/x-www-form-urlencoded
    return the result of the post in json
  */
DMPConnect.prototype.sendURLCommand= async function( entrypoint, param1,val1,param2,val2,param3,val3,param4,val4)
{
    url = "https://" + this._serverDomain + ":" + this._serverPort + "/" + entrypoint;
    
    let body_val='';
    if (param1 && val1 )
    {
        body_val = param1+'='+val1;
        if (param2 && val2 )
        {
            body_val = body_val+ '&'+ param2+'='+val2;
            if (param3 && val3 )
            {
                body_val = body_val+ '&'+ param3+'='+val3;
                if (param4 && val4 )
                {
                    body_val = body_val+ '&'+ param4+'='+val4;
                }
            }
        }
    }

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                'Authorization': 'Bearer'
            },
            body: body_val
        });
        const data = await response.json();
        return data;
    } catch (error) {
         console.error('error :', error);
         return {};
    }
}

DMPConnect.prototype.sendHttpCommand = function( aCommandData, anEntryPoint, aCallBackFunction )
{
    var xhr = getXhr();
    var url = "https://" + this._serverDomain + ":" + this._serverPort + "/remotecommand/" + anEntryPoint + "" ;

    var message = JSON.stringify(aCommandData);
    console.log( "Sending to '" + url + "' the following message '" + message + "'." );

    xhr.onreadystatechange = function () 
    {
        if (xhr.readyState == 4)  // Complete 
        {
            try 
            {
                jsonData = JSON.parse(xhr.responseText);
                console.log("HTTP command answer : " + JSON.stringify(jsonData));

                if (aCallBackFunction) 
                {
                    if (jsonData.s_status != "OK") 
                    {
                        aCallBackFunction({ error: jsonData });
                    } 
                    else 
                    {
                        aCallBackFunction(jsonData);
                    }
                }
            } 
            catch (e) 
            {
                aCallBackFunction({ error: e });
            }
        }
    }

    // Send request.
    xhr.open( "POST" , url , true );
    xhr.send( message );
}

/**
 *  @brief Wrapper and DmpConnect API error types.
 */
DMPConnect.ErrorType =
{
    // DmpConnect API error types.
    ApiErrors:      1,
    DMPErrors:      2,
    CurlErrors:     3,
    VitaleErrors:   4,
    MssErrors:      5,
    TlsiErrors:     6,
    DpErrors:       7,

    // JS Wrapper errors.
    WrapperErrors:  5000,

    // DmpConnect-JS server software errors.
    DmpConnectJsSoftwareErrors:  6000
};

/**
 *  @brief DMPConnect wrapper related error codes (WrapperErrors)
 */
DMPConnect.WrapperErrorCode =
{
    BrowserNotSupported:    1,
    SocketError:            2,
    ConnectionClosedError:  3,
    UnexpectedResponse:     4,
    ServerTimeout:          5,
    InitializationError:    6
};

/**
 *  @brief Error codes for DmpConnectJsSoftwareErrors error type.
 *         It is software errors returned by the WS server.
 */
DMPConnect.DmpConnectJsErrorCodes =
{
    ErrTechnical     : 1, // All internal software error (allocations, bugs, etc.).
    ErrFunctional    : 2, // Connector not initialized, no reader specified, unknown command, missing parameter, invalid input data (too wide, invalid type, missing, etc.)
                          // That kind of errors must be handled during the application development.
    ErrInvalidSession: 3  // Unknown session.
};

/**
 * Create a wrapper error having the format of errors returned by DmpConnect API.
 * This error object is expected to be handled by the _errorHandler callback.

 * @param {number} errorCode
 * @param {string} errorDesc
 * @param {string} errorTypeDescription
 * @param {string} errorContext
 * @param {string} errorExtendedInformations
 */
DMPConnect.prototype.createWrapperError = function( errorCode, errorDesc, errorTypeDescription, errorContext, errorExtendedInformations )
{
    errorTypeDescription      = errorTypeDescription      || '';
    errorContext              = errorContext              || '';
    errorExtendedInformations = errorExtendedInformations || '';

    return {
        i_apiErrorType:                 DMPConnect.ErrorType.WrapperErrors, // The other kind of error are returned by DmpConnect API functions.
        i_apiErrorCode:                 errorCode,
        s_apiErrorDescription:          errorDesc,
        s_apiErrorTypeDescription:      errorTypeDescription,
        s_apiErrorContext:              errorContext,
        s_apiErrorExtendedInformations: errorExtendedInformations
    };
};

/**
 *   If DmpConnect-JS is released with MSS connect, the MssParameters structure can be set in the optionalParameters parameters:
 *   MssParameters
 *   {
 *      s_numHomologation : "" ,            // A string that identify the software that use MSS. it is used for debugging purpose with ASIP, and must contains : name of the company, name of the software. Total length must not exceed 50 characters.
 *      s_producerAddress : "" ,            // Address (address + phone number) of the structure of the producer of the attachment. Lines must be separated by the newline "\n" character, ex: "10, Grand Rue\n50 170 Mont-Saint-Michel".
 *      s_softwareDetails : "" ,            // Details of the software that is used to generate the MSS messages. Details must includes the name, version and a contact, ex: "BIOLO version 1.1 (de la société BIOLOLOGIC au 01 58 45 00 00)"
 *   }
 *
 *  Optional parameters : 
 *  i_disableUpdateCheck            Disable check of the last version of the product.
 *  s_updateCheckChannel            Update channel used to get last version ("prod", "dev", "beta").
 *

 * Specific JSON output:
 * {
 *   "s_sessionId"      : "<The session id - a GUID>"
 *   "s_serviceVersion" : DmpConnect-JS version. Ex.: "0.9.9".
 * 
 *   // Optional Proxy settings that will override the global settings (xml settings), only for this session. 
 *   "s_proxyIpOrUrl"   : Proxy server or IP. 
 *   "i_proxyPort"      : Proxy port.
 *   "s_proxyLogin"     : Proxy login.
 *   "s_proxyPassword"  : Proxy password.
 *
 *   "s_applicationId"  : Identifier of the target application. Each application Id exists in a single process.
 *
 *   The following informations are returned mostly for information purposes.
 *  
 *
 *   If a proxy is defined (either in the global configuration (dmpconnect-js2.xml) or in the session):
 *     "s_proxyServer"
 *     "i_proxyPort"
 *     "s_proxyLogin"
 *     "s_proxyPass"
 *
 *  If a ntp server is defined:
 *    "s_ntpServer"
 *    "i_ntpPort"
 *
 *  If i_disableUpdateCheck is not 1:
 *    "s_lastVersionNumber"         Last version number of the product.
 *    "s_lastVersionUpdateLog"      Last version update log.
 *    "s_lastVersionLinkStr"        Last version download link.
 * }
 *
 * @param {number}   timeoutInSeconds   If no operation on the session occurred within the given delay (in seconds), it is automatically closed.
 *                                      Default value: 1 hour.
 * @param {object}  optionalParameters  Used to pass additional parameters (proxy).
 * @param {function} resultCallback     The callback that take the result from sendCommand
 */
DMPConnect.prototype.hl_openSession = function(timeoutInSeconds, optionalParameters, resultCallback)
{
    timeoutInSeconds = timeoutInSeconds || 3600;

    var command = {
        "s_commandName"      : "hl_openSession",
        "i_timeoutInSeconds" : timeoutInSeconds
    };

    

    if( optionalParameters !== undefined )
    {        
        for(var paramName in optionalParameters)
        {
            command[ paramName ] = optionalParameters[ paramName ]
        }

        
    }


    return this.sendCommand(command, 30, resultCallback, false, (function(self){
        return function(result) {
            if (result.s_status == 'OK') {
                self._sessionId = result.s_sessionId;
                self.setState('session', true);
                self._refreshCallback();
            }
        }
    })(this));
};

// IN
// {
//      (*) i_forceNtpSync
// }
// OUT
// {
//      f_timeSynchronizationDifference
// }
DMPConnect.prototype.hl_getTimeSynchronizationDifference = function(resultCallback)
{

    var command = {
        "s_commandName"      : "hl_getTimeSynchronizationDifference",
        i_forceNtpSync       : 1                                     /* Optional */
    };


    return this.sendCommand(command, 30, resultCallback, false, (function(self){
        return function(result) {
            {
                self._refreshCallback();
            }
        }
    })(this));
};

/**
  * @brief For debugging purpose only.
  * Force a segfault inside the server.
  */
DMPConnect.prototype.hl_crash = function()
{
    var command = 
    {
        "s_commandName" : "hl_crash" 
    };

    return this.sendCommand( command, 30 , undefined, false, (function (self)
    {
        return function( result ) 
        {
            self._refreshCallback();
        }
    })( this ));
}

/**
  * @brief For debugging purpose only.
  * Force a child process to be a zombie.
  */
DMPConnect.prototype.hl_zombie = function()
{
    var command = 
    {
        "s_commandName" : "hl_zombie",
        "s_sessionId"   : this.getSessionId()
    }

    return this.sendCommand( command, 2, undefined );
}

/**
  * @brief Send a request to the connector.
  *
  * @param requestBodyJson      Request body to send to the connector.
  * @param timeout              Timeout of the request.
  *
  */
DMPConnect.prototype.sendRequest = function( requestBodyJson, timeout, resultCallback )
{
    var command         = requestBodyJson;
    command.s_sessionId = this.getSessionId();

    return this.sendCommand( command, timeout, resultCallback );
}

/**
  * Generate a new unique identifier.
  * 
  * IN 
  * {
  *     i_type  Type of unique Identifier :
  *             1 : GUID        ex: {87C621C2-A245-4AE5-9C5D-FD3F2877AC55}
  *             2 : UUID        ex: "87c621c2-a245-4ae5-9c5d-fd3f2877ac55" (canonical UUID)
  *             3 : OID         ex: "2.25.210091339143779326641979668062571519317"
  * }
  * OUT 
  * {
  *     s_uniqueId      Unique ID.  
  * }
  */
DMPConnect.prototype.hl_generateUniqueId = function( anUniqueIdType, resultCallback )
{
    var command = 
    {
        "s_commandName" : "hl_generateUniqueId",
        "i_type"        : anUniqueIdType,
        "s_sessionId"   : this.getSessionId()
    };

    return this.sendCommand( command, 2 , resultCallback );
}

/**
  * @brief Same as hl_openSession but use a custom dcparameter
  */
DMPConnect.prototype.hl_openSessionWithCustomDcParam = function(aDcParameter,timeoutInSeconds, optionalParameters, resultCallback)
{
    timeoutInSeconds = timeoutInSeconds || 3600;

    var command = {
        "s_commandName"      : "hl_openSession",
        "i_timeoutInSeconds" : timeoutInSeconds,
        "s_dcparameters64"   : aDcParameter
    };

    // Proxy 
    if( optionalParameters !== undefined )
    {        
        for(var paramName in optionalParameters)
        {
            command[ paramName ] = optionalParameters[ paramName ]
        }


        
    }

    return this.sendCommand(command, 30, resultCallback, false, (function(self){
        return function(result) {
            if (result.s_status == 'OK') {
                self._sessionId = result.s_sessionId;
                self.setState('session', true);
                self._refreshCallback();
            }
        }
    })(this) );
}

/**
 * This function will issue a message every seconds until the socket or the session is closed.
 * For development/tuning.
 *
 * IN
 * OUT
 *  "sMessage" : "Test" : Always return this.
 */
DMPConnect.prototype.hl_startDummyMonitoring = function(resultCallback)
{
    var command = {
        "s_commandName" : "hl_startDummyMonitoring",
        "s_sessionId"   : this.getSessionId()
    };

    return this.sendCommand( command, 2, resultCallback, true );
};

/**
 * This functions initiate the monitoring of the "current CPx card", that should have been set first
 *  with a call to hl_getCpxCard().
 * After the standard aknowledgement answer, a first asynchronous event is always sent: it holds the current status
 *  of the CPx card.
 * After this, the smartcard reader is checked every "i_checkingInterval" seconds for a status change.
 * If the status did not change, nothing is sent.
 *
 * To stop monitoring, simply close the websocket.
 *
 * The status is returned as an integer (i_cardStatus) and also as a string (s_cardStatus).
 *
 * IN
 *   "i_checkingInterval" : CPx card checking interval in seconds.
 * OUT
 *   "i_cardStatus" : 0..7
 *   "s_cardStatus" : Textual description of the status. See below.
 *
 *  i_cardStatus: s_cardStatus
 *     0: NotInitialized      Should not happen.
 *     1: ValidCardInserted   A valid (not expired) CPx card is inserted.
 *     2: NoCardInserted      The smartcard reader did not found a card.
 *     3: UnknownCardInserted The card in the smartcard reader is not a CPx.
 *     4: CardStatusChanged   Card status has changed since the last access: the card has been removed, or has been changed. Please recreate a new CPx object.
 *     5: CardIsBlocked       Too many pin code errors.
 *     6: CardIsExpired       The validity end date of the card is above the current date.
 *     7: CpxReaderError      All possible errors: galss, missing reader, invalid setup, etc.
 * Other: InvalidStatus       Should not happen.
 */
DMPConnect.prototype.hl_startCpxCardMonitoring = function(checkingIntervalInSeconds, resultCallback)
{
    var command = {
        "s_commandName"      : "hl_startCpxCardMonitoring",
        "i_checkingInterval" : checkingIntervalInSeconds,
        "s_sessionId"        : this.getSessionId()
    };

    return this.sendCommand(command, 15, resultCallback, true);
};

/**
 * This functions initiate the monitoring of the "current Vitale card", that should have been set first
 *  with a call to hl_getVitaleCard().
 * After the standard aknowledgement answer, a first asynchronous event is always sent: it holds the current status
 *  of the Vitale card.
 * After this, the smartcard reader is checked every "i_checkingInterval" seconds for a status change.
 * If the status did not change, nothing is sent.
 *
 * To stop monitoring, simply close the websocket.
 *
 * The status is returned as an integer (i_cardStatus) and also as a string (s_cardStatus).
 *
 * IN
 *   "i_checkingInterval" : CPx card checking interval in seconds.
 * OUT
 *   "i_cardStatus" : See below.
 *   "s_cardStatus" : Textual description of the status. See below.
 *   If a new Vitale card is inserted:
 *   "i_vitaleVersion" (+ "s_vitaleVersion") :
 *     1 : Vital 1 IGEA
 *     2 : Vital 1 SCOT
 *     3 : Vital 2
 *   "i_cardNature"   (+ "s_cardNature") :
 *     1 : Real card
 *     2 : Demo card 
 *     3 : Test card
 *
 *  i_cardStatus & s_cardStatus
 *     0: NotInitialized      Should not happen.
 *     1: ReadError           A read error occurred: faulty , invalid, saturated, unknown, silent, read error...
 *     2: NewCardFound        The Vitale card has changed since the last check.
 *    (3: CardFound)          Never returned. A valid (not expired) Vitale card is inserted.
 *     4: CardMissing         Not card inserted in the smartcard reader.
 *     5: UnknownCard         A card is inserted but not a Vitale card.
 * Other: InvalidStatus       Should not happen.
 */
DMPConnect.prototype.hl_startVitaleCardMonitoring = function(checkingIntervalInSeconds, resultCallback)
{
    var command = {
        "s_commandName"      : "hl_startVitaleCardMonitoring",
        "i_checkingInterval" : checkingIntervalInSeconds,
        "s_sessionId"        : this.getSessionId()
    };

    return this.sendCommand( command, 15, resultCallback, true );
};

/**
  * @brief Start remote control system.
  * @note This must be initialized once per application. (@note: application is defined by the parameter s_applicationId of the hl_openSession command).
  * 
  * IN 
  * {
  *    
  * }
  * OUT 
  * {
  *     i_remoteInTcpPort           : The port of the TCP socket where remote IN commands can be sent to.
  *     i_remoteOutTcpPort          : The port of the TCP socket whete remote OUT answers are sent to.
  *     s_remoteInDirectory         : The directory where remote IN commands can be sent to.
  *     s_remoteOutDirectory        : The directory where remote OUT answers are sent to.
  * }
  *
  * @note remote control cannot be started twice, use hl_remoteControlStatus to check status of the remote control system. 
  */
DMPConnect.prototype.hl_startRemoteControl = function( command , resultCallback)
{
    command               = command || {};
    command.s_commandName = "hl_startRemoteControl";
    command.s_sessionId   = this.getSessionId();

    return this.sendCommand( command , 30 , resultCallback );
}

/**
  * @brief Stop the remote control system.
  * @note When stopped, no more commands can be received from remote IN and no answers are sent to remote OUT
  * IN 
  * {
  *    
  * }
  * OUT 
  * {
  *     i_running               :     Current status of the remote control system. 1 : running, 0: stopped.
  *   
  *     (*) i_remoteInTcpPort   :     The port of the TCP socket where remote IN commands can be sent to.
  *     (*) i_remoteOutTcpPort  :     The port of the TCP socket whete remote OUT answers are sent to.
  *     (*) s_remoteInDirectory :     The directory where remote IN commands can be sent to.
  *     (*) s_remoteOutDirectory:     The directory where remote OUT answers are sent to.
  * }
  *
  * (*) This field is available only if i_running is 1. 
  *
  * @note remote control cannot be stopped twice, use hl_remoteControlStatus to check status of the remote control system.
  */
DMPConnect.prototype.hl_stopRemoteControl = function( command, resultCallback )
{
    command               = command || {} ;
    command.s_commandName = "hl_stopRemoteControl" ;
    command.s_sessionId   = this.getSessionId();

    return this.sendCommand( command, 5, resultCallback ); 
}

/**
  * @brief Get status of the remote control system.
  *
  * IN 
  * { 
  *    
  * }
  * OUT 
  * {
  *    
  * }
  */
DMPConnect.prototype.hl_remoteControlStatus = function( command, resultCallback )
{
    command               = command || {} ;
    command.s_commandName = "hl_remoteControlStatus" ;
    command.s_sessionId   = this.getSessionId();

    return this.sendCommand( command, 5 , resultCallback );
} 


/**
  * @brief Start Remote Control IN monitoring.
  *     Each command that is send to server is returned as a json string through the websocket.
  *  
  */
DMPConnect.prototype.hl_startRemoteControlInMonitoring = function( resultCallback )
{
    var command = 
    {
        "s_commandName"     : "hl_startRemoteControlInMonitoring",
        "s_sessionId"       : this.getSessionId()
    };

    return this.sendCommand( command, 15 , resultCallback, true );
}

/**
 * @brief Send remote answer to JS (then back to the remote channel).
 * 
 * @param answerObject      Answer object (as defined by the user).
 * @param resultCallback    Result callback.
 */
DMPConnect.prototype.hl_sendRemoteInAnswer = function( answerObject, resultCallback )
{
    var command = 
    {
        "s_commandName"     : "hl_sendRemoteInAnswer" ,
        "s_sessionId"       : this.getSessionId()
    };
    command = Object.assign( command , answerObject );

    return this.sendCommand( command, 15 , resultCallback );    
}

/**
  * @brief Export a document to the remote out directory/TCP
  * 
  * IN
  * {
  *     i_documentHandle
  * } 
  * OUT
  * {
  *
  * }
  */
DMPConnect.prototype.hl_exportDocumentToRemoteOut = function( aDocumentHandle, resultCallback )
{
    var command = 
    {
        "s_commandName"     : "hl_exportDocumentToRemoteOut",
        "s_sessionId"       : this.getSessionId(),
        "i_documentHandle"  : aDocumentHandle
    };

    return this.sendCommand( command, 30 , resultCallback, true );
}

/**
  * @brief Export a document to the remote out directory/TCP
  * 
  * IN
  * {
  *     s_ins
  *     s_documentUniqueId
  *     s_documentUuid              // (Optional)
  * } 
  * OUT
  * {
  *
  * }
  */
DMPConnect.prototype.hl_exportDocumentToRemoteOutByUniqueId = function( anIns, aDocumentUniqueId, aDocumentUuid, resultCallback )
{
    var command = 
    {
        "s_ins"              : anIns,
        "s_documentUniqueId" : aDocumentUniqueId,
        "s_documentUuid"     : aDocumentUuid,

        "s_commandName"      : "hl_exportDocumentToRemoteOutByUniqueId" ,
        "s_sessionId"        : this.getSessionId()
    }

    return this.sendCommand( command, 60 , resultCallback, true );
}

/**
  * @brief Export a document to XML using same format as hl_exportDocumentToRemoteOut
  * 
  * IN
  * {
  *     i_documentHandle
  * } 
  * OUT
  * {
  *
  * }
  */
DMPConnect.prototype.hl_exportDocumentToXml = function( aDocumentHandle, resultCallback )
{
    var command = 
    {
        "s_commandName"     : "hl_exportDocumentToXml",
        "s_sessionId"       : this.getSessionId(),
        "i_documentHandle"  : aDocumentHandle
    };

    return this.sendCommand( command, 30 , resultCallback, true );
}

/**
  * @brief Export a document to XML using same format as hl_exportDocumentToRemoteOut
  * 
  * IN
  * {
  *     s_ins
  *     s_documentUniqueId
  *     s_documentUuid              // (Optional)
  * } 
  * OUT
  * {
  *
  * }
  */
DMPConnect.prototype.hl_exportDocumentToXmlByUniqueId = function( anIns, aDocumentUniqueId, aDocumentUuid, resultCallback )
{
    var command = 
    {
        "s_ins"              : anIns,
        "s_documentUniqueId" : aDocumentUniqueId,
        "s_documentUuid"     : aDocumentUuid,

        "s_commandName"      : "hl_exportDocumentToXmlByUniqueId" ,
        "s_sessionId"        : this.getSessionId()
    }

    return this.sendCommand( command, 60 , resultCallback, true );
}

/**
  * @brief Export a notification to remote out directory/TCP
  *  Notification indicates that HP is connected.
  *
  * IN 
  * {
  *    
  * }
  * OUT 
  * {
  *    
  * }
  */
DMPConnect.prototype.hl_notifyPsConnectedToRemoteOut = function( resultCallback )
{
    var command = 
    {
      "s_commandName"     : "hl_notifyPsConnectedToRemoteOut",
      "s_sessionId"       : this.getSessionId()
    };

    return this.sendCommand( command, 30, resultCallback, true );
}

/**
  * @brief Export a notification to remote out directory/TCP
  *   Notification indicates that HP is disconnected.
  */
DMPConnect.prototype.hl_notifyPsLoggedOutToRemoteOut = function( resultCallback )
{
  var command = 
  {
    "s_commandName"       : "hl_notifyPsLoggedOutToRemoteOut",
    "s_sessionId"         : this.getSessionId()
  };

  return this.sendCommand( command, 30, resultCallback, true );
}

/**
  * @brief Export a notification to remote out directory/TCP
  *   Notification is custom (defined in the s_status command).
  *
  */
DMPConnect.prototype.hl_notifyPsConnectionToRemoteOut = function( connectionStatus, resultCallback )
{
  var command = 
  {
    "s_commandName"       : "hl_notifyPsConnectionToRemoteOut",
    "s_sessionId"         : this.getSessionId(),

    "s_status"            : connectionStatus
  };

  return this.sendCommand( command, 30, resultCallback, true );
}

DMPConnect.prototype.hl_sendRemoteOutMessage = function( message, resultCallback )
{
  var command = 
  {
    "s_commandName" : "hl_sendRemoteOutMessage",
    "s_sessionId"   : this.getSessionId(),

    "Message" : message 
  };

  return this.sendCommand( command, 30 , resultCallback, true );
}

/**
  * Get information about remote control for all slaves that matches the given Dcparameters
  */
DMPConnect.prototype.hl_sessionRemoteControlStatus = function( aDcparam, resultCallback )
{
  var command = 
  {
    "s_commandName"       : "hl_sessionRemoteControlStatus",
    "s_dcparameters64"    : aDcparam
  };
  return this.sendCommand( command, 30, resultCallback );
}

/**
  * Get information about remote control or slave that runs using the given TseCredentials.
  */
DMPConnect.prototype.hl_getTseRemoteControlStatus = function( aTseCredential, resultCallback )
{
  var command =
  {
    "s_commandName"      : "hl_getTseRemoteControlStatus",
    "s_tseCredentials"   : aTseCredential,
  };

  return this.sendCommand( command, 30, resultCallback );
}


/**
  * @brief This function initiate the Virtual printer monitoring.
  *
  * To stop monitoring, simply close the websocket.
  *
  *
  * After each add of a document, the set of all pending documents is returned as an array of VirtualPrinterDocuments.
  *
  * OUT
  * {
  *     VirtualPrinterDocuments :
  *     [
  *         {
  *              "s_id" : "" ,
  *              "i_fileSize" : "",
  *              "s_fileName" : "",
  *              "s_contentInBase64" : ""
  *         }
  *     ]
  * }
  *
  * @note if no document is available, the structure is empty.
  */
DMPConnect.prototype.hl_startPrintedDocumentsMonitoring = function( resultCallback )
{
    var command =
    {
        "s_commandName" : "hl_startPrintedDocumentsMonitoring" ,
        "s_sessionId"   : this.getSessionId()
    };

    return this.sendCommand( command, 15 , resultCallback, true );
}

/**
  * @brief Remove all pending documents.
  *
  * @param documentsIds     (Optional) A set of document ids to remove.
  */
DMPConnect.prototype.hl_flushPrintedDocuments = function (documentsIds, resultCallback)
{
    var command =
    {
        "s_commandName" : "hl_flushPrintedDocuments" ,
        "s_sessionId"   : this.getSessionId()
    };

    if (documentsIds != undefined)
    {
        command.s_documentIds = documentsIds
    }

    return this.sendCommand(command, 15, resultCallback);
}

/**
 * @brief Get number of pending documents in the virtual printer directory
 *
 * IN
 * {
 * }
 * OUT
 * {
 *    "i_count" : ""
 * }
 */
DMPConnect.prototype.hl_getPrintedDocumentsCount = function( resultCallback )
{
    var command =
    {
        "s_commandName" : "hl_getPrintedDocumentsCount" ,
        "s_sessionId"   : this.getSessionId()
    }

    return this.sendCommand( command, 15 , resultCallback );
}

/**
  * @brief Get current list of printed documents
  *
  * OUT
  * {
  *     VirtualPrinterDocuments :
  *     [
  *         {
  *              "s_id" : "" ,
  *              "i_fileSize" : "",
  *              "s_fileName" : "",
  *              "s_contentInBase64" : ""
  *         }
  *     ]
  * }
  */
DMPConnect.prototype.hl_getPrintedDocuments = function( resultCallback )
{
    var command =
    {
        "s_commandName" : "hl_getPrintedDocuments" ,
        "s_sessionId"   : this.getSessionId()
    };

    return this.sendCommand( command, 15 , resultCallback );
}

/**
  * @brief Get next document from the Printed document list and remove it from the list.
  *
  * IN
  * OUT
  * {
  *      PrintedDocument
  *      {
  *          s_contentInBase64 : ""                  // Content of the document in base64
  *          s_fileName        : ""                  // Document name (without extension)
  *          i_fileSize        : ""                  // Size of the document (size corresponds to the unencoded document)
  *          s_id              : ""                  // Internal identifier of the document.
  *      },
  *      i_remainingDocumentCount : ""               // Number of remaining document in the list (after removing the returned document).
  * }
  *
  * Note: if no document is available, the structure PrintedDocument is not returned and i_remainingDocumentCount equals 0.
  */
DMPConnect.prototype.hl_getNextPrintedDocument = function (resultCallback)
{
    var command =
    {
        "s_commandName": "hl_getNextPrintedDocument",
        "s_sessionId": this.getSessionId()
    };

    return this.sendCommand(command, 30, resultCallback);
}

DMPConnect.prototype.hl_closeSession = function(resultCallback)
{
    var command = {
        "s_commandName" : "hl_closeSession",
        "s_sessionId"   : this.getSessionId()
    };

   return this.sendCommand(command, 20, resultCallback, false, (function(self){
       return function(result) {
           if (result.s_status == 'OK') {
               self._sessionId = "";
               self.resetState();
               self._refreshCallback();
           }
       }
   })(this));
};


/**
 * @brief Returned by hl_getPcscReaders.
 */
DMPConnect.ReaderSlotType =
{
    // 0 = invalid / uninitialized value.
    ST_NO_CARD     : 1,
    ST_VITALE_CARD : 2,
    ST_CPS_CARD    : 3,
    ST_OTHER       : 4,
    ST_UNKNOWN     : 5
};

/**
 * @brief Returned by hl_getPcscReaders.
 */
DMPConnect.ReaderAccessMode =
{
    // 0 = invalid / uninitialized value.
    AM_FULL_PCSC  : 1, // PC/SC reader
    AM_GALSS_PCSC : 2, // PC/SC reader relying on the GALSS server
    AM_GALSS_TL   : 3, // "Terminal Lecteur" reader.
    AM_GALSS_RAC  : 4  // "Referentiel Acces Cartes"-based reader.
} ;

/**
 * Specific JSON output (example):
 * {
 *   "Readers": [
 *     {
 *       "i_accessMode": 1,
 *       "i_slotType": 3,
 *       "s_accessMode": "Full PC/SC",
 *       "s_name": "SCM Microsystems Inc. SCR33x USB Smart Card Reader 1",
 *       "s_slotType": "CPS card"
 *     },
 *     {
 *       "i_accessMode": 1,
 *       "i_slotType": 2,
 *       "s_accessMode": "Full PC/SC",
 *       "s_name": "SCM Microsystems Inc. SCR35xx USB Smart Card Reader 0",
 *       "s_slotType": "Vitale card"
 *     },
 *     {
 *       "i_accessMode": 2,
 *       "i_slotType": 3,
 *       "s_accessMode": "GALSS/PCSC",
 *       "s_name": "PSS Reader on CPS",
 *       "s_slotType": "CPS card"
 *     },
 *     {
 *       "i_accessMode": 2,
 *       "i_slotType": 2,
 *       "s_accessMode": "GALSS/PCSC",
 *       "s_name": "Vitale",
 *       "s_slotType": "Vitale card"
 *     },
 *     {
 *       "i_accessMode": 2,
 *       "i_slotType": 2,
 *       "s_accessMode": "GALSS/PCSC",
 *       "s_name": "Log_SV",
 *       "s_slotType": "Vitale card"
 *     }
 *   ],
 *   "s_status": "OK"
 * }
 */
DMPConnect.prototype.hl_getPcscReaders = function(resultCallback)
{
    var command = {
        "s_commandName" : "hl_getPcscReaders",
        "s_sessionId"   : this.getSessionId()
    };

    return this.sendCommand(command, 15, resultCallback, false, (function (self) {
        return function (result) {
            if (result.s_status == 'OK') {
              self.setState('PcscReaders', true);
              self._refreshCallback();
            }
        }
    })(this));
};

/**
 * @param {number}  readerNumber    Indice of the reader in the list returned by hl_getPcscReaders().
 * @param {string}  readerName      Optional. PC/SC reader name (e.g. "Gemalto USB Smart Card Reader 0"),
 *                                   or a GALSS LAD name (e.g "CPS"). See DmpConnect specs for explanations.
 *                                  If it is set, 'readerNumber' is ignored.
 * @param {function} resultCallback The callback that take the result from sendCommand
 */
DMPConnect.prototype.hl_getCpxCard = function( readerNumber, readerName, resultCallback)
{
    var command = {
        "s_commandName"  : "hl_getCpxCard",
        "i_readerNumber" : readerNumber ? readerNumber : 0,
        "s_readerName"   : readerName ? readerName : "",
        "s_sessionId"    : this.getSessionId()
    };

    return this.sendCommand(command, 30, resultCallback, false, (function(self){
        return function(result) {
            if (result.s_status == 'OK') {
                self.setState('cpxCard', true);
                self._refreshCallback();
            }
        }
    })(this));
};


/**
  * @brief Update access token for the wireless CPx connection.
  *
  * IN
  * {
  *     s_authenticationToken   New token.  
  * }
  * OUT
  * {
  *    
  * }
  */
DMPConnect.prototype.hl_updateCpxAuthenticationToken = function( authToken, resultCallback )
{
    var command = {};
    command.s_sessionId           = this.getSessionId();
    command.s_authenticationToken = authToken;
    command.s_commandName         = "hl_updateCpxAuthenticationToken";

    return this.sendCommand(command, 10, resultCallback);
}

DMPConnect.prototype.hl_getCpxHpInfos = function( resultCallback )
{
    var command = 
    {
        "s_commandName" : "hl_getCpxHpInfos",
        "s_sessionId"   : this.getSessionId()
    };

    return this.sendCommand( command, 5, resultCallback );
}

/**
 * @param {number}  readerNumber    Indice of the reader in the list returned by hl_getPcscReaders().
 * @param {string}  readerName      Optional. PC/SC reader name (e.g. "SCM Microsystems Inc. SCR331-DI Smart Card Reader 0"),
 *                                   or a GALSS LAD name (e.g "Vitale"). See DmpConnect specs for explanations.
 *                                  If it is set, 'readerNumber' is ignored.
 * @param {function} resultCallback The callback that take the result from sendCommand
 */
DMPConnect.prototype.hl_getVitaleCard = function( readerNumber, readerName, resultCallback)
{
    var command = {
        "s_commandName"  : "hl_getVitaleCard",
        "i_readerNumber" : readerNumber,
        "s_readerName"   : readerName,
        "s_sessionId"    : this.getSessionId()
    };

    return this.sendCommand(command, 10, resultCallback, false, (function(self){
        return function(result) {
            if (result.s_status == 'OK') {
                self.setState('vitaleCard', true);
                self._refreshCallback();
            }
        }
    })(this));
};


/**
* @brief Update access token for the wireless Vitale connection.
*
* IN
* {
*     s_authenticationToken   New token.  
* }
* OUT
* {
*    
* }
*/
DMPConnect.prototype.hl_updateVitaleAuthenticationToken = function( authToken, resultCallback )
{
var command = {};
command.s_sessionId           = this.getSessionId();
command.s_authenticationToken = authToken;
command.s_commandName         = "hl_updateVitaleAuthenticationToken";

return this.sendCommand(command, 10, resultCallback);
}

/**
 * hl_getCpxCard must have been called first.
 * 
 * IN 
 * {
 *   "i_digestType"             0: SHA-1 ; 1: SHA-256 ; 2: SHA-512
 *   "s_stringToSign"
 * }
 *
 *
 * Specific JSON output:
 * {
 *   "s_signature"            : "<base64 content of the RSA signature>",
 *   "s_signatureCertificate" : "<PEM signature certificate>"
 *   "s_digest"               : "<base64 content of the hash>"
 * }
 *
 * @param {string} pinCode            E.g. "1234" for all test cards by default.
 * @param {string} data               Data to sign.
 * @param {function} resultCallback   The callback that take the result from sendCommand
 */
DMPConnect.prototype.hl_signWithCpxCard = function(pinCode, data, resultCallback)
{
    var command = {
        "s_commandName"  : "hl_signWithCpxCard",
        "s_pinCode"      : pinCode,
        "s_stringToSign" : data,
        "i_digestType"   : 1,
        "s_sessionId"    : this.getSessionId()
    };

    return this.sendCommand(command, 10, resultCallback);
};



/**
 * hl_getCpxCard must have been called first.
 *
 * IN :
 *  s_pinCode
 *  i_returnCertificates (*)
 *
 * Specific JSON output.
 * Example:
 * {
 *   "PracticeLocations": [
 *     {
 *       "s_practiceLocationActivity":           "SA07",
 *       "s_practiceLocationHealthcareSettings": "SA07",
 *       "s_practiceLocationName":               "CABINET DR GENE",
 *       "s_practiceLocationBillingNumber" :     "2000400012",
 *       "s_practiceLocationStructureId"   :     "499700240007002"
 *     }
 *   ],
 *   "i_remainingPinCodeInputs": 3,
 *   "s_given": "ALAIN",
 *   "s_internalId": "899900023351",
 *   "s_name": "GENE RPPS",
 *   "s_profession": "10",
 *   "s_professionOid": "1.2.250.1.71.1.2.7",
 *   "s_professionDescription" : "Médecin",
 *   "s_speciality": "SM26",
 *   "s_specialityDescription" : "Qualifié en médecine générale (SM)",
 *   "s_status": "OK"
 * }
 * @param {string}   pinCode          E.g. "1234" for all test cards by default.
 * @param {function} resultCallback   The callback that take the result from sendCommand
 */
DMPConnect.prototype.hl_readCpxCard = function(pinCode, resultCallback)
{
    var command = {
        "s_commandName"        : "hl_readCpxCard",
        "i_returnCertificates" : 1, // Note: this is optional.
        "s_sessionId"          : this.getSessionId()
    };

    if( pinCode.length == 4 )
    {
        command.s_pinCode = pinCode;
    }
if( pinCode.length > 4 )    {        command.s_authenticationToken = pinCode;    }


    return this.sendCommand(command, 120, resultCallback, false, (function(self){
        return function(result) {
            if (result.s_status == 'OK') {
                self.setState('cpxCardRead', true);
                self._refreshCallback();
            }
        }
    })(this));
};

/**
  * @brief Check CPx token status.
  * 
  * @note CPx card must be set before. (ie: call to hl_getCpxCard).
  *
  * IN
  * {
  *     
  * }
  * OUT 
  * {
  *     i_previousCardStatus    (If available)
  *     s_previousCardStatus    (If available)
  *     i_cardStatus
  *     s_cardStatus
  *
  * }
  */
DMPConnect.prototype.hl_checkCpxTokenStatus = function( optionalParams, resultCallback )
{
    var command = {
        "s_commandName" : "hl_checkCpxTokenStatus",
        "s_sessionId"   : this.getSessionId()
    };

    return this.sendCommand( command, 10, resultCallback );
}

/**
  * @brief Check Vitale token status.
  * 
  * @note Vitale card must be set before. (ie: call to hl_getVitaleCard).
  *
  * IN 
  * {
  *  
  * }
  * OUT 
  * {
  *     i_previousCardStatus    (If available)
  *     s_previousCardStatus    (If available)
  *     i_cardStatus
  *     s_cardStatus
  * }
  */
DMPConnect.prototype.hl_checkVitaleTokenStatus = function( optionalParams, resultCallback )
{
    var command = 
    {
        "s_commandName" : "hl_checkVitaleTokenStatus",
        "s_sessionId"   : this.getSessionId()
    };

    return this.sendCommand( command, 10, resultCallback );
}

/**
  * @brief Get the status of the inserted CPx card.
  *
  * @note hl_getCpxCard must have been called first.
  *
  * OUT
  * {
  *     i_nbInputLeft   : Number of remaining PIN code inputs.
  *     s_validityDate  : Card expiration date.
  *     s_cardId        : Serial number of the card.
  *     i_cardStatus    : CPx status. @see DmpConnect.CpxCardStatus enum for values.
  * }
  */
 DMPConnect.prototype.hl_getCpxStatus = function( resultCallback )
 {
     var command = {
         "s_commandName" : "hl_getCpxStatus",
         "s_sessionId"   : this.getSessionId()
     };

     return this.sendCommand( command, 30 , resultCallback );
 }

/**
  * @brief Check if license of the current CPx user is valid to use this product.
  *
  * IN
  * {
  * }
  * OUT
  * {
  * }
  *
  * @note If licence is valid, this function simply returns OK, if not an error is generated and
  *       the detail associated in the answer could be checked to know why license check failed.
  */
DMPConnect.prototype.hl_checkUserLicenseRight = function (resultCallback)
{
    var command =
    {
        "s_commandName": "hl_checkUserLicenseRight",
        "s_sessionId": this.getSessionId()
    };

    return this.sendCommand(command, 10, resultCallback);
}

DMPConnect.VitaleStatus =
    {
        ApiError    : 0, // API error.
        MissingCard : 1, // Card is missing.
        Error       : 2, // Error: invalid faulty, invalid, saturated, unknown, silent, read error, etc.
        ProdCard    : 3, // Card found. Real valid card.
        TestCard    : 4, // Card found. Test card.
        DemoCard    : 5, // Card found. Demo card.
    };

/**
 * hl_getVitaleCard must have been called.
 *
 * Specific JSON output:
 * {
 *   "Patients": [
 *     {
 *       "s_birthday": "601020",
 *       "s_birthname": "",
 *       "s_certifiedNir": "",
 *       "s_given": "IGOR",
 *       "s_ins": "1793908334471761438991",
 *       "s_name": "AATIUN",
 *       "s_nir": "1601063220118 03"
 *     },
 *     {
 *       "s_birthday": "650930",
 *       "s_birthname": "",
 *       "s_certifiedNir": "",
 *       "s_given": "CECILE",
 *       "s_ins": "",
 *       "s_name": "AATIUN",
 *       "s_nir": "1601063220118 03"
 *     },
 *     {
 *       "s_birthday": "951210",
 *       "s_birthname": "",
 *       "s_certifiedNir": "",
 *       "s_given": "SERGE",
 *       "s_ins": "",
 *       "s_name": "AATIUN",
 *       "s_nir": "1601063220118 03"
 *     }
 *   ],
 *   "i_cardStatus": 5, // See VitaleStatus enum.
 *   "s_cardStatusText": "Card found. Demo card.",
 *   "s_status": "OK"
 * }
 */
DMPConnect.prototype.hl_readVitaleCard = function(resultCallback)
{
    var command = {
        "s_commandName" : "hl_readVitaleCard",
        "s_sessionId"   : this.getSessionId(),
        "i_getXmlContent" : 1
    };

    return this.sendCommand(command, 120, resultCallback, false, (function(self){
        return function(result) {
            if (result.s_status == 'OK') {
                self.setState('vitaleCardRead', true);
                self._refreshCallback();
            }
        }
    })(this));
};

/**
  * @brief Set XML content of a Virtual card.
  * If s_xmlContent is not empty, every call to hl_readVitaleCard will use this content instead of a card.
  * 
  * IN 
  * {
  *     s_xmlContent    XML content of the virtual Card.
  *     i_cardType      Vitale card status. 
  * }
  * OUT 
  * {
  *     
  * }
  * 
  * @note This function can only be used for dev, it is disabled in production.
  */
DMPConnect.prototype.hl_setVitaleXmlContent = function( xmlContent, aCardType , resultCallback )
{
    var command = 
    {
        "s_commandName" : "hl_setVitaleXmlContent",
        "s_sessionId"   : this.getSessionId(),
        "s_xmlContent"  : xmlContent,
        "i_cardType"    : aCardType
    };

    return this.sendCommand(command, 2, resultCallback);
};

/**
 * @brief Query several object parameters in one call.
 *        If no parameter is specified, all of the object dat is dumped.
 *
 * IN
 *    "i_objectHandle"   : handle of the object to query,
 *
 *    "stringIds"        : { Id0 : a parameter id of the object,
 *                           Id1 : another parameter id of the object
 *                           ...
 *                         },
 *    "stringBase64Ids"  : { Id0 : a parameter id of the object,
 *                           Id1 : another parameter id of the object
 *                           ...
 *                        },
 *    "integerIds"       : { Id0 : a parameter id of the object,
 *                           Id1 : another parameter id of the object
 *                           ...
 *                        },
 *    "floatIds"         : { Id0 : a parameter id of the object,
 *                           Id1 : another parameter id of the object
 *                           ...
 *                        },
 * OUT
 *
 * For an object, are returned:
 * - the object parameters ids and value as "id":value. See ObjectParameterName enum.
 * - the handle of the object in "handleId".
 * - the type of the object in "type". See HandleType enum.
 * Example below.
 * {
 *   "9": "GENE RPPS",
 *   "10": "ALAIN",
 *   "11": "10",
 *   "12": "1.2.250.1.71.1.2.7",
 *   "13": "SM26",
 *   "14": "899900023351",
 *   "86": 25,
 *   "handleId": 524290,
 *   "type": 9
 * }
 *
 * A object that is a List is dumped like that (81 = DocumentAuthors)
 * "81": {
 *   "handleId": 65562,
 *   "list": [
 *     {
 *       "9": "GENE RPPS",
 *       "10": "ALAIN",
 *       "11": "10",
 *       "12": "1.2.250.1.71.1.2.7",
 *       "13": "SM26",
 *       "14": "899900023351",
 *       "86": 25,
 *       "handleId": 524290,
 *       "type": 9
 *     }
 *   ],
 *   "type": 2
 * },
 */
DMPConnect.prototype.hl_getObjectParameters = function(command, resultCallback)
{
    command.s_commandName = "hl_getObjectParameters";
    command.s_sessionId   = this.getSessionId();

    return this.sendCommand(command, 2, resultCallback);
};

/**
 * @brief Return the internal handles used by the high level (HL) API.
 *
 * These handles:
 * - can be used with the low level API ;
 * - may not be initialized or be invalid. Their validity can be checked with checkValidity() and/or handleExists() for example.
 *
 * @note If user only want to know if a given session exists, it should use hl_sessionExists instead (@see bellow), it is faster and does not lock internal state.
 *
 * IN
 * OUT
 *   "i_error"            : The error object used by all HL functions. Should never be destroyed.
 *   "i_cpxReader"        : The CPx reader object (GALSS or PC/SC).
 *   "i_cpxCard"          : The CPx inserted in the CPx reader. Used by all CPx-related functions (hl_readCpxCard, hl_signWithCpxCard, all DMP functions, etc.)
 *   "i_vitaleCard"       : The Vitale card.
 *
 *   "i_practiceLocation" : DMP connector: Current practice location used for all DMP access.
 *   "i_authToken"        : DMP connector: Authentification token (CPS PKCS#11 token) used to authenticate the user and sign data.
 *   "i_connection"       : DMP connector: The TLS connection to the DMP.
 *   "i_caCert"           : DMP connector: The DMP CA certificate object.
 *   "i_user"             : DMP connector: The user object created from the CPx data.
 *   "i_dmpConnector"     : DMP connector object. The main object of the low level API.
 *
 *   "i_documentsFound"   : List of the documents found after a call to hl_findDocuments. Can be reused with getDocumentsExtraMetadata() for example.
 */
DMPConnect.prototype.hl_getSessionState = function(command, resultCallback)
{
    command = command || {};
    command.s_commandName = "hl_getSessionState";
    command.s_sessionId   = this.getSessionId();

    return this.sendCommand(command, 10, resultCallback);
};

/**
  * @brief Test if a given session exists.
  *
  * IN
  * OUT 
  *     i_exists        (1) the session exists (0) the session does not or no longer exists.
  *     s_timeout       (*) Timeout of the session. (Format: HH:MM'SS")
  *     s_remaining     (*) Remaining time before timeout. (Format: HH:MM'SS")
  *
  * (*) Only present if i_exists is equal to 1.
  *
  * @note Whereas hl_getSessionState update internal timestamp, this function does not.
  *       (ie: calling this function in a loop may not prevent the session to expire). 
  */
DMPConnect.prototype.hl_sessionExists = function(command,resultCallback)
{
    command               = command || {};
    command.s_commandName = "hl_sessionExists";
    command.s_sessionId   = this.getSessionId();

    return this.sendCommand(command, 5, resultCallback);
}

/**
 * @brief Create a new connection object
 *
 * This handle can be used in sendHttpRequest for example
 *
 * IN
 *  "s_server"                    : The URL domain (ex: http://google.fr) or IP (ex: 192.168.0.1)
 *  "i_requestTimeout"            : Timeout of the connection
 *  "s_proxyIpOrUrl"              : [Optional] Proxy IP (ex: 127.0.0.1) or URL (http://myproxy.com)
 *  "i_proxyPort"                 : [Optional] Proxy port
 *  "s_proxyLogin"                : [Optional] Proxy login
 *  "s_proxyPassword"             : [Optional] Proxy password
 *  "s_caCertName"                : [Optional] Name of the CA cert file to use.
 *  "i_usePkcs11"                 : [Optional] Use the current CPS card PKCS#11 token to establish the connection.
 *  "i_disableHttpRedirections"   : [Optional] If true, disable HTTP 3XX redirections.
 *  "i_disableOcspRevocationCheck": [Optional] Id true, disable certificate revocation check (OCSP). 
 * OUT
 *  "i_connectionHandle"    : handle of the connection
 *
 * @note: if no caCertName given, the default one, handles connections to :
 *          - "serveurs DMP de l'IGC Santé et de l'IGC CPS";
 *          - "serveurs MSS ASIP"
 *          - "Téléservices de la CNAMTS (TLSi)"
 */
DMPConnect.prototype.hl_createConnection = function(command, resultCallback)
{
    command = command || {};
    command.s_commandName = "hl_createConnection";
    command.s_sessionId   = this.getSessionId();

    return this.sendCommand(command, 10, resultCallback);
}


/*
 * @brief Get lists of Interoperability Framework codes, a.k.a. "Jeux de valeurs" in the CI-SIS documentation.
 * 8 type of codes are available, see below.
 *
 * IN
 * {
 *    // One or more of the following identifiers.
 *    s_codeIds: [
 *        "healthcareSettings"         // "Cadre de soins". Ex.: SA07, SA08, etc.
 *        "practiceSettings",          // "Cadre d'exercice". Ex.: AMBULATOIRE, DEPISTAGE, MAD, etc.
 *        "activitySectors",           // "Secteur d'activité". Ex.: SA01 .. SA54
 *        "docFormats",                // Document formats (Texte, RTF, PDF, etc.). Ex.: urn:ihe:iti:xds-sd:text:2008, urn:ihe:iti-fr:xds-sd:rtf:2010, etc.
 *        "docTypes",                  // Document types (CR de Biologie, Synthese, etc.). Ex.: 11488-4, 11506-3, etc.
 *        "professions",               // Healthcare professionals profession codes.
 *        "physicianSpecialities",     // Specialities for "Medecins" profession (code: 10).
 *        "pharmacistSpecialities"     // Specialities for "Pharmacien" profession (code: 21).
 *        "dentistSpecialities"        // Specialities for "Chirurgien-Dentiste" profession (code: 40).
 *        "docCategories",             // Document categories (Compte rendu, Synthèse, etc.). Ex: 10, 11, 31, etc.
 *        "docTypesToDocCategories",   // Document category associated to a document type.
 *        "docTypesHistory",           // All document types (same content as "docTypes" obut also includes obsolète document type codes).
 *        "docTypesTranslation",       // Associative table used to get up to date document type code, given a type code (ie: get new value from a putative obsolete one).
 *        "adeliSpecialties",          // ADELI specialties.
 *        "amoSpecialties"             // AMO specialties.
 *      ]
 * }
 * OUT
 * {
 *    healthcareSettings : // Name if the item = identifier given in s_codeIds
 *    [
 *         {
 *             s_codeSystem: "1.2.250.1.71.4.2.4",
 *             s_code: "SA07",
 *             s_displayName: "Cabinet individuel"
 *         },
 *         {
 *             ...
 *         },
 *    ],
 *    practiceSettings :
 *    [
 *         {
 *             s_codeSystem: "1.2.250.1.213.1.1.4.9",
 *             s_code: "SA01",
 *             s_displayName: "Ambulatoire"
 *         },
 *         {
 *             ...
 *         },
 *    ...
 * }
 */
DMPConnect.prototype.hl_getInteropCodes = function( command, resultCallback )
{
    command.s_commandName = "hl_getInteropCodes";
    command.s_sessionId   = this.getSessionId();

    return this.sendCommand( command, 2, resultCallback );
};

/**
  * @brief Get CIS (Code Identifiant de Spécialité) codes.
  *
  * IN 
  *     (*) Codes  : [ ]                // Array of codes to retreive. 
  *     (*) s_name :                    // Name of the product. Can contain wildcard '%' or '_' like in SQL query.
  *     (*) s_form:                     // Administration. Can contain wildcard '%' or '_' like in SQL query. 
  *     (*) s_authorizationDateBegin    // Get only codes where authorization starts after the given date. format : YYYY-MM-DD
  *     (*) s_authorizationDateEnd      // Get only codes where authorization starts before the given date. format : YYYY-MM-DD
  *     (*) i_marketed                  // Get only marketed products (1) or non marketed products (0).
  *     (*) s_owner                     // Owner of the product. Can contain wildcard '%' or '_' like in SQL query. 
  * OUT 
  *     Codes []                        // Array of codes that match the given query.
  *     
  * Example of returned code :
  * {
  *          "s_code": "69742445",
  *          "s_name": "DOLI ETAT GRIPPAL PARACETAMOL/VITAMINE C/PHENIRAMINE 500 mg/200 mg/25 mg, poudre pour solution buvable en sachet",
  *          "s_form": "poudre pour solution buvable",
  *          "Routes": [
  *              "orale"
  *          ],
  *          "i_status": 1,
  *          "s_procedureType": "Procédure nationale",
  *          "i_marketed": 0,
  *          "s_date": "2014-02-05",
  *          "i_alert": 2,
  *          "s_ueId": "",
  *          "Owners": [
  *              " OPELLA HEALTHCARE FRANCE"
  *          ],
  *          "i_surveillance": 0
  *      },
  */
DMPConnect.prototype.hl_getCisCodes = function( command, resultCallback )
{
    command               = command || {};
    command.s_commandName = "hl_getCisCodes";
    command.s_sessionId   = this.getSessionId();

    return this.sendCommand( command, 5, resultCallback );
}

/**
  * @brief Get ATC codes. (Classification Anatomique, Thérapeutique et Chimique).
  *
  * IN 
  *   (*) Codes 
  *   (*) s_nameFr
  *   (*) s_nameEn
  *   (*) i_level
  * OUT 
  *   {
  *     "Codes" : []
  *   }
  * (*) Optional
  * where each code is : 
  * {
  *   "s_code" : ""     ATC code.
  *   "Names"  :
  *   {
  *     "s_fr" : "",    // French translation.
  *     "s_en" : ""     // Original name (english).
  *   },
  *   "i_level" :       // Hierarchical level.
  * }
  */
DMPConnect.prototype.hl_getAtcCodes = function( command, resultCallback )
{
    command               = command || {};
    command.s_commandName = "hl_getAtcCodes";
    command.s_sessionId   = this.getSessionId();

    return this.sendCommand( command, 2, resultCallback );    
}

/**
  * @brief Get most up to date version of a typeCode.
  * 
  * IN 
  * {
  *     s_typeCode          // typeCode to get of up to date version of. 
  * }
  * OUT 
  * {
  *     s_typeCode          // Last version of the input typeCode.   
  * }
  *
  * @note If input s_typeCode is already the lattest version of the typecode, the function returns the input typeCode value. 
  */
DMPConnect.prototype.hl_getUpdatedTypeCode = function( typeCode, resultCallback )
{
    command = {};
    command.s_typeCode = typeCode;
    command.s_commandName = "hl_getUpdatedTypeCode" ;
    command.s_sessionId   = this.getSessionId();

    return this.sendCommand( command , 2 , resultCallback );
}

/**
  * @brief Get the set of tables inside the NOS database. 
  *
  * IN
  * {
  *    
  * }
  * OUT 
  * {
  *     TableNames : [ "", "" ];   
  * }
  */
DMPConnect.prototype.hl_getNosTableNames = function( resultCallback )
{
    command = {};
    command.s_commandName = "hl_getNosTableNames" ;
    command.s_sessionId   = this.getSessionId() ;

    return this.sendCommand( command, 5 , resultCallback );
}

/**
  * @brief Get the codes associated with the given NOS tables.
  *
  * IN
  * {
  *     TableNames : []   
  * }
  * OUT 
  * {
  *     "TableName1" : [ { s_code, s_codeSystem , s_displayName }, ... ],
  *     "TableName2" : [ ... ]    
  * }
  */
DMPConnect.prototype.hl_getNosCodes = function( tableArray, resultCallback )
{
    command = {};
    command.s_commandName = "hl_getNosCodes" ;
    command.s_sessionId   = this.getSessionId();

    command.TableNames = tableArray;

    return this.sendCommand( command, 5 , resultCallback );
}

/**
  * @brief Get the set of tables inside the CISIS database. 
  *
  * IN
  * {
  *    
  * }
  * OUT 
  * {
  *     TableNames : [ "", "" ];   
  * }
  */
DMPConnect.prototype.hl_getCiSisTableNames = function( resultCallback )
{
    command = {};
    command.s_commandName = "hl_getCiSisTableNames" ;
    command.s_sessionId   = this.getSessionId() ;

    return this.sendCommand( command, 5 , resultCallback );
}

/**
  * @brief Get the codes associated with the given CISIS tables.
  *
  * IN
  * {
  *     TableNames : []   
  * }
  * OUT 
  * {
  *     "TableName1" : [ { s_code, s_codeSystem , s_displayName }, ... ],
  *     "TableName2" : [ ... ]    
  * }
  *
  */
DMPConnect.prototype.hl_getCiSisCodes = function( tableArray, resultCallback )
{
    command = {};
    command.s_commandName = "hl_getCiSisCodes" ;
    command.s_sessionId   = this.getSessionId();

    command.TableNames = tableArray;

    return this.sendCommand( command, 5 , resultCallback );
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Temporary User-defined session data
// ---------------------------------------------------------------------------------------------------------------------------------
// Note:
//
//  Session data(s) consist(s) in user defined data stored as string in base 64, that can be get/set using uniques ID. The following
//  functionalities are available:
//
//  - User can add a new data (in this case, a corresponding ID will be returned
//  - User can update an existing data (using it's ID as reference)
//  - User can get value of an existing data
//  - User can remove an existing session data
//  - User can remove all session data
//
// There is a maximum size on all of the user session data:
//
//  - User cannot set a session data that exceed the maximum size
//  - User cannot add a session data that would result in a total of all it's session data above the maximum size
//  - User can query the current size of his session data
//  - User can query the maximum size allowed for the session data

/**
 * @brief Set or Update a user defined session data
 *
 * IN
 * {
 *      "s_sessionDataId"           : [Optional] User session data ID. If set try to update existing data, if not set create a new one.
 *      "s_sessionData64"           : Data to set/update (string in base64)
 * }
 * OUT
 * {
 *      "s_sessionDataId"           : ID of the data that have been set/updated
 * }
 *
 * @note If session data exceed maximum allowed size (@see hl_getMaximumSessionDataSize) data is not added and an error is returned
 * @note If session data size plus existing data size exceed maximum allowed size (@see hl_getMaximumSessionDataSize) data is not added and an error is returned
 * @note If sessionDataId is set but no existing data with this ID exists, data is not added and an error is returned
 */
DMPConnect.prototype.hl_setSessionData = function (command, resultCallback)
{
    command                 = command || {};
    command.s_commandName   = "hl_setSessionData";
    command.s_sessionId     = this.getSessionId();

    return this.sendCommand(command, 10, resultCallback);
}

/**
 * @brief Get an existing session data
 *
 * IN
 * {
 *      "s_sessionDataId"           : ID of the session data to get
 * }
 * OUT
 * {
 *      "s_sessionData64"           : content of the session data to get
 * }
 *
 * @note If session data does not exists, an error is returned
 */
DMPConnect.prototype.hl_getSessionData = function(command, resultCallback)
{
    command                 = command || {};
    command.s_commandName   = "hl_getSessionData";
    command.s_sessionId     = this.getSessionId();

    return this.sendCommand(command, 10, resultCallback);
}

/**
 * @brief Remove an existing data
 *
 * IN
 * {
 *      "s_sessionDataId"           : ID of the session data to remove
 * }
 * OUT
 *
 * @note If session data does not exists, an error is returned
 */
DMPConnect.prototype.hl_removeSessionData = function(command, resultCallback)
{
    command                 = command || {};
    command.s_commandName   = "hl_removeSessionData";
    command.s_sessionId     = this.getSessionId();

    return this.sendCommand(command, 10, resultCallback);
}

/**
 * @brief Clear all session data
 *
 * IN
 * OUT
 *
 */
DMPConnect.prototype.hl_clearSessionData = function(resultCallback)
{
    var command =
    {
        "s_commandName"     : "hl_clearSessionData",
        "s_sessionId"       : this.getSessionId()
    };

    return this.sendCommand(command, 10, resultCallback);
}


/**
 * @brief Get current size of the session data
 *
 * IN
 * OUT
 * {
 *      "i_sessionDataSize"       : Current size (in byte) of the session data(s)
 * }
 */
DMPConnect.prototype.hl_getSessionDataSize = function(resultCallback)
{
    var command =
    {
        "s_commandName"     : "hl_getSessionDataSize" ,
        "s_sessionId"       : this.getSessionId()
    };

    return this.sendCommand(command, 10, resultCallback);
}

/**
 * @brief Get maximum allowed size for the session data
 *
 * IN
 * OUT
 * {
 *      "hl_getMaximumSessionDataSize"  : Maximum allowed size (in byte) for the session data(s)
 * }
 */
DMPConnect.prototype.hl_getMaximumSessionDataSize = function(resultCallback)
{
    var command =
    {
        "s_commandName"     : "hl_getMaximumSessionDataSize" ,
        "s_sessionId"       : this.getSessionId()
    };

    return this.sendCommand(command, 10, resultCallback);
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Persistant user data
// ---------------------------------------------------------------------------------------------------------------------------------
// Note:
//  Persistant data consist in data that can be read/save between sessions
//      Unlike session data:
//          - Data is not destroyed at the end of the session
//          - Data is unique (ie: only one block of persistant memory can be get/set), after each set, the existing data is overwritten
//
//      Like session data:
//          - a data can be saved or retrieved
//          - user can query the current size of the persistant data
//          - a maximum size limit exists for persistant data
//          - user can query the maximum allowed size
//
//  If user data exceed the maximum allowed size, an error is returned and the current persistent data is keep untouched.
//


/**
 * @brief set the persistant data
 *
 * IN
 * {
 *      "s_persistantData64"    : A base64 string containing the data to store in the persistant storage
 * }
 * OUT
 *
 * @note If the given data size exceed maximum allowed size (@see hl_getMaximumPersistantDataSize), an error is returned and the existing data size is kept untouched.
 * @note Existing persistant data is overwritten by this function.
 */
DMPConnect.prototype.hl_setPersistantData = function( command , resultCallback )
{
    command                 = command || {};
    command.s_commandName   = "hl_setPersistantData";
    command.s_sessionId     = this.getSessionId();

    return this.sendCommand(command, 10, resultCallback);
}

/**
 * @brief get persistant data
 *
 * IN
 *
 * OUT
 * {
 *      "s_persistantData64"    : A base64 string containing the data stored in the persistant storage
 * }
 *
 * @note If no existing persistant data exists, "s_persistantData64" will contains an empty string
 */
DMPConnect.prototype.hl_getPersistantData = function( resultCallback )
{
    var command =
    {
        "s_commandName"     : "hl_getPersistantData" ,
        "s_sessionId"       : this.getSessionId()
    };

    return this.sendCommand(command, 10, resultCallback);
}

/**
 * @brief Clear existing persistant data
 *
 * IN
 *
 * OUT
 */
DMPConnect.prototype.hl_clearPersistantData = function( resultCallback )
{
    var command =
    {
        "s_commandName"     : "hl_clearPersistantData" ,
        "s_sessionId"       : this.getSessionId()
    };

    return this.sendCommand(command, 10, resultCallback);
}

/**
 * @brief Get existing persistant data size
 *
 * IN
 *
 * OUT
 * {
 *      "i_persistantDataSize"      : Size (in byte) of the persistant data currently stored
 * }
 */
DMPConnect.prototype.hl_getPersistantDataSize = function( resultCallback )
{
    var command =
    {
        "s_commandName"     : "hl_getPersistantDataSize" ,
        "s_sessionId"       : this.getSessionId()
    };

    return this.sendCommand(command, 10, resultCallback);
}


/**
 * @brief Get maximum allowed size of the persistant data
 *
 * IN
 *
 * OUT
 * {
 *      "i_maximumPersistantDataSize"       : Maximum size (in byte) of the persistant data that can be stored
 * }
 */
DMPConnect.prototype.hl_getMaximumPersistantDataSize = function( resultCallback )
{
    var command =
    {
        "s_commandName"     : "hl_getMaximumPersistantDataSize" ,
        "s_sessionId"       : this.getSessionId()
    };

    return this.sendCommand(command, 10, resultCallback);
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Low level API functions.
// ---------------------------------------------------------------------------------------------------------------------------------
// Note:
//   The reference documentation of the functions below is available in the C headers and the C API specification PDF.
//   JavaScript and C functions names are the same.
//
//   Most of the time, the expected JSON parameter have the same name than the C parameters prefixed with the parameter type ('i_', 's_', etc.).
//
//   Ex.: D_HANDLE getObjectFromList( D_HANDLE list, unsigned int position, D_HANDLE error );
//   Expects the following parameters:
//   {
//     "i_list" : xxx,
//     "i_position": xxx
//   }
//
//   The last parameter 'D_HANDLE error'' is always omitted: the internal session error object is used.
//
//   Example of call:
//       dmpConnectInstance.getParameterString( { i_globalParameter: DMPConnect.GlobalParameterName.VersionName } );

/**
 * @brief Helper function for low level API function calls.
 * Can be used directly, however most of the available calls are wrapped in a specific function.
 *
 * For command taking an 'i_globalParameter' parameter, see DMPConnect.GlobalParameterName.
 *
 * For command taking an 'i_parameter' parameter, see DMPConnect.ObjectParameterName.
 * In this last case, the function will also need the object handle in i_object (decimal value).
 *
 * @param {string}   commandName       Id of the command. Ex.: "getParameterString", etc.
 * @param {object}   commandParameters Object holding the function parameters in its properties.
 *                                     Ex.: { "i_globalParameter": DMPConnect.GlobalParameterName.VersionName }
 * @param {function} resultCallback    The callback that take the result from sendCommand
 */
DMPConnect.prototype.lowLevelApiFunctionCall = function(commandName, commandParameters, resultCallback)
{
    var command = {};
    command.s_commandName = commandName;
    command.s_sessionId   = this.getSessionId();

    // Merge the function-specific parameters.
    for(var paramName in commandParameters)
    {
        command[paramName] = commandParameters[paramName];
    }

    // Execute the command.
    return this.sendCommand(command, 10, resultCallback);
};

// IN : i_globalParameter, i_resultInBase64
// OUT: s_utf8String, s_utf8StringSize
DMPConnect.prototype.getParameterString           = function( commandParameters, resultCallback ) {this.lowLevelApiFunctionCall( "getParameterString"          , commandParameters, resultCallback ); };

// IN : i_globalParameter
// OUT: i_returnValue
DMPConnect.prototype.getParameterStringSize       = function( commandParameters, resultCallback ) {this.lowLevelApiFunctionCall( "getParameterStringSize"      , commandParameters, resultCallback ); };

// IN : i_globalParameter
// OUT: i_returnValue
DMPConnect.prototype.getParameterInt              = function( commandParameters, resultCallback ) {this.lowLevelApiFunctionCall( "getParameterInt"             , commandParameters, resultCallback ); };

// IN : i_globalParameter
// OUT: i_returnValue
DMPConnect.prototype.getObjectParameterStringSize = function( commandParameters, resultCallback ) {this.lowLevelApiFunctionCall( "getObjectParameterStringSize", commandParameters, resultCallback ); };

// IN : i_parameter, i_object, i_resultInBase64
// OUT: s_utf8String, s_utf8StringSize
DMPConnect.prototype.getObjectParameterString     = function( commandParameters, resultCallback ) {this.lowLevelApiFunctionCall( "getObjectParameterString"    , commandParameters, resultCallback ); };

// IN : i_parameter, i_object
// OUT: i_returnValue
DMPConnect.prototype.getObjectParameterInt        = function( commandParameters, resultCallback ) {this.lowLevelApiFunctionCall( "getObjectParameterInt"       , commandParameters, resultCallback ); };

// IN : i_parameter, i_object
// OUT: f_returnValue
DMPConnect.prototype.getObjectParameterFloat      = function( commandParameters, resultCallback ) {this.lowLevelApiFunctionCall( "getObjectParameterFloat"     , commandParameters, resultCallback ); };

// IN : i_list, i_position
// OUT: i_returnValue
DMPConnect.prototype.getObjectFromList            = function( commandParameters, resultCallback ) {this.lowLevelApiFunctionCall( "getObjectFromList"           , commandParameters, resultCallback ); };

// IN : i_list
// OUT: i_returnValue
DMPConnect.prototype.getListSize                  = function( commandParameters, resultCallback ) {this.lowLevelApiFunctionCall( "getListSize"                 , commandParameters, resultCallback ); };

// IN : i_list, i_position
// OUT: i_returnValue
DMPConnect.prototype.copyObjectFromList           = function( commandParameters, resultCallback ) {this.lowLevelApiFunctionCall( "copyObjectFromList"          , commandParameters, resultCallback ); };

// IN : i_handle
// OUT: i_returnValue
DMPConnect.prototype.handleExists                 = function( commandParameters, resultCallback ) {this.lowLevelApiFunctionCall( "handleExists"                , commandParameters, resultCallback ); };

// IN : i_handle
// OUT: i_returnValue
DMPConnect.prototype.checkValidity                = function( commandParameters, resultCallback ) {this.lowLevelApiFunctionCall( "checkValidity"               , commandParameters, resultCallback ); };

// IN : i_handle
// OUT: i_returnValue
DMPConnect.prototype.getHandleType                = function( commandParameters, resultCallback ) {this.lowLevelApiFunctionCall( "getHandleType"               , commandParameters, resultCallback ); };

// IN : i_type
// OUT: i_returnValue
DMPConnect.prototype.createObject                 = function( commandParameters, resultCallback ) {this.lowLevelApiFunctionCall( "createObject"                , commandParameters, resultCallback ); };

// IN : i_handle
DMPConnect.prototype.releaseObject                = function( commandParameters, resultCallback ) {this.lowLevelApiFunctionCall( "releaseObject"               , commandParameters, resultCallback ); };

// IN : i_list
DMPConnect.prototype.releaseList                  = function( commandParameters, resultCallback ) {this.lowLevelApiFunctionCall( "releaseList"                 , commandParameters, resultCallback ); };

// IN : i_error
DMPConnect.prototype.releaseErrorObject           = function( commandParameters, resultCallback ) {this.lowLevelApiFunctionCall( "releaseErrorObject"          , commandParameters, resultCallback ); };

// Currently for debug only.
// ---------------------------------------------------------------------------------------------------------------------------------



/**
 * @brief Return either the server log, the log of DmpConnect or both logs.
 *
 * Log data is returned in base 64, with '\n' as new line delimiter.
 *
 * All input parameters below are optional.
 * The default behaviour (no parameters defined) is to return the last 50 lines of both logs.
 *
 * IN
 *   i_getServerLog             : Optional. 1: retreive the log. 0: do not retreive it. Default: 1
 *   i_getDmpConnectLog         : idem.
 *   i_serverLogNbLastLines     : Optional. Default: 50
 *   i_dmpConnectLogNbLastLines :Optional.  Default: 50
 * OUT
 *   s_serverLogBase64     : Defined if i_getServerLog     != 0
 *   s_dmpConnectLogBase64 : Defined if i_getDmpConnectLog != 0
 */
DMPConnect.prototype.getLogsTail = function(command, resultCallback)
{
    command = command || {};
    command.s_commandName = "getLogsTail";

    return this.sendCommand(command, 4, resultCallback);
};

/**
 * @brief Return either the server log, the log of DmpConnect or both logs.
 * This is the log associated with the current JS process where the session resides.
 *
 * Log data is returned in base 64, with '\n' as new line delimiter.
 *
 * All input parameters below are optional.
 * The default behaviour (no parameters defined) is to return the last 50 lines of both logs.
 *
 * IN
 *   i_getServerLog             : Optional. 1: retreive the log. 0: do not retreive it. Default: 1
 *   i_getDmpConnectLog         : idem.
 *   i_serverLogNbLastLines     : Optional. Default: 50
 *   i_dmpConnectLogNbLastLines :Optional.  Default: 50
 * OUT
 *   s_serverLogBase64     : Defined if i_getServerLog     != 0
 *   s_dmpConnectLogBase64 : Defined if i_getDmpConnectLog != 0
 */
DMPConnect.prototype.hl_getSessionLogsTail = function( command, resultCallback )
{
    command = command || {};
    command.s_commandName = "hl_getSessionLogsTail";
    command.s_sessionId   = this.getSessionId();

    return this.sendCommand(command, 4, resultCallback);
}

/**
  * Return system information.
  *
  *
  * IN 
  * {
  *    
  * }
  * OUT 
  * {
  *     s_platform       : "Windows" | "macOS" | "Linux" 
  *     i_nbSlaveProcess 
  *     i_nbSession
  *     Sessions : []
  *     Processes: []
  * }
  *
  * Session array is composed of:
  * {
  *     s_processId
  *     s_sessionId   
  * }
  *
  * Process array is composed of:
  * {
  *     s_processId                 // Internal Id, can be used to make association between the process and the session.
  *     s_workingDirectory
  *     s_integratorId
  *     s_applicationId
  *     i_processPort               // Communication port of the process (listening port of the slave).
  *     i_isTse                     // False on macOS or Linux.
  *     i_isRunning   
  * }
  */
DMPConnect.prototype.hl_getSystemInformation = function( command, resultCallback )
{
    command = command || {};
    command.s_commandName = "hl_getSystemInformation" ;
    
    return this.sendCommand( command, 4, resultCallback );
}

/**
  * @brief Get list of active features in the current build.
  *
  * IN 
  * {
  *    
  * }
  * OUT 
  * {
  *    "Features" = [{}]            // List of features.
  * } 
  */
DMPConnect.prototype.hl_activeFeatures = function( command, resultCallback )
{
    command = command || {};
    command.s_commandName = "hl_activeFeatures" ;

    return this.sendCommand( command, 4 , resultCallback );
}

/**
  * @brief Indicate if Wireless mode is enabled.
  *
  * IN
  * {
  *     
  * }
  * OUT
  * {
  *     i_enabled 
  * }
  */
DMPConnect.prototype.hl_isWirelessModeEnabled = function( command, resultCallback )
{
    command = command || {};
    command.s_commandName = "hl_isWirelessModeEnabled" ;

    return this.sendCommand( command, 4 , resultCallback );
}

/**
  * @brief Get current log levels.
  *
  * IN 
  * {
  *
  * }
  * OUT 
  * {
  *     i_dmpConnectLogLevel
  *     i_dmpConnectJsLogLevel
  * }
  */
DMPConnect.prototype.getLogLevels = function( command, resultCallback )
{
    command = command || {};
    command.s_commandName = "getLogLevels";

    return this.sendCommand( command, 4 , resultCallback );
}

/**
  * @brief Set log levels.
  *
  * IN 
  * {
  *     i_dmpConnectLogLevel
  *     i_dmpConnectJsLogLevel
  * }
  * OUT 
  * {
  *        
  * }
  */
DMPConnect.prototype.setLogLevels = function( command, iDmpConnectLevel, iDmpConnectJsLevel, resultCallback )
{
    command = command || {};

    command.s_commandName          = "setLogLevels";
    command.i_dmpConnectLogLevel   = iDmpConnectLevel;
    command.i_dmpConnectJsLogLevel = iDmpConnectJsLevel;

    return this.sendCommand( command, 4 , resultCallback );
}

/**
 * @brief Return either the server log, the log of DmpConnect or both logs.
 * This is the log associated with the current JS process where the session resides.
 *
 * Log data is returned in base 64, with '\n' as new line delimiter.
 *
 * All input parameters below are optional.
 * The default behaviour (no parameters defined) is to return the last 50 lines of both logs.
 *
 * IN
 *   i_getServerLog             : Optional. 1: retreive the log. 0: do not retreive it. Default: 1
 *   i_getDmpConnectLog         : idem.
 *   i_serverLogNbLastLines     : Optional. Default: 50
 *   i_dmpConnectLogNbLastLines :Optional.  Default: 50
 * OUT
 *   s_serverLogBase64     : Defined if i_getServerLog     != 0
 *   s_dmpConnectLogBase64 : Defined if i_getDmpConnectLog != 0
 */
DMPConnect.prototype.hl_getSessionLogsTail = function( command, resultCallback )
{
    command = command || {};
    command.s_commandName = "hl_getSessionLogsTail";
    command.s_sessionId   = this.getSessionId();

    return this.sendCommand(command, 4, resultCallback);
}

/**
 * @brief Convert a date to standard format:
 *     - if it's already standard, return same date
 *     - if it's lunar, convert it to standard date
 *
 * @param {string} aDate    Input date (YYMMDD or DDMMYYYY)
 *
 * IN
 *      "s_date"
 * OUT
 *      "s_date"
 */
DMPConnect.prototype.hl_convertLunarDateToStandardDate = function(aDate, resultCallback)
{
    command = {};
    command.s_date          = aDate;
    command.s_commandName   = "hl_convertLunarDateToStandardDate";
    command.s_sessionId     = this.getSessionId();

    return this.sendCommand(command, 5, resultCallback);
};


/**
 * @brief Convert a pdf to a pdf/a1
 *
 * IN
 *         "s_contentInBase64"
 *     (*) "i_pdfa1ImageResolution"         // default value: 200
 *     (*) "i_pdfa1IgnoreTransparency"
 * OUT
 *     "s_fileContentInBase64"
 *
 * (*) Optionnal
 */
DMPConnect.prototype.hl_convertPdfToPdfA1 = function (pdfInBase64, command, resultCallback)
{
    command = command || {};
    command.s_commandName = "hl_convertPdfToPdfA1";
    command.s_sessionId   = this.getSessionId();

    command.s_contentInBase64 = pdfInBase64;

    return this.sendCommand(command, 90, resultCallback);
}

/**
 * @brief Compute century for a birth date without century
 *
 * @param {string} aDate        Date from which century must be computed.
 * @param {int} aQuality        Quality of the patient, important value is 6 that indicate that patient is a child.
 *
 * IN
 *      "s_date"
 *      "i_quality"
 * OUT
 *      "i_century"
 */
DMPConnect.prototype.hl_computeBirthDayCentury = function(aDate, aQuality, resultCallback)
{
    command = {};
    command.s_date          = aDate;
    command.i_quality       = aQuality;
    command.s_commandName   = "hl_computeBirthDayCentury"
    command.s_sessionId     = this.getSessionId();

    return this.sendCommand(command, 5, resultCallback);
};

/**
  * @brief Regularize a birth date if needed.
  *         Regularization perform the two steps:
  *             1. It standardize date, if it's a lunar one ;
  *             2. It adds a century if date is in format YYMMDD.
  *
  * @param {string} aDate       Input date (format YYMMDD or DDMMYYYY).
  * @param {int}    aQuality    Quality of the patient, important value is 6 that indicate that patient is a child.
  *
  * IN
  *     s_date
  *     i_quality
  * OUT
  *     s_date
  */
DMPConnect.prototype.hl_regularizeBirthDateIfNeeded = function(aDate, aQuality, resultCallback)
{
    command = {};
    command.s_date          = aDate;
    command.i_quality       = aQuality;
    command.s_commandName   = "hl_regularizeBirthDateIfNeeded"
    command.s_sessionId     = this.getSessionId();

    return this.sendCommand(command, 5, resultCallback);
}

/**
  * @brief Extract document content based on it's CDA.
  *
  * IN
  * {
  *     s_cdaContentInBase64        CDA content in Base64 format.
  * }
  * OUT
  * {
  *    s_title,
  *    s_uniqueId,
  *    s_typeCode,
  *    i_documentFormat
  *    s_documentFormatCode
  *    s_documentFormatDescription
  *
  *    Patient
  *    {
  *       s_ins
  *       s_insType
  *       s_birthName
  *      s_commonName
  *      s_birthGiven
  *      s_commonGiven
  *      s_birthDate
  *      i_sex
  *    },
  *    Authenticators []         (*) Only presnet if there's at least one item.
  *    Authors []                (*) Only presnet if there's at least one item.
  *    EventCodes []             (*) Only presnet if there's at least one item.
  *    Informants []             (*) Only presnet if there's at least one item.
  *    IntendedRecipients []     (*) Only presnet if there's at least one item.
  *    DataEnterer {}            (*) Only presnet if there's a Data enterer.
  *    Participants []           (*) Only presnet if there's at least one item. 
  *    Performer {}      
  * }        
  */
DMPConnect.prototype.hl_getDocumentFromCda = function( aDocumentInBase64 , resultCallback )
{
    command = {};
    command.s_commandName        = "hl_getDocumentFromCda";
    command.s_sessionId          = this.getSessionId();
    command.s_cdaContentInBase64 = aDocumentInBase64;

    return this.sendCommand( command, 15, resultCallback );
}

/**
  * @brief Extract documents content base on an IHE_XDM archive.
  *
  * IN 
  * {
  *    s_iheXdmContentArchiveInBase64       Content of the IHE_XDM archive in Base64 format.
  * }
  * OUT 
  * {
  *     Documents   []                      Set of documents contained in IHE_XDM archive.   
  * }
  *
  * @note Document content is the same as hl_getDocumentFromCda output. 
  * @note Only the document referenced in the METADATA.XML are extracted. 
  */
DMPConnect.prototype.hl_getDocumentsFromIheXdm = function( aDocumentInBase64, resultCallback )
{
    command = {};
    command.s_commandName                  = "hl_getDocumentsFromIheXdm" ;
    command.s_sessionId                    = this.getSessionId();
    command.s_iheXdmContentArchiveInBase64 = aDocumentInBase64;
    
    return this.sendCommand( command, 15 , resultCallback );
}

// ---------------------------------------------------------------------------------------------------------------------------------
// definitions-common.h
// ---------------------------------------------------------------------------------------------------------------------------------

DMPConnect.DMPCONNECT_VERSION_MAJOR =  3;
DMPConnect.DMPCONNECT_VERSION_MINOR =  0;
DMPConnect.DMPCONNECT_VERSION_BUILD =  0;

/**
  * @brief Log levels.
  *
  * For use with setupLogOutput().
  *
  */
DMPConnect.LogVerbosity =
{
    Disabled : 0x00, ///< No output at all.
    Fatal    : 0x01, ///< Only fatal error messages are shown.
    Errors   : 0x02, ///< Previous + standard error messages.
    Warnings : 0x03, ///< Previous + warning error messages.
    Infos    : 0x04, ///< Previous + informative messages.
    Dev      : 0x05, ///< Previous + debug infos for developers. Note: to be able to see details about network-related operations (cURL, OpenSSL) this log level must be set at the initialization step.
};

DMPConnect.ConsoleOutputMode =
{
    ConsoleOutputEnabled  : 0x10000, ///< Log output is done into the console and the log file.
    ConsoleOutputDisabled : 0x20000  ///< Log output only done into the log file.
};

/**
  * @brief API objects types.
  */
DMPConnect.HandleType =
{
    /// Common handle types.
    HandleType_Undefined            : 1,
    HandleType_List                 : 2,
    HandleType_Error                : 3,

    /// Galss support handle types.
    HandleType_GalssResource        : 4,
    HandleType_Galss                : 5,
    HandleType_PcscResource         : 107, // Since v.2.0.0
    HandleType_Pcsc                 : 108, // Since v.2.0.0

    /// Vitale support handle types.
    HandleType_Vitale               : 6,
    HandleType_VitalePatient        : 7,

    /// Common CPx & ES support handle types.
    HandleType_TimeProvider         : 8,
    HandleType_HP                   : 9,
    HandleType_SubmissionSet        : 10,
    HandleType_Document             : 11,
    HandleType_Connection           : 12,
    HandleType_DmpConnector         : 13,
    HandleType_Certificate          : 14,
    HandleType_PracticeLocation     : 15,
    HandleType_EventCode            : 19,      // Since v.1.1.6.

    /// CPx support handle types.
    HandleType_CPx                  : 16,
    HandleType_CPxPkcsToken         : 17,
    HandleType_Filter               : 18
};

/**
  * @brief Global API parameters.
  *
  * Values can be retreived with:
  * - getParameterStringSize(), getParameterString() for strings parameters.
  * - getParameterInt() for integer, bool or D_HANDLE parameters.
  */
DMPConnect.GlobalParameterName =
{
    // String parameters.
    // -------------------------------------------
    VersionName                             : 1,

    PatientWebAccessPdfStoragePath          : 2,
    PatientWebAccessPdfTemplateFilename     : 3,
    PatientWebAccessPdfDefaultUrl           : 4,
    CryptolibCpsDllFilename                 : 5, // Renamed in v2.0.0. Previous name "Pkcs11ModulePath".

    // Integer parameters.
    // -------------------------------------------
    VersionMajor                            : 6,
    VersionMinor                            : 7,
    VersionBuild                            : 8,

    // D_HANDLE parameters.
    // -------------------------------------------
    CurrentTimeProvider                     : 9, // Handle type: Time Provider.

    // Boolean parameters.
    // -------------------------------------------
    Pkcs11EngineIsInitialized               : 10
};

/**
  * @brief Objects parameters.
  *
  * Values can be retreived with:
  * - getObjectParameterString(), getObjectParameterStringSize() for strings parameters.
  * - getObjectParameterInt() for integers, D_HANDLE, enums or boolean parameters.
  * - getObjectParameterFloat() for float parameters.
  */
DMPConnect.ObjectParameterName =
{
    // String parameters.
    // -------------------------------------------
    ErrorDescription                    : 1,
    ErrorContext                        : 2,
    ErrorExtendedInformations           : 3,
    TimeProviderIp                      : 4,
    ConnectionUrl                       : 5,
    ConnectionProxyIp                   : 6,
    ConnectionProxyLogin                : 7,
    ConnectionProxyPassword             : 8,
    HpName                              : 9,
    HpGiven                             : 10,
    HpProfession                        : 11,
    HpProfessionOid                     : 12,
    HpProfessionDescription             : 133,
    HpSpeciality                        : 13,
    HpSpecialityDescription             : 134,
    HpInternalId                        : 14,
    PracticeLocationName                : 15,
    PracticeLocationActivity            : 16,
    PracticeLocationPracticeSettings    : 17,
    PracticeLocationHealthcareSettings  : 112, // Since v.2.2.1. Set if a compatible activity sector is found in a CPx card. Empty otherwise.
    DmpConnectorUniqueId                : 18,
    DmpConnectorDumpPath                : 19,
    DmpConnectorLocalPatientRootOid     : 94,  // Since v.1.1.4.
    DocumentUniqueId                    : 20,
    DocumentContent                     : 21,
    DocumentCreationDate                : 48,  // UTC date of the medical document creation. Available in ES mode since v.2.0.0.
    DocumentServiceStartDate            : 49,  // UTC date of the start of the medical act.  Idem.
    DocumentServiceStopDate             : 50,  // UTC date of the end   of the medical act.  Idem.
    DocumentCda_CrBioSetIdRoot          : 104, // Since v.1.5.1. Extra CDA metadata for "Compte Rendu de Biologie".
    DocumentCda_CrBioSetIdExtension     : 105, // Since v.1.5.1. Idem.
    DocumentCda_CrBioVersionNumber      : 106, // Since v.1.5.1. Idem.
    SubmissionSetTitle                  : 23,
    SubmissionSetDescription            : 24,
    SubmissionSetLocalPatientId         : 93,  // Since v.1.1.4.
    EventCodeCode                       : 96,  // Since v.1.1.6.
    EventCodeDescription                : 97,  // Since v.1.1.6.

    CertificateFilename                 : 25,
    CertificatePassPhrase               : 26,

    GalssResourceName                   : 27,
    GalssFilename                       : 28,
    PcscResourceName                    : 109,

    VitaleApiLecFilename                : 29,
    VitaleCurrentReaderName             : 113, // Since v.2.0.0.
    VitaleXmlContent                    : 30,
    VitalePatientName                   : 31,
    VitalePatientBirthname              : 32,
    VitalePatientGiven                  : 33,
    VitalePatientBirthday               : 34,  // Format: either YYMMDD or DDMMYYYY (since v2.0.0).
    VitalePatientIns                    : 35,  // INS-C if any (see VitalePatientInsAvailable).
    VitalePatientXml                    : 36,
    VitalePatientNir                    : 37,
    VitalePatientCertifiedNir           : 38,

    FilterCategoriesList                : 39,  // Filter categories list (';'-separated).
    FilterFormatsList                   : 40,  // Filter formats    list (';'-separated).
    FilterPracticesList                 : 41,  // Filter practices  list (';'-separated).
    FilterBottomCreationDate            : 42,
    FilterTopCreationDate               : 43,
    FilterBottomServiceStartDate        : 44,
    FilterTopServiceStartDate           : 45,
    FilterBottomSubmissionDate          : 46,
    FilterTopSubmissionDate             : 47,
    DocumentTitle                       : 51,
    DocumentDescription                 : 52,
    DocumentClassCode                   : 53,
    DocumentTypeCode                    : 54,
    DocumentHealthCareFacilityTypeCode  : 55,
    DocumentPracticeSettingCode         : 56,
    DocumentPatientIns                  : 58,
    DocumentHash                        : 59,
    DocumentXDSMetadata                 : 60,
    DocumentSubmissionDate              : 61,
    DocumentUUId                        : 102, // Since v.1.2.5
    DocumentPreviousUUId                : 62,  // Renamed in v.1.2.5
    DocumentNextUUId                    : 63,  // Renamed in v.1.2.5
    DocumentCDAContent                  : 103, // Since v.1.2.6
    CpxSerialNumber                     : 99,  // Since v.1.2.0
    CpxReaderName                       : 110, // Since v.2.0.0
    CpxValidityDate                     : 111, // Since v.2.0.0

    // Integer parameters.
    // -------------------------------------------
    ErrorCode                           : 64,

    TimeProviderPort                    : 66,
    ConnectionTimeOut                   : 67,
    ConnectionProxyPort                 : 68,

    DocumentSize                        : 70,

    // Boolean parameters.
    // -------------------------------------------
    DocumentPdfFormatConversionEnabled  : 71,

    VitalePatientInsAvailable           : 72,

    // Float parameters.
    // -------------------------------------------
    TimeProviderSynchronizationDifference : 74,

    // D_HANDLE parameters.
    // -------------------------------------------
    ConnectionAuthenticationToken       : 75,  // Handle type: Certificate.
    ConnectionCaCertificate             : 76,  // Handle type: Certificate.
    DmpConnectorConnection              : 77,  // Handle type: Connection.
    DmpConnectorPracticeLocation        : 78,  // Handle type: Practice location.
    DmpConnectorUser                    : 79,  // Handle type: HP.
    DocumentPerformer                   : 80,  // Handle type: HP.
    DocumentAuthors                     : 81,  // Handle type: List.
    DocumentEventCodes                  : 95,  // Handle type: List. Added since 1.1.6.
    SubmissionSetDocuments              : 82,  // Handle type: List.

    PracticeLocationSignatureCertificate: 83, // Handle type: Certificate. ES mode only.

    // Enum parameters.
    // -------------------------------------------
    ErrorTypeCode                       : 65,  // enum: ErrorType.

    GalssResourceEntryType              : 84,  // enum: GalssResourceType.
    GalssResourceEntryProtocol          : 85,  // enum: GalssResourceProtocol.

    EventCodeClassification             : 98,  // enum: EventCodeType.  Since v.1.1.6.
    VitaleCardType                      : 101, // enum: CardDemoStatus. Since v.1.1.5. Real card or Demo card.
    HpAuthenticationMode                : 86,  // enum: UserAuthenticationMode.
    DocumentPerformerRole               : 87,  // enum: PerformerRole.

    CPxCardType                         : 100, // enum: CardDemoStatus. Since v.1.2.0. Real card or Demo card.
    CpxType                             : 69,  // enum: CPxType.
    FilterDocumentStatus                : 88,  // enum: DocumentStatus.
    FilterDocumentVisibility            : 89,  // enum: DocumentVisibility.
    Document_Status                     : 90,  // enum: DocumentStatus.
    Document_Visibility                 : 91,  // enum: DocumentVisibility.
    Document_Format                     : 92   // enum: DocumentFormat.
};

/**
  * @brief CPx types: "CPS", "CPF", "CPE", "CPA" or "CSA".
  *
  * Only CPS or CPF cards can be used to get DMP documents.
  * See DMPConnect.ObjectParameterName.CpxType.
  *
  */
DMPConnect.CPxType =
{
    CPS : 1,
    CPF : 2,
    CPE : 3,
    CPA : 4
};

/**
  * @brief Type of CPx or Vitale card: real card or demonstration/dev card?
  * See See DMPConnect.ObjectParameterName.CPxCardType and DMPConnect.ObjectParameterName.VitaleCardType
  */
DMPConnect.CardDemoStatus =
{
    Real : 1,
    Demo : 2
};

/**
  * @brief Type of the identifier of the HP.
  *
  * These values are used for i_internalIdType or i_hpInternalIdType of HP structures ;
  * They can be defined for custom author ;
  * They are returned for all authors after a document search.
  */
DMPConnect.IdentifierType =
{
    // Patient identifier
    IdentifierType_PatientIns                : 1 ,
    IdentifierType_PatientInsC               : 2 ,
    // National identifier
    IdentifierType_NationalIdentifierRpps    : 3 ,
    IdentifierType_NationalIdentifierAdeli   : 4 ,
    IdentifierType_NationalIdentifierSirius  : 5 ,
    IdentifierType_NationalIdentifierStudent : 6 ,
    // Internal identifier (ID is internal to a given structrure)
    // For id relative to a different structures of the current user:
    IdentifierType_InternalIdentifierAdeli   : 7 ,
    IdentifierType_InternalIdentifierFiness  : 8 ,
    IdentifierType_InternalIdentifierSiren   : 9 ,
    IdentifierType_InternalIdentifierSiret   : 10 ,
    IdentifierType_InternalIdentifierRpps    : 11 ,
    // For id relative to the current structure:
    IdentifierType_InternalIdentifier        : 12
};

// Type of informant.
DMPConnect.InformantType = 
{
    InformantType_Informant        : 1,
    InformantType_EmergencyContact : 2,
    InformantType_TrustedContact   : 3,
    InformantType_Unknown          : 4 
};

// Relation between informant and patient.
DMPConnect.InformantRelationType = 
{
    InformantRelationType_Mother           : 1,   ///< 'Mère'
    InformantRelationType_Father           : 2,   ///< 'Père'
    InformantRelationType_NaturalMother    : 3,   ///< 'Mère biologique'
    InformantRelationType_NaturalFather    : 4,   ///< 'Père biologique'
    InformantRelationType_StepMother       : 5,   ///< 'Belle-mère - épouse du père ou de la mère'
    InformantRelationType_StepFather       : 6,   ///< 'Beau-père - époux du père ou de la mère'
    InformantRelationType_GrandMother      : 7,   ///< 'Grand-mère'
    InformantRelationType_GrandFather      : 8,   ///< 'Grand-père'
    InformantRelationType_GrandGrandMother : 9,   ///< 'Arrière-grand-mère'
    InformantRelationType_GrandGrandFather : 10,  ///< 'Arrière-grand-père'
    InformantRelationType_NaturalDaughter  : 11,  ///< 'Fille-biologique'
    InformantRelationType_NaturalSon       : 12,  ///< 'Fils-biologique'
    InformantRelationType_Brother          : 13,  ///< 'Frere'
    InformantRelationType_Sister           : 14,  ///< 'Soeur'
    InformantRelationType_HalfBrother      : 15,  ///< 'Demi-frère'
    InformantRelationType_HalfSister       : 16,  ///< 'Demi-soeur'
    InformantRelationType_GrandDaughter    : 17,  ///< 'Petite-fille'
    InformantRelationType_GrandSon         : 18,  ///< 'Petit-fils'
    InformantRelationType_Uncle            : 19,  ///< 'Oncle'
    InformantRelationType_Aunt             : 20,  ///< 'Tante'
    InformantRelationType_Nephew           : 21,  ///< 'Neveu'
    InformantRelationType_Niece            : 22,  ///< 'Nièce'
    InformantRelationType_Husband          : 23,  ///< 'Mari'
    InformantRelationType_Wife             : 24,  ///< 'Femme'
    InformantRelationType_CivilUnion       : 25,  ///< 'concubin ou partenaire PACS'
    InformantRelationType_SameAddress      : 26,  ///< 'personne habitant sous le même toit'
    InformantRelationType_Friend           : 27,  ///< 'Autre Proche'
    InformantRelationType_Neighbor         : 28,  ///< 'Voisin'
    InformantRelationType_FamilyMember     : 29,  ///< 'Autre membre de la famille'
    InformantRelationType_Child            : 30,  ///< 'Enfant'
    InformantRelationType_Cousin           : 31,  ///< 'Cousin'
    InformantRelationType_Spouse           : 32,  ///< 'Coinjoint'
    InformantRelationType_Daughter         : 33,  ///< 'Fille'
    InformantRelationType_Son              : 34,  ///< 'Fils'
    InformantRelationType_Guardian         : 35,  ///< 'Tuteur'
    InformantRelationType_Curator          : 36,  ///< 'Curateur'
    InformantRelationType_Agent            : 37,  ///< 'Mandataire de sauvegarde'
    InformantRelationType_Unknown          : 38
};

// Type of telecom 
DMPConnect.TelecomType = 
{
    TelecomType_Phone   : 1,
    TelecomType_Fax     : 2,
    TelecomType_Mail    : 3,
    TelecomType_HTTP    : 4,
    TelecomType_FTP     : 5,
    TelecomType_MLLP    : 6,
    TelecomType_Unknown : 7
};

// Usage of telecom
DMPConnect.TelecomUsage = 
{
    TelecomUsage_Home       : 1,    ///< 'Domicile'
    TelecomUsage_MainHome   : 2,    ///< 'Domicile principal'
    TelecomUsage_Holiday    : 4,    ///< 'Lieu de vacances'
    TelecomUsage_Workplace  : 8,    ///< 'Lieu de travail'
    TelecomUsage_Direct     : 16,   ///< 'Numéro direct'
    TelecomUsage_Public     : 32,   ///< 'Numéro publique'
    TelecomUsage_Emergency  : 64,   ///< 'Numéro d'urgence'
    TelecomUsage_Mobile     : 128,  ///< 'Téléphone mobile'
    TelecomUsage_Pager      : 256,  ///< 'Beeper'
    TelecomUsage_Unknown    : 512
};

// Kind of address 
DMPConnect.AddressType =
{
    AddressType_Home        : 1,  ///< 'Domicile'
    AddressType_MainHome    : 2,  ///< 'Domicile principal'
    AddressType_Holiday     : 3,  ///< 'Domicile de vacances'
    AddressType_Workplace   : 4,  ///< 'Lieu de travail'
    AddressType_Temporary   : 5,  ///< 'Adresse temporaire'
    AddressType_Unknown     : 6
};

// Street name type 
DMPConnect.StreetNameType =
{
    StreetNameType_Alley            : 1,    ///< 'Allée'
    StreetNameType_Avenue           : 2,    ///< 'Avenue'
    StreetNameType_Boulevard        : 3,    ///< 'Boulevard'
    StreetNameType_Center           : 4,    ///< 'Centre'
    StreetNameType_CommercialCenter : 5,    ///< 'Centre Commercial'
    StreetNameType_Building         : 6,    ///< 'Immeuble'
    StreetNameType_Impasse          : 7,    ///< 'Impasse'
    StreetNameType_Locality         : 8,    ///< 'Lieu dit'
    StreetNameType_Subdivision      : 9,    ///< 'Lotissement'
    StreetNameType_Passage          : 10,   ///< 'Passage'
    StreetNameType_Place            : 11,   ///< 'Place'
    StreetNameType_Residence        : 12,   ///< 'Residence'
    StreetNameType_Rundabout        : 13,   ///< 'Rond point'
    StreetNameType_Road             : 14,   ///< 'Route'
    StreetNameType_Street           : 15,   ///< 'Rue'
    StreetNameType_Square           : 16,   ///< 'Square'
    StreetNameType_Village          : 17,   ///< 'Village'
    StreetNameType_Area             : 18,   ///< 'Zone d'activité'
    StreetNameType_DevelopmentZone  : 19,   ///< 'Zone d'aménagement concerté'
    StreetNameType_HoldingZone      : 20,   ///< 'Zone d'aménagement différé'
    StreetNameType_IndustrialZone   : 21,   ///< 'Zone industrielle'
    StreetNameType_Unknown          : 22
};

// Immunization type.
DMPConnect.ImmunizationType =
{
    ImmunizationType_Unknown    : 1,        ///< 'Vaccination sans autre précision'
    ImmunizationType_FirstSerie : 2,        ///< '1ère série vaccinante'
    ImmunizationType_Booster    : 3         ///< 'Rappel de vaccin'
};

// Immunization route of administration
DMPConnect.ImmunizationRouteType =
{
    ImmunizationRouteType_Unknown                 : 1,
    ImmunizationRouteType_IntramuscularInjection  : 2, ///< 'Injection intramusculaire'
    ImmunizationRouteType_IntracutaneousInjection : 3, ///< 'Injection intradermique'
    ImmunizationRouteType_Inhalation              : 4, ///< 'Par inhalation'
    ImmunizationRouteType_Oral                    : 5, ///< 'Prendre par la bouche'
    ImmunizationRouteType_SubcutaneousInjection   : 6  ///< 'Injection sous-cutanée'
};

// Immunization approach site.
DMPConnect.ImmunizationApproachSite =
{
    ImmunizationApproachSite_Unknown      : 1,
    ImmunizationApproachSite_Buttock      : 2,          ///< 'Fesse'
    ImmunizationApproachSite_LeftDeltoid  : 3,          ///< 'Deltoïde gauche'
    ImmunizationApproachSite_RightDeltoid : 4,          ///< 'Deltoide droit'
    ImmunizationApproachSite_LeftThigh    : 5,          ///< 'Cuisse gauche'
    ImmunizationApproachSite_RightThigh   : 6,          ///< 'Cuisse droite'
};

// Structured body section type.
DMPConnect.CDASectionType = 
{
    CDASectionType_Immunizations          : 30
};

// Authority Identifier type
DMPConnect.Hl7AuthorityIdentifierType =
{
    Hl7AuthorityIdentifierType_Name             : 1, // Identifier is a generic name.
    Hl7AuthorityIdentifierType_GeographicFiness : 2, // Identifier is a geographic FINESS.
    Hl7AuthorityIdentifierType_JuridicFiness    : 3, // Identifier is a juridic FINESS.
    Hl7AuthorityIdentifierType_Iso              : 4  // Identifier is an OID (Object Identifier) as defined in International Standard Organization.
};

// Identifier reliability
DMPConnect.Hl7IdentityReliabilityCode =
{
    Hl7IdentityReliability_Empty                     : 1,  // 'Identité non encore qualifiée'
    Hl7IdentityReliability_Temporary                 : 2,  // 'Provisoire'
    Hl7IdentityReliability_Valid                     : 3,  // 'Validé'
    Hl7IdentityReliability_Slave                     : 4,  // 'Doublon ou esclave'
    Hl7IdentityReliability_Disabled                  : 5,  // 'Désactivé'
    Hl7IdentityReliability_PutativeDuplicate         : 6,  // 'Doublon potentiel'
    Hl7IdentityReliability_ProvedDuplicate           : 7,  // 'Doublon avéré'
    Hl7IdentityReliability_PutativeCollision         : 8,  // 'Collision potentielle'
    Hl7IdentityReliability_ValidatedCollision        : 9,  // 'Collision validée'
    Hl7IdentityReliability_Filiation                 : 10, // 'Filiation'
    Hl7IdentityReliability_Hidden                    : 11, // 'Cachée'
    Hl7IdentityReliability_Anonymous                 : 12, // 'Anonyme'
    Hl7IdentityReliability_CheckedPyPatient          : 13, // 'Identité vérifiée par le patient'
    Hl7IdentityReliability_ReceivedFromAnotherDomain : 14, // 'Reçue d’un autre domaine'
    Hl7IdentityReliability_ClearedByAnotherDomain    : 15, // 'Identité rapprochée dans un autre domaine'
    Hl7IdentityReliability_Theft                     : 16, // 'Usurpation'
    Hl7IdentityReliability_DetectedNamesake          : 17, // 'Homonyme détecté'
    Hl7IdentityReliability_ProvedNamesake            : 18, // 'Homonyme avéré'
    Hl7IdentityReliability_Invalid                   : 19, // 'Invalidation de l’identité'
    Hl7IdentityReliability_Fictional                 : 20, // 'Identité fictive'
    Hl7IdentityReliability_Doubtful                  : 21  // 'Identité douteuse'
};


/**
 * hl_getCpxCard must have been called.
 *
 * Optional parameters that can be set in 'optionalParameters' parameter:
 *   HTTP proxy:
 *     "s_proxyIpOrUrl"
 *     "i_proxyPort"
 *     "s_proxyLogin"
 *     "s_proxyPassword"
 *  Custom NTP server:
 *     "s_ntpIpOrUrl"
 *     "i_ntpPort"
 *  Connection Handle:
 *     "i_getConnectionHandle"      If set to 1, the webserver will return the handle of the connection used for DMP connection in addition to the unique instance ID
 *  Local patient Root OID
 *     "s_localPatientRootOid"
 *
 * Returns :
 * - the instance unique ID of the software. The number is unique per user account and do not change
 *  between restarts or software updates. It is used to identify the software instances that performed the DMP transactions.
 *
 * { [...]
 *    "s_instanceUniqueId":"2.25.186812548863990774547154938929646584355"
 * }
 * - if i_getConnectionHandle is set to 1, il also returns the connection handle used for Dmp transactions :
 * {
 *    [...]
 *    "i_connectionHandle" : 254678
 * }
 *
 *
 * @param {string}   pinCode                      E.g. "1234" for all test cards by default.
 * @param {string}   serverName                   Available values: "formation1", "formation2", "formation3", "formation5", "". See spec.
 *                                                Set it to an empty string to use the production DMP.
 * @param {number}   transactionsTimeoutInSeconds DMP transactions timeout.
 * @param {string}   practiceSetting              E.g. "AMBULATOIRE". See spec.
 * @param {number}   cpsPracticeLocationIndice    Practice location to use. Indice in the CPx card list of practices locations.
 * @param {function} resultCallback               The callback that take the result from sendCommand
 */
DMPConnect.prototype.hl_createDmpConnector = function( pinCode, serverName, transactionsTimeoutInSeconds, practiceSetting, cpsPracticeLocationIndice, optionalParameters, resultCallback )
{
    var serverUrl = "";

    // Empty string => production DMP.
    if( serverName === "" )
        serverUrl = "https://lps2.dmp.gouv.fr/si-dmp-server/v2/services";
    else
    {
        if( serverName.substr( 0 , 8 ) == "https://" )
        {
            serverUrl = serverName;
        }
        else 
        {
            serverUrl = "https://" + serverName + ".dmp.gouv.fr/si-dmp-server/v2/services"
        }
    }

    var command = {
        "s_commandName"               : "hl_createDmpConnector",
        "s_dmpUrl"                    : serverUrl,
        "i_transactionsTimeout"       : transactionsTimeoutInSeconds,
        "s_practiceSetting"           : practiceSetting,
        "i_cpsPracticeLocationIndice" : cpsPracticeLocationIndice,
        "s_sessionId"                 : this.getSessionId()
    };

    if( pinCode.length == 4 )
    {
        command.s_pinCode = pinCode;
    }
if( pinCode.length > 4 )    {        command.s_authenticationToken = pinCode;    }


    // Merge the HTTP proxy parameters and/or the NTP proxy parameters set in optionalParameters.
    if( optionalParameters )
    {
        for( var p in optionalParameters )
        {
            command[ p ] = optionalParameters[ p ];
        }
    }

    return this.sendCommand( command, 60, resultCallback, false, (function(self){
        return function(result) {
            if (result.s_status == 'OK') {
                self.setState('DmpConnector', true);
                self._refreshCallback();
            }
        }
  })(this));
};




/**
 * @brief Get Certified identity (TD00) for a given patient.
 * @param {string}  nirOD       Nir "Ouvrant-Droit"     (as found on Vitale Card)
 * @param {string}  birthDate   Patient birth date (in format YYYYMMDD)
 * @param {int}     birthRank   Patient birth rank (almost always 1, except for twins)
 * @param {string}  nirIndividu Patient NIR Individu (only if known)
 *
 *
 * Exemple of output:
  {
  "Ins":
    {
      "s_ins": "188102B17295264",
      "s_insType": "T"
    },
  "s_birthday": "19881012",
  "s_patientGivenName": "FIGATELLIX",
  "s_patientName": "",
  "s_status": "OK"
  }
 */
DMPConnect.prototype.hl_getCertifiedIdentity = function( nirOD, birthDate, birthRank, nirIndividu, resultCallback )
{
    var command =
    {
        "s_commandName" : "hl_getCertifiedIdentity",
        "s_sessionId"   : this.getSessionId(),
        "s_nirOD"       : nirOD ,
        "s_birthDate"   : birthDate,
        "i_birthRank"   : birthRank,
        "s_nirIndividu" : nirIndividu
    };

    return this.sendCommand( command , 20 , resultCallback,false, (function(self){
        return function(result) {
            if (result.s_status == 'OK') 
            {
                self.setDmpState('certifiedIdentity', true);
                self._refreshCallback();
            }
        }
    })(this));
}

/**
  * Try to get INS-NIR from INS-C
  */
DMPConnect.prototype.hl_getInsNirFromInsC = function( insC, resultCallback )
{
    var command =
    {
        "s_commandName" : "hl_getInsNirFromInsC",
        "s_sessionId"   : this.getSessionId(),
        "s_insC"        : insC
    };

    return this.sendCommand( command, 30, resultCallback );
}


/**
 * @brief update the practice location (and setting) for the current dmp Connector
 * @param {int} practiceLocationIndice          Practice location to use. Indice in the CPx card list of practices locations.
 * @param {string}   practiceSetting            E.g. "AMBULATOIRE". See spec.
 */
DMPConnect.prototype.hl_setPracticeLocation = function( practiceSetting , practiceLocationIndice , resultCallback )
{
    var command =
    {
        "s_commandName"               : "hl_setPracticeLocation",
        "i_cpsPracticeLocationIndice" : practiceLocationIndice,
        "s_practiceSetting"           : practiceSetting,
        "s_sessionId"                 : this.getSessionId()
    };

    return this.sendCommand( command , 20 , resultCallback );
}

DMPConnect.DMPStatus =
{
    DMPExist    : 1,
    DMPIsClosed : 2,
    DMPNotFound : 3,
    DMPError    : 4
};

DMPConnect.Sex =
{
    UnknownSex : 1,
    Male       : 2,
    Female     : 3
};

DMPConnect.Gender =
{
    UnknownGender : 1,
    M             : 2,
    Mme           : 3
};

DMPConnect.UserAuthorizationStatus =
{
    AuthorizationError   : 1, /// Means that the DMP does not exists or was closed.
    AuthorizationExist   : 2,
    AuthorizationExpired : 3,
    AuthorizationDenied  : 4,
    NoAuthorization      : 5
};

/**
 * TD 0.2: Check DMP existence and the status of the authorizations of the PS on it.
 * See specs.
 *
 * hl_createDmpConnector must have been called.
 *
 * Example of output:
 * {
 * "ExistingTestAnswer": {
 *   "AdminData": {
 *     "Ins": {
 *       "s_ins": "255069999999934",
 *       "s_insType": "D"
 *     },
 *     "s_birthday": "19550615",
 *     "s_insC": "0448685716413283718907",              // Note return is optional: it only exists if patient has an INS-C
 *     "s_patientGivenName": "Nathalie",
 *     "s_patientName": "DESMAUX"
 *   },
 *   "i_dmpStatus": 1,
 *   "i_sex": 3,
 *   "i_userAuthorization": 2,
 *   "i_webAccessIsOpen": 1,
 *   "s_closingDate": "",
 *   "s_closingMessage": ""
 * },
 * "i_treatingPhysician": 0,
 * "s_status": "OK"
 *}
 *
 * @param {string}   ins              The INS to check.
 * @param {function} resultCallback   The callback that take the result from sendCommand
 */
DMPConnect.prototype.hl_getDirectAuthenticationDMPStatus = function(ins, resultCallback)
{
    var command = {
        "s_commandName" : "hl_getDirectAuthenticationDMPStatus",
        // INS can be given using the s_ins field :
        "s_ins"        : ins,
        // Or using the Ins structure :
        /*
        "Ins" :
        {
            "s_ins" : ins ,
            "s_insType" : "N"
        },
        */
        "s_sessionId"   : this.getSessionId()
    };

    return this.sendCommand(command, 6, resultCallback,false, (function(self){
        return function(result) {
            if (result.s_status == 'OK') {
                self.setDmpState('directAuthenticationStatus', true);
                self._refreshCallback();
            }
        }
    })(this));
};



/**
  * @brief Document status.
  */
DMPConnect.DocumentStatus =
{
    Approved   : 1,
    Deprecated : 2,
    Archived   : 4
};

/**
  * @brief Document confidentiality visibility types.
  */
DMPConnect.DocumentVisibility =
{
    Normal                                     : 1,
    PatientHidden                              : 2,
    HealthcareProfesionnalHidden               : 4,
    GuardianHidden                             : 8,
    PatientHiddenGuardianHidden                : 10,
    GuardianHiddenHealthcareProfesionnalHidden : 12
};

/**
  * @brief Document formats.
  */
DMPConnect.DocumentFormat =
{
    // Unstructured documents.
    PlainText            : 1,
    RtfText              : 2,
    JpgImage             : 3,
    TiffImage            : 4,
    PdfApplication       : 5,

    // Structured documents.
    VsmDocument          : 6,
    CrBiologie           : 7,

    // DLU
    DluDocument          : 40,      // 'DLU - Admission en EHPAD
    DluFludtDocument     : 41,      // 'DLU - Fiche de liaison d'urgence - transfert de l'EHPAD vers les urgences'
    DluFludrDocument     : 42,      // 'DLU - Fiche de liaison d'urgence - retour des urgences vers l'EHPAD'

    // IDL 
    LdlEesDocument       : 44,      // 'Information de liaison - Lettre de Liaison à l'entrée d'un établissement de santé'
    LdlSesDocument       : 45,      // 'Information de liaison - Lettre de Liaison à la sortie de l'établissement de santé'

    // HR
    HrDocument           : 59,      // 'Données de remboursement'
    
    // HVAC
    HvacDocument         : 60,      // 'Historique de vaccination'
    HvacNoteDocument     : 61,      // 'Note de vaccination'

    // IMG
    DicomKosDocument     : 64,      // 'Références d'objets d'un examen d'imagerie'
    ImgCrImagerieDocument: 65,      // 'Compte Rendu d'Imagerie'
    ImgDaImagerieDocument: 66       // 'Demande d'actes d'imagerie médicale'
} ;

/**
  * @brief Performer roles.
  */
DMPConnect.PerformerRole =
{
    Actor     : 1,
    MainActor : 2,
    Assistant : 3
};

/**
  * @brief Event code classification.
  */
DMPConnect.EventCodeType =
{
    CCAM          : 1, ///< Actes médicaux, y compris imagerie, anatomocytopathologie, …
    SNOMED_3_5_VF : 2, ///< Actes médicaux, y compris imagerie, anatomocytopathologie, …
    CIM_10        : 3, ///< Diagnostics
    LOINC         : 4, ///< Comptes rendus d'examens biologiques
    DRC           : 5  ///< Résultats de consultation de médecine générale.
};

/**
 * TD 3.1: Search for documents on the DMP.
 * See specs.
 *
 * hl_createDmpConnector must have been called.
 *
 * Any subsequent call will discard the document handles (i_handle).
 *
 * IN : All parameters are optional except "s_ins".
 *   "s_ins"
 *   "Categories"
 *   {
 *     "#0" : "46241-6",
 *     "#1" : "11488-4"
 *     ...
 *   }
 *   "Formats"
 *   {
 *     "#0", "urn:ihe:pat:apsr:larynx:2010"
 *   }
 *   "Practices"
 *   {
 *     "#0", "PALLIATIF"
 *   }
 *   "s_serviceStartDateTop"
 *   "s_serviceStartDateBottom"
 *   "s_serviceStopDateTop"
 *   "s_serviceStopDateBottom"
 *   "s_creationDateTop"
 *   "s_creationDateBottom"
 *   "s_submissionDateTop"
 *   "s_submissionDateBottom"
 *   "i_status"     : See DMPConnect.DocumentStatus. Multiple choices are possible by adding values of this enum.
 *   "i_visibility" : See DMPConnect.DocumentVisibility. Multiple choices are possible by adding values of this enum.
 *   "i_disableMetadataSearch" 
 * OUT
 *
 * {
 *  "Documents": [
 *    {
 *      "Authors": [
 *        {
 *          "i_handle": 524290,
 *          "i_hpAuthenticationMode": 25,
 *          "s_hpGiven": "ALAIN",
 *          "s_hpInternalId": "899900023351",
 *          "s_hpName": "GENE RPPS",
 *          "s_hpProfession": "10",
 *          "s_hpProfessionOid": "1.2.250.1.71.1.2.7",
 *          "s_hpProfessionDescription": "Médecin",
 *          "s_hpSpeciality": "SM26",
 *          "s_hpSpecialityDescription": "Qualifié en médecine générale (SM)"
 *        }
 *      ],
 *      "EventCodes": [],
 *      "i_document_Format": 5,
 *      "i_document_Status": 1,
 *      "i_document_Visibility": 4,
 *      "i_handle": 131073,
 *      "s_classCode": "10",
 *      "s_creationDate": "20161110165758",
 *      "s_description": "Description très utile",
 *      "s_healthCareFacilityTypeCode": "SA07",
 *      "s_nextUUId": "",                           (*) Only available if metadata search is performed (default)
 *      "s_practiceSettingCode": "AMBULATOIRE",
 *      "s_previousUUId": "",                       (*) Only available if metadata search is performed (default)
 *      "s_serviceStartDate": "20161110165758",
 *      "s_serviceStopDate": "",
 *      "s_submissionDate": "",                     (*) Only available if metadata search is performed (default)
 *      "s_submissionSetId": "",                    (*) Only available if metadata search is performed (default)
 *      "s_title": "Document de test DmpConnect JS",
 *      "s_typeCode": "46241-6",
 *      "s_uniqueId": "2.25.12275515752198996114817914428683693778",
 *      "s_uuid": "urn:uuid:00f8e428-a767-11e6-95bf-00163e58645d"
 *    },
 *    {
 *      "Authors": [*],
 *      "EventCodes": [*],
 *       *
 *    },
 *  ],
 *  "s_status": "OK"
 *}
 * @param {string}   ins              The INS of the DMP to look in.
 * @param {object}   command          Allow to extend the command for optionals parameters
 * @param {function} resultCallback   The callback that take the result from sendCommand
 */
DMPConnect.prototype.hl_findDocuments = function(ins, command, resultCallback)
{
    command = command || {};
    command.s_commandName = "hl_findDocuments";
    // INS number can be given the s_ins field :
    command.s_ins        = ins;
    // or using the Ins structure :
    // command.Ins =
    // {
    //     "s_ins" : ins ,
    //     "s_insType" : "N"
    // };
    command.s_sessionId   = this.getSessionId();

    return this.sendCommand(command, 600, resultCallback);
};

/**
  * @brief Get all documents associated with a submission Set.
  *
  * IN 
  * { 
  *     s_ins
  *     s_submissionSetId  
  * }
  */
DMPConnect.prototype.hl_getSubmissionSetDocuments = function( ins, submissionSetId, command, resultCallback )
{
    command = command || {};
    command.s_commandName = "hl_getSubmissionSetDocuments";
    // INS number can be given the s_ins field :
    command.s_ins        = ins;
    // or using the Ins structure :
    // command.Ins =
    // {
    //     "s_ins" : ins ,
    //     "s_insType" : "N"
    // };
    command.s_sessionId      = this.getSessionId();
    command.s_submissionSetId = submissionSetId;

    return this.sendCommand( command, 600, resultCallback );
}

/**
  * @brief Get Submission set ID of a given document.
  *
  * IN 
  * {
  *    s_ins                PatientIns 
  *    s_documentUniqueId   Document UniqueId
  *    s_documentUuid       Document UUID (optional)
  * }
  * OUT 
  * {
  *    s_submissionSetId    ID of the submission set which contains the given document.
  * }
  */
DMPConnect.prototype.hl_getDocumentSubmissionSetIdByUniqueId = function( ins, uniqueId, uuid, command , resultCallback )
{
    command = command || {};
    command.s_commandName      = "hl_getDocumentSubmissionSetIdByUniqueId";
    command.s_sessionId        = this.getSessionId();

    command.s_ins              = ins ;
    command.s_documentUniqueId = uniqueId;
    command.s_documentUuid     = uuid;
    

    return this.sendCommand( command, 60, resultCallback );
}


/**
  * @brief For TD 0.3 below.
  */
DMPConnect.UserAuthorizationAction =
{
    AddAuthorization    : 1,
    RemoveAuthorization : 2
};

/**
 * @brief TD 0.3 Update HP authorization on a DMP.
 * See specs.
 * hl_createDmpConnector must have been called.
 *
 * IN
 *   "s_ins"
 *   "i_action" : 1 = AddAuthorization, 2 = RemoveAuthorization. See UserAuthorizationAction enum.
 *   "i_setTreatingPhysician" : To remove the treating physician status, call the function with AddAuthorization and false.
 * OUT
 *
 * @param {string}   ins               The INS of the DMP we need to access or not.
 * @param {number}   action            The action to perform (i.e. add or remove the authorization). See UserAuthorizationAction.
 * @param {number}   treatingPhysician 1: enable "Medecin Traitant" access to the given DMP. Ignored if action == RemoveAuthorization
 * @param {object}   command           Allow to extend the command for optionals parameters
 * @param {function} resultCallback    The callback that take the result from sendCommand
 */
DMPConnect.prototype.hl_updateUserDmpAccessAuthorization = function(ins, action, treatingPhysician, command, resultCallback)
{
    command = command || {};
    command.s_commandName          = "hl_updateUserDmpAccessAuthorization";
    command.s_ins                  = ins;
    command.i_setTreatingPhysician = treatingPhysician;
    command.i_action               = action;
    command.s_sessionId            = this.getSessionId();

    return this.sendCommand(command, 10, resultCallback);
};

/**
 * @brief TD 0.4 Get accessible DMPs list for the current user (CPx mode) or structure (ES mode).
 * See specs.
 * hl_createDmpConnector must have been called.
 *
 * IN
 *   "s_type"               "LASTAUTORIZATION" or "LASTDOC"
 *   "s_date"               Format: YYYYMMDD
 *   "s_psOwnerId"          (Optional) If the user has a CPE, this value is set to the idNatPs of the PS for which patient list is requested. If user has a CPS, this value is ignored.
 * OUT
 * {
 *   "AccessibleDmps": [
 *     {
 *       "s_birthday": "19980121",
 *       "s_ins": "",
 *       "s_lastAccessDate": "20161121112348",
 *       "s_lastAddDate": "",
 *       "s_lastUpdateDate": "20161121111802",
 *       "s_patientBirthName": "",
 *       "s_patientGivenName": "THIBAULT",
 *       "s_patientName": "AATITROIS",
 *       "s_insC" : "0529528580088131833916",
 *       "i_treatingPhysician" : 1,
 *     },
 *     { ...
 *     },
 *     { ...
 *     }
 *   ],
 *   "s_status": "OK"
 * }
 *
 * @param {string}   type             "LASTAUTORIZATION" or "LASTDOC".
 * @param {string}   date             Format: YYYYMMDD.
 * @param {object}   command          Allow to extend the command for optionals parameters
 * @param {function} resultCallback   The callback that take the result from sendCommand
 */
DMPConnect.prototype.hl_getAccessibleDMPList = function(type, date, command, resultCallback)
{
    command = command || {};
    command.s_commandName = "hl_getAccessibleDMPList";
    command.s_type        = type;
    command.s_date        = date;
    command.s_sessionId   = this.getSessionId();

    return this.sendCommand(command, 30, resultCallback);
};

/**
 * @brief TD 3.2 Download a document from the DMP.
 * See specs.
 * hl_createDmpConnector must have been called.
 *
 * The document is stored in the system temporary directory.
 * Its URL is returned in 's_filePath'.
 *
 * IN
 *       "i_documentHandle"
 *   (*) "i_getCdaHeaders"     (Optional) Set it to 1 to retrieve CDA headers in HTML format
 *   (*) "i_getCdaContent"     (Optional) Set it to 1 to retrieve CDA content in base64 format.
 *   (*) "i_getPerformer"      (Optional) Set it to 1 to retreive informations relative to the Performer (Hp data, and it's role).
 *   (*) "i_getInformants"     (Optional) Set it to 1 to retreive informants of associated to the document if any.
 *   (*) "i_getAuthors"        (Optional) Set it to 1 to retreive authors of the document.
 *   (*) "i_getDataEnterer"    (Optional) Set it to 1 to retreive data enterer of the document.
 *   (*) "i_getParticipants"   (Optional) Set it to 1 to retreive Participants.
 *   (*) "i_getAuthenticators" (Optional) Set it to 1 to retreive authenticators.
 * OUT
 *   "s_filePath": Complete local file URL (client side).
 *                 Ex.: file:///C:/Users/cyrion/AppData/Local/Temp/5fd4d6d3-ed32-11e5-bb05-00163e58645d.txt
 *                 The name of the file is based on 'the uuid of the document'.
 *   "Performer"           :  Information relative to the Performer. (only present if i_getPerformer is set to 1).
 *                            The structure will contains the following data:
 *  
 *   "Performer": 
 *   {
 *     "Hp": 
 *     {
 *         "i_hpAuthenticationMode": 
 *         "i_hpInternalIdType": 
 *         "s_hpGiven": 
 *         "s_hpInstitution": 
 *         "s_hpInternalId": 
 *         "s_hpName": 
 *         "s_hpProfession": 
 *         "s_hpProfessionDescription": 
 *         "s_hpProfessionOid": 
 *         "s_hpSpeciality": 
 *         "s_hpSpecialityDescription": 
 *         "Addresses"      : [ {addr1}, {addr2} ]          - Only present if Performer has at least one address.
 *         "Telecoms"       : [ {tel1}, {tel2} ]            - Only present if Performer has at least one telecom.
 *      },
 *      "i_role": 2                         Type : DMPConnect.PerformerRole 
 *    }
 *    "s_cdaHeadersInBase64":        CDA headers in HTML format. (only present if i_getCdaHeaders is set to 1).
 *    "s_cdaContentInBase64":        CDA document in Base64 format (only present if i_getCdaContent is set to 1).
 *    "i_documentFormat"             (enum DocumentFormat)
 *    "s_documentFormatCode"         (ex: urn:ihe:iti:xds-sd:pdf:2008)
 *    "s_documentFormatDescription"  (ex: Document à corps non structuré en Pdf/A-1) - Only for display purpose.
 *    "Informants" : []               Array of informants. (only present if i_getInformants is set to 1) each informant is structured as:
 *    {
 *      "i_type"         :              // Type: DmpConnect.InformantType
 *      "i_relationType" :              // Type: DmpConnect.InformantRelationType
 *      "s_name"         : ""
 *      "s_given"        : ""
 *      "Addresses"      : [ {addr1}, {addr2} ]
 *      "Telecoms"       : [ {tel1}, {tel2} ]
 *    },
 *    "Authors" : []                  Array of authors. (only present if i_getAuthors is set to 1) each HP is defined as follow:
 *    {
 *         "i_hpAuthenticationMode": 
 *         "i_hpInternalIdType": 
 *         "s_hpGiven": 
 *         "s_hpInstitution": 
 *         "s_hpInternalId": 
 *         "s_hpName": 
 *         "s_hpProfession": 
 *         "s_hpProfessionDescription": 
 *         "s_hpProfessionOid": 
 *         "s_hpSpeciality": 
 *         "s_hpSpecialityDescription": 
 *         "Addresses"      : [ {addr1}, {addr2} ]          - Only present if author has at least one address.
 *         "Telecoms"       : [ {tel1}, {tel2} ]            - Only present if author has at least one telecom.
 *     },
 *     DataEnterer                      HP structure. (Only present if i_getDataEnterer is set to 1).
 *     {
 *         "i_hpAuthenticationMode": 
 *         "i_hpInternalIdType": 
 *         "s_hpGiven": 
 *         "s_hpInstitution": 
 *         "s_hpInternalId": 
 *         "s_hpName": 
 *         "s_hpProfession": 
 *         "s_hpProfessionDescription": 
 *         "s_hpProfessionOid": 
 *         "s_hpSpeciality": 
 *         "s_hpSpecialityDescription": 
 *         "Addresses"      : [ {addr1}, {addr2} ]          - Only present if data enterer has at least one address.
 *         "Telecoms"       : [ {tel1}, {tel2} ]            - Only present if data enterer has at least one telecom.
 *     },
 *     Participants : []            Array of Participants. (only present if i_getParticipants is set to 1). Each Participant is defined as follow:
 *     {
 *      s_type
 *      s_role 
 *      s_class 
 *      s_participationDateStart
 *      s_participationDateEnd
 *      Person              // Same as HP structure.
 *      {
 *         "i_hpAuthenticationMode": 
 *         "i_hpInternalIdType": 
 *         "s_hpGiven": 
 *         "s_hpInstitution": 
 *         "s_hpInternalId": 
 *         "s_hpName": 
 *         "s_hpProfession": 
 *         "s_hpProfessionDescription": 
 *         "s_hpProfessionOid": 
 *         "s_hpSpeciality": 
 *         "s_hpSpecialityDescription": 
 *         "Addresses"      : [ {addr1}, {addr2} ]          - Only present if participant has at least one address.
 *         "Telecoms"       : [ {tel1}, {tel2} ]            - Only present if participant has at least one telecom.
 *      }
 *     } 
 *     Authenticators : []          Array of authenticators. (Only present if i_getAuthenticators is set to 1). Each authenticator is defined as follow:
 *     {
 *          s_authenticationDate    : 
 *          Hp :            // Same as HP structure.
 *          {
 *              "i_hpAuthenticationMode": 
 *              "i_hpInternalIdType": 
 *              "s_hpGiven": 
 *              "s_hpInstitution": 
 *              "s_hpInternalId": 
 *              "s_hpName": 
 *              "s_hpProfession": 
 *              "s_hpProfessionDescription": 
 *              "s_hpProfessionOid": 
 *              "s_hpSpeciality": 
 *              "s_hpSpecialityDescription": 
 *              "Addresses"      : [ {addr1}, {addr2} ]          - Only present if participant has at least one address.
 *              "Telecoms"       : [ {tel1}, {tel2} ]            - Only present if participant has at least one telecom.
 *          }
 *     }
 *
 *
 *    Address structure is:
 *    {
 *      "i_type"                : ""    // Type: DmpConnect.AddressType
 *      "s_country"             : ""
 *      "s_state"               : ""
 *      "s_city"                : ""
 *      "s_postalCode"          : ""
 *      "s_houseNumber"         : ""
 *      "s_houseNumberNumeric"  : ""
 *      "s_streetName"          : ""
 *      "i_streetNameType"      : ""    // Type: DmpConnect.StreetNameType
 *      "s_additionalLocator"   : ""
 *      "s_unitId"              : ""
 *      "s_postBox"             : ""
 *      "s_precInct"            : ""
 *    }
 *    Telecom structure is:
 *    {
 *      "i_type"                : ""    // Type: TelecomType    - Note: it can be a bitwise combination of TelecomType.
 *      "i_usage"               : ""    // Type: TelecomUsage
 *      "s_value"               : ""
 *    }  
 *
 * @param {number}   hdoc             Handle of the document to download. Handles are returned by hl_findDocuments (in i_handle).
 * @param {object}   command          Allow to extend the command for optionals parameters
 * @param {function} resultCallback   The callback that take the result from sendCommand
 */
DMPConnect.prototype.hl_getDocumentContent = function(hdoc, command, resultCallback)
{
    command = command || {};
    command.s_commandName    = "hl_getDocumentContent";
    command.s_sessionId      = this.getSessionId();

    command.i_documentHandle    = hdoc;
    command.i_getCdaHeaders     = 1;
    command.i_getCdaContent     = 1;
    command.i_getPerformer      = 1;
    command.i_getInformants     = 1;
    command.i_getAuthors        = 1;
    command.i_getDataEnterer    = 1;
    command.i_getParticipants   = 1;
    command.i_getAuthenticators = 1;

    return this.sendCommand(command, 90, resultCallback);
};

DMPConnect.prototype.hl_getDocumentsContent = function(i_handle, command, resultCallback)
{
    command = command || {};
    command.s_commandName    = "hl_getDocumentsContent";
    command.s_sessionId      = this.getSessionId();

    command.DocumentsHandle    = {}; 
    command.DocumentsHandle    = i_handle;
    command.i_getCdaHeaders     = 1;
    command.i_getCdaContent     = 1;
    command.i_getPerformer      = 1;
    command.i_getInformants     = 1;
    command.i_getAuthors        = 1;
    command.i_getDataEnterer    = 1;
    command.i_getParticipants   = 1;
    command.i_getAuthenticators = 1;

    return this.sendCommand(command, 90, resultCallback);
};

DMPConnect.prototype.hl_getDocumentsContentByUniqueId = function( selectedUuids,selectedUniqueIds,PatienINS, commandData, resultCallback)
{
    commandData = commandData || {};
    commandData.s_commandName    = "hl_getDocumentsContentByUniqueId";
    commandData.s_sessionId      = this.getSessionId();

    commandData.Uuids               = selectedUuids;
    commandData.UniqueIds           = selectedUniqueIds;
    commandData.s_ins               = PatienINS;
    commandData.i_getCdaHeaders     = 1;
    commandData.i_getCdaContent     = 1;
    commandData.i_getPerformer      = 1;
    commandData.i_getInformants     = 1;
    commandData.i_getAuthors        = 1;
    commandData.i_getDataEnterer    = 1;
    commandData.i_getParticipants   = 1;
    commandData.i_getAuthenticators = 1;

    return this.sendCommand(commandData, 90, resultCallback);
};

/**
 * @brief TD 3.2 Download a document from the DMP.
 * See specs.
 * hl_createDmpConnector must have been called.
 *
 * IN
 *      s_ins                        Patient INS.
 *      s_documentUniqueId           Document Unique ID
 *  (*) s_documentUuid               Document UUID. If not provided, an additional transaction is performed.
 *  (*) i_getCdaHeaders              (Optional) Set it to 1 to retrieve CDA headers in HTML format 
 *  (*) i_getCdaContent              (Optional) Set it to 1 to retrieve CDA content in base64 format.
 *  (*) i_getPerformer               (Optional) Set it to 1 to retreive informations relative to the Performer (Hp data, and it's role). * OUT
 *  (*) i_getInformants              (Optional) Set it to 1 to retreive informants of associated to the document if any.
 *  (*) i_getAuthors                 (Optional) Set it to 1 to retreive authors of the document.
 *  (*) i_getDataEnterer             (Optional) Set it to 1 to retreive data enterer of the document.
 *  (*) i_getParticipants            (Optional) Set it to 1 to retreive Participants.
 *  (*) i_getAuthenticators          (Optional) Set it to 1 to retreive authenticators.
 * OUT
 *      s_fileContentInBase64        Content of the document in Base64 format
 *      s_cdaHeadersInBase64         CDA headers in HTML format. (only present if i_getCdaHeaders is set top 1).
 *      s_cdaContentInBase64         CDA document in Base64 format (only present if i_getCdaContent is set to 1).
 *      Performer                    Information relative to the Performer. (only present if i_getPerformer is set to 1).
 *      Informants                   Array of informants. (only present if i_getInformants is set to 1) 
 *      i_documentFormat             (Values from DMPConnect.DocumentFormat)
 *      s_documentFormatCode         (ex: urn:ihe:iti:xds-sd:pdf:2008)
 *      s_documentFormatDescription  (ex: Document à corps non structuré en Pdf/A-1) - Only for display purpose.
 * 
 *  "Performer": 
 *   {
 *     "Hp": 
 *     {
 *         "i_hpAuthenticationMode": 
 *         "i_hpInternalIdType": 
 *         "s_hpGiven": 
 *         "s_hpInstitution": 
 *         "s_hpInternalId": 
 *         "s_hpName": 
 *         "s_hpProfession": 
 *         "s_hpProfessionDescription": 
 *         "s_hpProfessionOid": 
 *         "s_hpSpeciality": 
 *         "s_hpSpecialityDescription": 
 *         "Addresses"                : [ {addr1}, {addr2} ]          - Only present if Performer has at least one address.
 *         "Telecoms"                 : [ {tel1}, {tel2} ]            - Only present if Performer has at least one telecom.
 *      },
 *      "i_role": 2                         Type : DMPConnect.PerformerRole 
 *    } 
 *    "Informants" : []                 Array of informants. (only present if i_getInformants is set to 1) each informant is structured as:
 *    {
 *      "i_type"         :              // Type: DmpConnect.InformantType
 *      "i_relationType" :              // Type: DmpConnect.InformantRelationType
 *      "s_name"         : ""
 *      "s_given"        : ""
 *      "Addresses"      : [ {addr1}, {addr2} ]
 *      "Telecoms"       : [ {tel1}, {tel2} ]
 *    }
 *    "Authors" : []                    Array of authors. (only present if i_getAuthors is set to 1) each HP is defined as follow:
 *    {
 *         "i_hpAuthenticationMode": 
 *         "i_hpInternalIdType": 
 *         "s_hpGiven": 
 *         "s_hpInstitution": 
 *         "s_hpInternalId": 
 *         "s_hpName": 
 *         "s_hpProfession": 
 *         "s_hpProfessionDescription": 
 *         "s_hpProfessionOid": 
 *         "s_hpSpeciality": 
 *         "s_hpSpecialityDescription": 
 *         "Addresses"      : [ {addr1}, {addr2} ]          - Only present if author has at least one address.
 *         "Telecoms"       : [ {tel1}, {tel2} ]            - Only present if author has at least one telecom.
 *     }
 *     DataEnterer                      HP structure. (Only present if i_getDataEnterer is set to 1).
 *     {
 *         "i_hpAuthenticationMode": 
 *         "i_hpInternalIdType": 
 *         "s_hpGiven": 
 *         "s_hpInstitution": 
 *         "s_hpInternalId": 
 *         "s_hpName": 
 *         "s_hpProfession": 
 *         "s_hpProfessionDescription": 
 *         "s_hpProfessionOid": 
 *         "s_hpSpeciality": 
 *         "s_hpSpecialityDescription": 
 *         "Addresses"      : [ {addr1}, {addr2} ]          - Only present if data enterer has at least one address.
 *         "Telecoms"       : [ {tel1}, {tel2} ]            - Only present if data enterer has at least one telecom.
 *     },
 *     Participants : []            Array of Participants. (only present if i_getParticipants is set to 1). Each Participant is defined as follow:
 *     {
 *      s_type
 *      s_role 
 *      s_class 
 *      s_participationDateStart
 *      s_participationDateEnd
 *      Person // A HP structure.
 *      {
 *         "i_hpAuthenticationMode": 
 *         "i_hpInternalIdType": 
 *         "s_hpGiven": 
 *         "s_hpInstitution": 
 *         "s_hpInternalId": 
 *         "s_hpName": 
 *         "s_hpProfession": 
 *         "s_hpProfessionDescription": 
 *         "s_hpProfessionOid": 
 *         "s_hpSpeciality": 
 *         "s_hpSpecialityDescription": 
 *         "Addresses"      : [ {addr1}, {addr2} ]          - Only present if participant has at least one address.
 *         "Telecoms"       : [ {tel1}, {tel2} ]            - Only present if participant has at least one telecom.
 *      }
 *     } 
 *     Authenticators : []          Array of authenticators. (Only present if i_getAuthenticators is set to 1). Each authenticator is defined as follow:
 *     {
 *          s_authenticationDate    : 
 *          Hp :            // Same as HP structure.
 *          {
 *              "i_hpAuthenticationMode": 
 *              "i_hpInternalIdType": 
 *              "s_hpGiven": 
 *              "s_hpInstitution": 
 *              "s_hpInternalId": 
 *              "s_hpName": 
 *              "s_hpProfession": 
 *              "s_hpProfessionDescription": 
 *              "s_hpProfessionOid": 
 *              "s_hpSpeciality": 
 *              "s_hpSpecialityDescription": 
 *              "Addresses"      : [ {addr1}, {addr2} ]          - Only present if participant has at least one address.
 *              "Telecoms"       : [ {tel1}, {tel2} ]            - Only present if participant has at least one telecom.
 *          }
 *     }
 *
 *    Address structure is:
 *    {
 *      "i_type"                : ""    // Type: DmpConnect.AddressType
 *      "s_country"             : ""
 *      "s_state"               : ""
 *      "s_city"                : ""
 *      "s_postalCode"          : ""
 *      "s_houseNumber"         : ""
 *      "s_houseNumberNumeric"  : ""
 *      "s_streetName"          : ""
 *      "i_streetNameType"      : ""    // Type: DmpConnect.StreetNameType
 *      "s_additionalLocator"   : ""
 *      "s_unitId"              : ""
 *      "s_postBox"             : ""
 *      "s_precInct"            : ""
 *    }
 *    Telecom structure is:
 *    {
 *      "i_type"                : ""    // Type: TelecomType
 *      "i_usage"               : ""    // Type: TelecomUsage
 *      "s_value"               : ""
 *    }  
 * */
DMPConnect.prototype.hl_getDocumentContentByUniqueId = function (ins, uniqueId, uuid, command, resultCallback)
{
    command = command || {};
    command.s_commandName = "hl_getDocumentContentByUniqueId";
    command.s_sessionId   = this.getSessionId();

    command.s_ins               = ins;
    command.s_documentUniqueId  = uniqueId;
    command.s_documentUuid      = uuid;
    command.i_getCdaHeaders     = 1;   
    command.i_getCdaContent     = 1; 
    command.i_getPerformer      = 1;
    command.i_getInformants     = 1;
    command.i_getAuthors        = 1;
    command.i_getDataEnterer    = 1;
    command.i_getParticipants   = 1;
    command.i_getAuthenticators = 1;

    return this.sendCommand(command, 90, resultCallback);
}


DMPConnect.prototype.hl_getDocumentHistoryByUniqueId = function (ins, uniqueId, uuid, command, resultCallback)
{
    command = command || {};
    command.s_commandName = "hl_getDocumentHistoryByUniqueId";
    command.s_sessionId   = this.getSessionId();

    command.s_ins               = ins;
    command.s_uniqueId  = uniqueId;
    command.s_uuid      = uuid;

    return this.sendCommand(command, 90, resultCallback);
}

/**
 * @brief Toggle the Document visibility status between "Hidden to HP that are not treating physician"
 *  and "Visible to all HP" (TD 3.3). See spec.
 * IN
 *     "s_healthCareSettings"
 *     "i_document"
 *     "i_newVisibility"
 * OUT
 */
DMPConnect.prototype.hl_updateDocumentVisibility = function(hdoc, healthcareSettings, newVisibility, command, resultCallback)
{
    command = command || {};
    command.s_commandName        = "hl_updateDocumentVisibility";
    command.i_document           = hdoc;
    command.s_healthCareSettings = healthcareSettings;
    command.i_newVisibility      = newVisibility;
    command.s_sessionId          = this.getSessionId();

    return this.sendCommand(command, 5, resultCallback);
};

/**
 * @brief Toggle the Document visibility status between "Hidden to HP that are not treating physician"
 *  and "Visible to all HP" (TD 3.3). See spec.
 *
 * IN
 *         "s_ins"                 Patient INS.
 *         "s_documentUniqueId"    Document Unique Id.
 *   (*)   "s_documentUuid"        Document UUID. Optional only if current document status is 'Approved'. If not provided, an additional transaction is performed. Thus slowing down the command.
 *         "s_healthCareSettings"  Healthcare settings.
 *         "i_newVisibility"       New visibility.
 * OUT
 */
DMPConnect.prototype.hl_updateDocumentVisibilityByUniqueId = function( ins , documentUniqueId, documentUuid, healthcareSettings, newVisibility, command, resultCallback )
{
    command               = command || {};
    command.s_commandName = "hl_updateDocumentVisibilityByUniqueId";
    command.s_sessionId   = this.getSessionId();

    command.s_ins                = ins;
    command.s_documentUniqueId   = documentUniqueId;
    command.s_documentUuid       = documentUuid;
    command.s_healthCareSettings = healthcareSettings;
    command.i_newVisibility      = newVisibility;

    return this.sendCommand(command, 10, resultCallback);
}

/**
 * @brief Toggle status between 'Approved' and 'Archived' (TD 3.3). See spec.
 * IN
 *     "s_healthCareSettings"
 *     "i_document"
 * OUT
 */
DMPConnect.prototype.hl_updateDocumentStatus = function(hdoc, healthcareSettings, command, resultCallback)
{
    command = command || {};
    command.s_commandName        = "hl_updateDocumentStatus";
    command.i_document           = hdoc;
    command.s_healthCareSettings = healthcareSettings;
    command.s_sessionId          = this.getSessionId();

    return this.sendCommand(command, 5, resultCallback);
};

/**
 * @brief Toggle status between 'Approved' and 'Archived' (TD 3.3). See spec.
 * IN
 *         "s_ins"                 Patient INS.
 *         "s_documentUniqueId"    Document Unique Id.
 *   (*)   "s_documentUuid"        Document UUID. Optional only if current document status is 'Approved'. If not provided, an additional transaction is performed. Thus slowing down the command.
 *         "s_healthCareSettings"  Healthcare settings.
 * OUT
 */
DMPConnect.prototype.hl_updateDocumentStatusByUniqueId = function( ins, documentUniqueId, documentUuid, healthcareSettings, command, resultCallback )
{
    command               = command || {};
    command.s_commandName = "hl_updateDocumentStatusByUniqueId";
    command.s_sessionId   = this.getSessionId();

    command.s_ins                = ins;
    command.s_documentUniqueId   = documentUniqueId;
    command.s_documentUuid       = documentUuid;
    command.s_healthCareSettings = healthcareSettings;

    return this.sendCommand(command, 10, resultCallback);
}


/*
 * @brief Delete a document from the DMP. (TD 3.3). See spec.
 * IN
 *     "s_uniqueId"
 *     "s_ins"
 *     "s_healthCareSettings"
 *     "s_uuid"
 * OUT
 *
 * @note This function only delete an active document, to delete an archived document, use hl_deleteArchivedDocument
 */
DMPConnect.prototype.hl_deleteDocument = function(uid, ins, healthcareSettings, command, resultCallback)
{
    command = command || {};
    command.s_commandName        = "hl_deleteDocument";
    command.s_uniqueId           = uid;
    // INS number can be given s_ins field:
    command.s_ins               = ins;
    // or using the Ins structure:
    // command.Ins =
    // {
    //     "s_ins"     : ins,
    //     "s_insType" : "N"
    // };
    command.s_healthCareSettings = healthcareSettings;
    command.s_sessionId          = this.getSessionId();

    return this.sendCommand(command, 5, resultCallback);
};

/** 
  * Delete an archived document from the DMP (TD3.3c). See spec.
  * IN
  *     "s_uniqueId"
  *     "s_ins"
  *     "s_healthCareSettings"
  *     "s_uuid"
  * OUT
  *
  * @note This function only delete an archived document, to delete an active document, use hl_deleteDocument
  */
DMPConnect.prototype.hl_deleteArchivedDocument = function(uid, ins, healthcareSettings, command, resultCallback)
{
    command = command || {};
    command.s_commandName        = "hl_deleteArchivedDocument";
    command.s_uniqueId           = uid;
    // INS number can be given s_ins field:
    command.s_ins                = ins;
    // or using the Ins structure:
    // command.Ins =
    // {
    //     "s_ins"     : ins,
    //     "s_insType" : "N"
    // };
    command.s_healthCareSettings = healthcareSettings;
    command.s_sessionId          = this.getSessionId();

    return this.sendCommand(command, 5, resultCallback);
};

/**
  * @brief Document visibility types.
  */
DMPConnect.DocumentVisibility =
{
    Normal                          : 1,
    PatientHidden                   : 2,
    HealthcareProfesionnalHidden    : 4,
    GuardianHidden                  : 8,
};


/**
 * @brief TD 2.1 Send a document to the DMP.
 *
 * hl_createDmpConnector must have been called.
 *
 * The document must be stored in a local file.
 *
 * IN
 *   "s_fileContentBase64",        // Document content in base64.
 *   "s_ins",
 *   "s_documentTitle",
 *   "s_documentDescription",
 *   "s_documentCategory",         // See specs section 9.1.5 "TABLE 5 CATEGORIES DE DOCUMENT (TYPECODE)".
 *   "i_documentVisibility",       // Normal=1. See enum DocumentVisibility.
 *   "i_documentFormat",           // (*) PlainText = 1, PdfApplication = 5, etc. Cf. enum DocumentFormat.
 *   "s_documentFormatCode"        // (*) PlainText = urn:ihe:iti:xds-sd:text:2008, etc.
 *   "s_documentVersionNumber"     // Optional.
 *   "s_documentSetIdRoot"         // Optional.
 *   "s_documentSetIdExtension"    // Optional.
 *   "s_healthcareSetting",        // See specs section 9.1.8 "TABLE 8 - CADRE DE SOINS (HEALTHCARE SETTINGS)".
 *   "s_replacedDocumentUniqueId", // Optional.
 *   "s_submissionSetTitle",       // Optional.
 *   "s_submissionSetDescription"  // Optional.
 *   "s_submissionSetOid"          // Optional.
 *   "s_localPatientId"            // Optional.
 *   "s_eventCodes"                // Optional.
 *   "i_transcodeTypeCode"         // Optional. If true (1), transform the value of "s_documentCategory" to make sure the most up to date value is sent to the DMP servers. If false (0), an obsolete value of "s_documentCategory" will raise an error. Default Off (0)
 *   "i_forceSchematronsValidation"// Optional. If true (1), enable schematrons validation for unstructured documents (default: off).
 *   "i_retrieveDocumentUuid"      // Optional. If true (1), a supplemental transaction is performed to get technical identifier (UUID) of the document sent. (Default: false). 
 *   "i_disablePdfa1Conversion"    // Optional. If true (1), PDF/A1 conversion is disabled.
 *   "i_pdfa1IgnoreTransparency"   // Optional. If true (1), Transparency is ignored during PDF/A1 conversion.
 *   "i_pdfa1ImageResolution"      // Optional. Set maximum resolution (in DPI) for images during PDF/A1 conversion. If not set default resolution is employed (200 DPI). 
 *   "s_creationDate"              // Optional. Document original creation date. Format: YYYYMMDDhhmmss[+-]hhmm : Local time relative to UTC time (with time difference) + seconds.
 *   "s_serviceStartDate"          // Optional. Start date of the medical act described by the document
 *   "s_serviceStopDate"           // Optional. Ending date of the medical act described by the document.
 *   "s_documentOid"               // Optional. Document OID, only set the value if the structure has it's own OIDs.
 *   "s_styleSheetInBase64"        // Optional. Document stylesheet (xsl). 
 *   Informants                    // Optional, array of Informant.
 *   TreatingPhysician             // Optional, Hp Structure.
 *   AdditionalAuthors             // Optional, Array of HP structure.
 *   Performer                     // Optional, Performer structure { Hp : {}, i_role }
 *
 *  Formats for s_serviceStartDate et s_serviceStopDate: 
 *     YYYYMMJJ               : Local time
 *     YYYYMMDDhhmm[+-]hhmm   : Local time relative to UTC time (with time difference).
 *     YYYYMMDDhhmmss[+-]hhmm : idem + seconds.
 * 
 * (*) User must define the format of the document using either:
 * - i_documentFormat         In this case format is defined in enum DMPConnect.DocumentFormat. 
 * - s_documentFormatCode     In this case format is defined as URN codes (ex: urn:ihe:iti:xds-sd:text:2008, @see table "docFormats" in hl_getInteropCodes). 
 * Note: if both i_documentFormat and s_documentFormatCode are employed, an error is returned.
 *
 * OUT
 *   "s_uniqueId"                   // Sent document unique id (to replace or remove it, etc.)
 *
 * @param {string}              ins
 * @param {string}              fileContentBase64         Document content in base64.
 * @param {string}              documentTitle             Title of the document.
 * @param {string}              documentDescription       Description of the document.
 * @param {string}              documentCategory          See specs section 9.1.5 "TABLE 5 CATEGORIES DE DOCUMENT (TYPECODE)".
 * @param {number}              documentVisibility        Normal=1. See enum DocumentVisibility.
 * @param {number or string}    documentFormat            If documentFormat is an integer : PlainText = 1, PdfApplication = 5, etc. Cf. enum DocumentFormat, if documentFormat is a string:  PlainText = urn:ihe:iti:xds-sd:text:2008, ... .
 * @param {string}              healthcareSetting         See specs section 9.1.8 "TABLE 8 - CADRE DE SOINS (HEALTHCARE SETTINGS)".
 * @param                       replacedDocumentUniqueId  Optional
 * @param                       submissionSetTitle        Optional.
 * @param                       submissionSetDescription  Optional.
 * @param                       EventCodes                Optional.
 * @param {object}              command                   Allow to extend the command for optionals parameters
 * @param {function}            resultCallback            The callback that take the result from sendCommand
 */
DMPConnect.prototype.hl_sendDocument = function( ins,
                                                 fileContentBase64,
                                                 documentTitle,
                                                 documentDescription,
                                                 documentCategory,
                                                 documentVisibility,
                                                 documentFormat,
                                                 healthcareSetting,
                                                 replacedDocumentUniqueId,
                                                 submissionSetTitle,
                                                 submissionSetDescription,
                                                 eventCodes,
                                                 command,
                                                 resultCallback)
{
    command = command || {};
    command.s_commandName               = "hl_sendDocument";

    // INS number can be given using the s_ins field:
    command.s_ins                      = ins;
    // Or using the Ins structure:
    // command.Ins =
    // {
    //     "s_ins"     : ins ,
    //     "s_insType" : "N"
    // };
    command.s_fileContentBase64         = fileContentBase64;
    command.s_documentTitle             = documentTitle;
    command.s_documentDescription       = documentDescription;
    command.s_documentCategory          = documentCategory;
    command.i_documentVisibility        = documentVisibility;
    if( typeof documentFormat === "string" )
    {
        command.s_documentFormatCode        = documentFormat;
    }
    else 
    {
        command.i_documentFormat            = documentFormat;
    }
    command.s_healthcareSetting         = healthcareSetting;
    if (eventCodes)                     command.s_eventCodes                = eventCodes;
    if (replacedDocumentUniqueId)       command.s_replacedDocumentUniqueId  = replacedDocumentUniqueId;
    if (submissionSetTitle)             command.s_submissionSetTitle        = submissionSetTitle;
    if (submissionSetDescription)       command.s_submissionSetDescription  = submissionSetDescription;
    command.s_sessionId                 = this.getSessionId();



    // May take a long time for big documents.
    return this.sendCommand(command, 200, resultCallback);
};

/**
  * Validation of the documents (document are only valided using CDA-XSD and schematrons).
  *     useful for development of CDA documents. 
  *
  * IN
  * {
  *    Documents :
  *    [
  *          {
  *              s_contentInBase64
  *              s_title
  *              s_description
  *              s_category
  *              i_visibility
  *              i_format
  *              s_replacedDocumentUniqueId      (*)
  *              s_oid                           (*)
  *              EventCodes : []                 (*) // Array with event code objects.
  *              s_creationDate                  (*) // Format: YYYYMMDDhhmmss[+-]hhmm
  *              s_serviceStartDate              (*) // Formats: YYYYMMJJ, YYYYMMDDhhmm[+-]hhmm or YYYYMMDDhhmmss[+-]hhmm
  *              s_serviceStopDate               (*)
  *              Informants : [ { } ]            (*) // Array with informant objects.
  *              TreatingPhysician               (*) // Hp Structure.
  *              AdditionalAuthors : [ {} ]      (*) // Array of Hp Stucture 
  *              Performer : {}                  (*) // Performer structure { Hp : {}, i_role }
  *           }
  *    ]
  *    s_ins
  *    s_healthcareSetting
  *    s_submissionSetTitle         (*)
  *    s_submissionSetDescription   (*)
  *    s_localPatientId             (*)
  *    i_transcodeTypeCode          (*) // If true (1), transform the value of "s_category" on each document to make sure the most up to date value is sent to the DMP servers. If false (0), an obsolete value of "s_category" will raise an error. Default Off (0)
  *    i_disablePdfa1Conversion"    (*) If true (1), PDF/A1 conversion is disabled.
  *    i_pdfa1IgnoreTransparency"   (*) If true (1), Transparency is ignored during PDF/A1 conversion.
  *    i_pdfa1ImageResolution       (*) Set maximum resolution (in DPI) for images during PDF/A1 conversion. If not set default resolution is employed (200 DPI). 
  *    i_getCdaHeaders              (*) If true (1), CDA headers are retreived.
  *    i_getCda                     (*) If true (1), Full CDA is retreived.
  *    i_getContent                 (*) If true (1), Content of the document is retrieved. It is either the document if document is unstructured or the Document converted in HTML format if the document is structured. 
  *    s_submissionSetOid           (*) 
  * }
  * OUT 
  * {
  *     Documents (d) : 
  *     [
  *         { s_cdaHeadersInBase64 : "" (a) , s_cdaInBase64 : "" (b), s_contentInBase64 (c) }
  *     ]
  *
  * (a) Only present if i_getCdaHeaders is true (1).
  * (b) Only present if i_getCda        is true (1).
  * (c) Only present if i_getContent    is true (1).
  * (d) Only present if i_getCdaHeaders or i_getCda or i_getContent is true (1) 
  * }
  */
DMPConnect.prototype.hl_validateDocuments = function( ins,
                                                      healthcareSetting,
                                                      submissionSetTitle,
                                                      submissionSetDescription,
                                                      localPatientId,
                                                      documents,
                                                      command,
                                                      resultCallback )
{
    command                 = command || {} ;
    command.s_commandName   = "hl_validateDocuments" ;
    command.s_sessionId     = this.getSessionId() ;

    command.s_ins = ins ;
    // Or using the Ins structure:
    // command.Ins =
    // {
    //     "s_ins"     : ins ,
    //     "s_insType" : "N"
    // };
    command.s_healthcareSetting = healthcareSetting ;
    if( submissionSetTitle )
        command.s_submissionSetTitle = submissionSetTitle ;
    if( submissionSetDescription )
        command.s_submissionSetDescription = submissionSetDescription ;
    if( localPatientId )
        command.s_localPatientId = localPatientId ;

    command.Documents = documents ;

    return this.sendCommand( command, 200 , resultCallback ) ;
}

DMPConnect.prototype.hl_getCdaFromSubmissionSet = function( ins,
                                                            healthcareSetting,
                                                            submissionSetTitle,
                                                            submissionSetDescription,
                                                            localPatientId,
                                                            documents,
                                                            command,
                                                            resultCallback )
{
    command                 = command || {} ;
    command.s_commandName   = "hl_getCdaFromSubmissionSet" ;
    command.s_sessionId     = this.getSessionId() ;

    command.s_ins = ins ;
    // Or using the Ins structure:
    // command.Ins =
    // {
    //     "s_ins"     : ins ,
    //     "s_insType" : "N"
    // };
    command.s_healthcareSetting = healthcareSetting ;
    if( submissionSetTitle )
        command.s_submissionSetTitle = submissionSetTitle ;
    if( submissionSetDescription )
        command.s_submissionSetDescription = submissionSetDescription ;
    if( localPatientId )
        command.s_localPatientId = localPatientId ;

    command.Documents = documents ;

    return this.sendCommand( command, 200 , resultCallback ) ;
}

/**
  *
  * IN: 
  *
  * For each document, the following parameters are available:
  *
  * IN
  * {
  *    Documents :
  *    [
  *          {
  *              s_contentInBase64
  *              s_title
  *              s_description
  *              s_category
  *              i_visibility
  *              i_format
  *              s_replacedDocumentUniqueId      (*)
  *              s_versionNumber                 (*)
  *              s_setIdRoot                     (*)
  *              s_setIdExtension                (*)
  *              s_styleSheetInBase64            (*) // Document stylesheet (xsl). 
  *              EventCodes : []                 (*) // Array with event code objects.
  *              s_creationDate                  (*) // Format: YYYYMMDDhhmmss[+-]hhmm
  *              s_serviceStartDate              (*) // Formats: YYYYMMJJ, YYYYMMDDhhmm[+-]hhmm or YYYYMMDDhhmmss[+-]hhmm
  *              s_serviceStopDate               (*)
  *              Informants : [ { } ]            (*) // Array with informant objects.
  *              TreatingPhysician               (*) // Hp Structure.
  *              AdditionalAuthors : [ {} ]      (*) // Array of Hp Stucture 
  *              Performer : {}                  (*) // Performer structure { Hp : {}, i_role }
  *           }
  *    ]
  *    s_ins
  *    s_healthcareSetting
  *    s_submissionSetTitle         (*)
  *    s_submissionSetDescription   (*)
  *    s_localPatientId             (*)
  *    i_disablePdfa1Conversion     (*) // If true (1), PDF/A1 conversion is disabled.
  *    i_pdfa1IgnoreTransparency    (*) // If true (1), Transparency is ignored during PDF/A1 conversion.
  *    i_pdfa1ImageResolution       (*) // Set maximum resolution (in DPI) for images during PDF/A1 conversion. If not set default resolution is employed (200 DPI). 
  *    i_transcodeTypeCode          (*) // If true (1), transform the value of "s_category" on each document to make sure the most up to date value is sent to the DMP servers. If false (0), an obsolete value of "s_category" will raise an error. Default Off (0)
  *    i_forceSchematronsValidation (*) // If true (1), enable schematrons validation for unstructured documents (default: off).
  * }
  * OUT 
  * {
  *     UniqueIds : Array of unique IDs, one per document, in the same order of the document array.   
  * }
  * (*) Optional arguments.
  */
DMPConnect.prototype.hl_sendDocuments = function( ins,
                                                  healthcareSetting,
                                                  submissionSetTitle,
                                                  submissionSetDescription,
                                                  localPatientId,
                                                  documents,
                                                  command,
                                                  resultCallback )
{
    command                 = command || {} ;
    command.s_commandName   = "hl_sendDocuments" ;
    command.s_sessionId     = this.getSessionId();

    command.s_ins = ins ;
    // Or using the Ins structure:
    // command.Ins =
    // {
    //     "s_ins"     : ins ,
    //     "s_insType" : "N"
    // };
    command.s_healthcareSetting = healthcareSetting ;
    if( submissionSetTitle )
        command.s_submissionSetTitle = submissionSetTitle ;
    if( submissionSetDescription )
        command.s_submissionSetDescription = submissionSetDescription ;
    if( localPatientId )
        command.s_localPatientId = localPatientId ;

    command.Documents = documents ;

    return this.sendCommand( command, 200 , resultCallback ) ;
}

/**
  * @brief Perform TD3.1b transaction.
  *
  * IN 
  * {
  *   s_ins
  *   s_documentUniqueId 
  * }
  * OUT 
  * {
  *   s_documentUuid
  * }
  */
DMPConnect.prototype.hl_getDocumentUuidByUniqueId = function( documentUniqueId, ins, command, resultCallback )
{
    command               = command || {};
    command.s_commandName = "hl_getDocumentUuidByUniqueId" ;
    command.s_sessionId   = this.getSessionId();

    command.s_ins = ins ;
    // Or using the Ins structure:
    // command.Ins =
    // {
    //     "s_ins"     : ins ,
    //     "s_insType" : "N"
    // };
    command.s_documentUniqueId = documentUniqueId;

    return this.sendCommand( command, 30, resultCallback );
}


/**
 * @brief TD 1.3a Get DMP administrative data.
 *
 * hl_createDmpConnector must have been called.
 *
 * IN
 *   "s_ins"
 * OUT
 *   DmpAdministrativeData_t structure. See C headers.
 *
 *   Example of answer:
 *   {
 *     "GuardianData" :
 *     {
 *       "CivilStatus" :
 *       {
 *         "i_gender" : 1
 *         "s_given" : paul
 *         "s_name" : lucien
 *       }
 *
 *       "ContactDetails" :
 *       {
 *         "s_address" :
 *         "s_addressComplement" :
 *         "s_city" :
 *         "s_landlinePhone" :
 *         "s_mail" :
 *         "s_mobilePhone" :
 *         "s_postalCode" :
 *       }
 *
 *       "i_role" : 2
 *     }
 *
 *     "PatientData" :
 *     {
 *       "ExtendedCivilStatus" :
 *       {
 *         "CivilStatus" :
 *         {
 *           "i_gender" : 3
 *           "s_given" : NATHALIE
 *           "s_name" : DESMAUX
 *         }
 *
 *         "i_sex" : 3
 *         "s_birthCountry" :
 *         "s_birthDay" : 19550614
 *         "s_birthName" : DESMAUX
 *       }
 *
 *       "ExtendedContactDetails" :
 *       {
 *         "ContactDetails" :
 *         {
 *           "s_address" : 5 BD ALEXANDRE OYON
 *           "s_addressComplement" : APARTEMENT 50
 *           "s_city" : MARSEILLE
 *           "s_landlinePhone" :
 *           "s_mail" :
 *           "s_mobilePhone" : 0687820756
 *           "s_postalCode" : 13000
 *         }
 *
 *         "s_country" : FRANCE
 *       }
 *
 *       "Ins" :
 *       {
 *         "s_ins" : 0448685716413283718907
 *         "s_insType" : C
 *       }
 *
 *     }
 *
 *     "PatientOppositions" :
 *     {
 *       "i_brisDeGlaceOpposition" : 0
 *       "i_centre15Opposition" : 0
 *     }
 *
 *     "i_guardian" : 2
 *   }
 *
 * @param {string}                  ins
 * @param {object}   command        Allow to extend the command for optionals parameters
 * @param {function} resultCallback The callback that take the result from sendCommand
 */
DMPConnect.prototype.hl_getDmpAdministrativeData = function(ins, command, resultCallback)
{
    command = command || {};
    command.s_commandName = "hl_getDmpAdministrativeData";

    // Use either s_ins field of Ins structure :
    /*
    command.Ins =
    {
         s_ins : ins ,
         s_insType : "N"
    };
    */
    command.s_ins        = ins;


    command.s_sessionId   = this.getSessionId();

    return this.sendCommand(command, 10, resultCallback);
};


/**
 * @brief TD 1.3b Update DMP administrative data.
 *
 * hl_createDmpConnector must have been called.
 *
 * IN
 *   DmpAdministrativeData_t structure. See C headers. (same format as the output of hl_getDmpAdministrativeData)
 *
 *   Example of input
 *   {
 *     "GuardianData" :
 *     {
 *       "CivilStatus" :
 *       {
 *         "i_gender" : 1
 *         "s_given" : paul
 *         "s_name" : lucien
 *       }
 *
 *       "ContactDetails" :
 *       {
 *         "s_address" :
 *         "s_addressComplement" :
 *         "s_city" :
 *         "s_landlinePhone" :
 *         "s_mail" :
 *         "s_mobilePhone" :
 *         "s_postalCode" :
 *       }
 *
 *       "i_role" : 2
 *     }
 *
 *     "PatientData" :
 *     {
 *       "ExtendedCivilStatus" :
 *       {
 *         "CivilStatus" :
 *         {
 *           "i_gender" : 3
 *           "s_given" : NATHALIE
 *           "s_name" : DESMAUX
 *         }
 *
 *         "i_sex" : 3
 *         "s_birthCountry" :
 *         "s_birthDay" : 19550614
 *         "s_birthName" : DESMAUX
 *       }
 *
 *       "ExtendedContactDetails" :
 *       {
 *         "ContactDetails" :
 *         {
 *           "s_address" : 5 BD ALEXANDRE OYON
 *           "s_addressComplement" : APARTEMENT 50
 *           "s_city" : MARSEILLE
 *           "s_landlinePhone" :
 *           "s_mail" :
 *           "s_mobilePhone" : 0687820756
 *           "s_postalCode" : 13000
 *         }
 *
 *         "s_country" : FRANCE
 *       }
 *
 *       "Ins" :
 *       {
 *         "s_ins" : 0448685716413283718907
 *         "s_insType" : C
 *       }
 *
 *     }
 *
 *     "PatientOppositions" :
 *     {
 *       "i_brisDeGlaceOpposition" : 0
 *       "i_centre15Opposition" : 0
 *     }
 *
 *     "i_guardian" : 2
 *   }
 * OUT
 *
 * @param {string}   dmpAdminData The administrative patient data structure
 * @param {function} resultCallback The callback that take the result from sendCommand
 */
DMPConnect.prototype.hl_updateDmpAdministrativeData = function( dmpAdminData , resultCallback )
{
    var command =
    {
        "DmpAdministrativeData" : dmpAdminData,
        "s_commandName"         : "hl_updateDmpAdministrativeData",
        "s_sessionId"           : this.getSessionId()
    };

    return this.sendCommand(command, 10, resultCallback);
}

DMPConnect.Sex =
{
    UnknownSex : 1,
    Male       : 2,
    Female     : 3
};

DMPConnect.Gender =
{
    UnknownGender : 1,
    M             : 2,
    Mme           : 3
};

DMPConnect.GuardianStatus =
{
    NoGuardianDefined : 1,
    GuardianDefined   : 2
};

DMPConnect.GuardianAction =
{
    IgnoreGuardianSetup : 1,
    AddGuardian         : 2,
    RemoveGuardian      : 3
};

DMPConnect.LegalRepresentantRole =
{
    Father           :  1,
    Mother           :  2,
    StepFather       :  3,
    StepMother       :  4,
    GrandFather      :  5,
    GrandMother      :  6,
    GrandGrandFather :  7,
    GrandGrandMother :  8,
    Aunt             :  9,
    Uncle            : 10,
    Brother          : 11,
    Sister           : 12,
    Guardian         : 13
};



/**
 * @brief TD 0.5 Find DMP based on partial information.
 *
 * hl_createDmpConnector must have been called.
 *
 * See specs for the maximum size of each string field.
 *
 *  IN: M = Mandatory data.
 *  {
 *     s_name       [81] Part of the patient name. At least 2 characters.
 *     s_givenName  [61] Patient given name.
 *     s_birthday   [ 9] Patient birthday as YYYYMMDD.
 *     s_city       [39] City.
 *     s_postalCode [11] Postal code.
 *     i_sex             Sex enum.
 *   M i_approxName      Boolean
 *   M i_approxCity      Boolean
 *  }
 *  OUT
 *
 * @param {object}   command        The input parameters.
 * @param {function} resultCallback The callback that take the result from sendCommand
 */
DMPConnect.prototype.hl_findPatients = function( command          ,
                                                 resultCallback   )
{
    command = command || {};
    command.s_commandName = "hl_findPatients";
    command.s_sessionId   = this.getSessionId();

    return this.sendCommand( command, 10, resultCallback );
};

/**
 * @brief TD 1.4 Close a Dmp.
 *
 * hl_createDmpConnector must have been called.
 *
 * See specs for the maximum size of each string field.
 *
 *  IN:
 *    "s_ins"
 *    "s_reason"
 *    "s_name"
 *    "s_given"
 * OUT
 *
 * @param {string}   ins
 * @param {string}   reason
 * @param {string}   name
 * @param {string}   given
 *
 * @param {object}   command        Allow to extend the command for optionals parameters
 * @param {function} resultCallback The callback that take the result from sendCommand
 */
DMPConnect.prototype.hl_closeDmp = function( ins              ,
                                             reason           ,
                                             name             ,
                                             given            ,

                                             command          ,
                                             resultCallback   )
{
    command = command || {};
    command.s_commandName       = "hl_closeDmp";

    // INS can be given using s_ins field :
    command.s_ins              = ins  ;
    // Or using Ins structure
    // command.Ins =
    // {
    //     s_ins : ins ,
    //     s_insType : "N"
    // };

    command.s_reason            = reason;
    command.s_name              = name  ;
    command.s_given             = given ;

    command.s_sessionId         = this.getSessionId();

    return this.sendCommand(command, 10, resultCallback);
};

DMPConnect.AccessMode =
{
    NormalAccess : 1,
    BrisDeGlace  : 2,
    Centre15     : 3,
};

/**
 * @brief Set the DmpConnector access mode.
 *        See specifications, function setDmpConnectorDirectAuthenticationDmpAccessMode().
 * IN (M = Mandatory)
 * {
 *  M i_accessMode : Cf. DMPConnect.AccessMode enum.
 *  M s_reason     : For the "Bris de Glace" mode, holds the description of the reason for opening a DMP in this mode.
 * }
 * OUT
 *
 * @param {number}   accessMode Cf. DMPConnect.AccessMode enum.
 * @param {string}   reason     For the "Bris de Glace" mode, holds the description of the reason for opening a DMP in this mode.
 *
 * @param {object}   command        Allow to extend the command for optionals parameters (none currently)
 * @param {function} resultCallback The callback that take the result from sendCommand
 */
DMPConnect.prototype.hl_setDmpAccessMode = function( accessMode, reason, command, resultCallback )
{
    command = command || {};
    command.s_commandName = "hl_setDmpAccessMode";
    command.i_accessMode  = accessMode;
    command.s_reason      = reason;
    command.s_sessionId   = this.getSessionId();

    return this.sendCommand( command, 2, resultCallback );
};

/**
 * Values for OTP Channel type.
 */
DMPConnect.OTPChannelType =
{
    SMS  : 1,
    Mail : 2
};

/**
  * @brief Get OTP Channel value.
  *
  * If web access does not exists or given channel is not present, an error is returned.
  *
  * IN
  * {
  *     s_ins
  *     i_otpChannelType            1 for SMS, 2 for Mail
  * }
  * OUT
  * {
  *     s_otpChannelValue
  * }
  *
  * User must have an autorization access on the given DMP.
  *
  * @param {string} ins             INS of the patient.
  * @param {int} otpChannelType     Type of the channel to query. 1 for SMS ; 2 for Mail.
  * @param {function} resultCallback The callback that take the result from sendCommand
  */
DMPConnect.prototype.hl_getOtpChannelValue = function( ins, type, resultCallback )
{
    var command = {};

    command.s_commandName          = "hl_getOtpChannelValue";
    command.s_ins                  = ins;
    command.i_otpChannelType       = type;
    command.s_sessionId            = this.getSessionId();

    return this.sendCommand( command, 20, resultCallback );
};

/**
 * Set or reset the patient PDF form.
 *
 * If no channel is given, the function will try to reset the patient credentials.
 * It can fail if no OTP was defined in the past.
 *
 * The function must be called with either one of the channel defined, or none of them.
 *
 * Set the channel value to 'remove' to delete it, but please note that:
 * - One channel must remain defined.
 * - It is not possible to know if a channel is defined.
 *
 * IN * = optional
 * {
 *    s_ins
 * *  s_otpPhone :
 *   OR
 * *  s_otpEmail : set to 'remove' to delete it. It is impossible to remove both channels.
 * *  i_returnAsFile : if it is set to 1, the pdf file stored on the system (in a temporary directory) is returned.
 * }
 * OUT
 * {
 *    s_pdfFileContentInBase64
 *    s_pdfFileUrl : returned if i_returnAsFile is set to 1
 * }
 *
 * @param {string}   ins            INS of the patient.
 * @param {string}   otpPhone       Optional.
 * @param {string}   otpEmail       Optional.
 * @param {integer}  returnAsFile   Optional. Set to 1 to keep the generated pdf on the client temporary directory.
 * @param {function} resultCallback The callback that take the result from sendCommand
 */
DMPConnect.prototype.hl_getPatientWebAccessPdf = function( ins, otpPhone, otpEmail, returnAsFile, openPdf, resultCallback )
{
    otpPhone     = otpPhone     || "";
    otpEmail     = otpEmail     || "";
    returnAsFile = returnAsFile || 0;

    var command = {};
    command.s_commandName          = "hl_getPatientWebAccessPdf";
    command.s_ins                  = ins;
    command.i_openPdfAfterCreation = openPdf;

    if( otpPhone.length )
        command.s_otpPhone = otpPhone;

    if( otpEmail.length )
        command.s_otpEmail = otpEmail;

    command.i_returnAsFile = returnAsFile;

    command.s_sessionId    = this.getSessionId();

    return this.sendCommand( command, 20, resultCallback );
};


/**
  * Create Web Access for the patient.
  *
  *
  * IN 
  * {
  *         s_ins                       INS of the patient.
  *     (*) s_otpPhone                  OTP value for SMS channel.
  *     (*) s_otpEmail                  OTP value for Email channel.
  *     (*) i_openPdfAfterCreation      if = 1, try to open the file with the system default pdf viewer.
  *     (*) i_returnAsFile              if = 1, the pdf file stored on the system (in a temporary directory) is returned.
  * }
  * OUT 
  * {
  *        s_pdfFileContentInBase64  Access creation PDF.
  *    (*) s_pdfFileUrl              returned if i_returnAsFile is set to 1
  * }
  *
  * (*) Optional. 
  *
  *
  * Notes:
  * - if WebAccess already exists an error is returned.
  * - If both s_otpPhone and s_otpEmail are defined, web access is created using SMS channel then Mail channel is added.
  */
DMPConnect.prototype.hl_createWebAccess = function( ins, otpPhone, otpEmail, returnAsFile, openPdf, resultCallback )
{
    otpPhone     = otpPhone     || "";
    otpEmail     = otpEmail     || "";
    returnAsFile = returnAsFile || 0;

    var command = {};
    command.s_commandName          = "hl_createWebAccess";
    command.s_ins                  = ins;
    command.i_openPdfAfterCreation = openPdf;
    command.s_sessionId            = this.getSessionId();

    if( otpPhone.length )
        command.s_otpPhone = otpPhone;

    if( otpEmail.length )
        command.s_otpEmail = otpEmail;

    command.i_returnAsFile = returnAsFile;

    return this.sendCommand( command, 20, resultCallback );
}

/**
  * Add a channel to the Web Access of the patient.
  * 
  * IN
  * {
  *         s_ins                       INS of the patient.
  *     (*) s_otpPhone                  OTP value for SMS channel.
  *     (*) s_otpEmail                  OTP value for Email channel.
  * }
  * OUT 
  * {
  *  
  * }
  *
  * Notes: 
  * - Web Access must exists (if not use hl_createWebAccess).
  * - Only one channel could be added.
  * - If channel already exists an error is returned (to update value use hl_updateWebAccessChannel)
  */
DMPConnect.prototype.hl_addWebAccessChannel = function( ins, otpPhone, otpEmail, resultCallback )
{
    otpPhone     = otpPhone     || "";
    otpEmail     = otpEmail     || "";

    var command = {};
    command.s_commandName          = "hl_addWebAccessChannel";
    command.s_ins                  = ins;
    command.s_sessionId            = this.getSessionId();

    if( otpPhone.length )
        command.s_otpPhone = otpPhone;

    if( otpEmail.length )
        command.s_otpEmail = otpEmail;

    return this.sendCommand( command, 20, resultCallback );
}

/**
  * Update OTP channel(s) value(s).
  *
  * IN 
  * {
  *         s_ins                       INS of the patient.
  *     (*) s_otpPhone                  OTP value for SMS channel.
  *     (*) s_otpEmail                  OTP value for Email channel.
  * }
  * OUT 
  * {
  *      
  * }
  * 
  * Notes: 
  * - Web Access must exists (if not use hl_createWebAccess).
  * - Channel must exist (if not use hl_addWebAccessChannel).
  * - Both channels could be updated, the order of the update is SMS then Mail. 
  */
DMPConnect.prototype.hl_updateWebAccessChannel = function( ins, otpPhone, otpEmail, resultCallback )
{
    otpPhone     = otpPhone     || "";
    otpEmail     = otpEmail     || "";

    var command = {};
    command.s_commandName          = "hl_updateWebAccessChannel";
    command.s_ins                  = ins;
    command.s_sessionId            = this.getSessionId();

    if( otpPhone.length )
        command.s_otpPhone = otpPhone;

    if( otpEmail.length )
        command.s_otpEmail = otpEmail;

    return this.sendCommand( command, 20, resultCallback );
}

/**
 * TD 0.9: Get the Web PS DMP Urls for a patient.
 * IN * = optional
 * {
 *    s_ins
 * }
 * OUT
 * {
 *    Urls : [
 *       "url of TableauDeBord",
 *       "url of DossierPatient",
 *       "url of GestionDMPPatient",
 *       "url of Documents",
 *       "url of ParcoursDeSoins",
 *       "url of HistoriqueAcces",
 *       "url of Parametrages",
 *       "url of VolontesEtDroits",
 *       "url of CarnetDeVaccination",
 *    ]
 * }
 *
 * @param {string}   ins            INS of the patient.
 * @param {function} resultCallback The callback that take the result from sendCommand
 */
DMPConnect.prototype.hl_getWebPsDmpUrls = function( ins, resultCallback )
{
    var command = {};
    command.s_commandName  = "hl_getWebPsDmpUrls";
    // INS can be given using the s_ins field :
    command.s_ins         = ins;
    // or using the Ins structure:
    // command.Ins =
    // {
    //     "s_ins" : ins ,
    //     "s_insType" : "N"
    // } ;

    command.s_sessionId    = this.getSessionId();

    return this.sendCommand( command, 2, resultCallback );
};

/**
  * @brief Set custom webPS root URL.
  *
  * IN
  * {
  *     s_url
  * }
  * OUT
  * {
  *
  * }
  *
  * @param {string} url                 New root URL for the WebPS urls. Set it to an empty string to reset to the default value.
  * @param {function} resultCallback    The callback that take the result from sendCommand
  */
DMPConnect.prototype.hl_setWebPsRootUrl = function( url, resultCallback )
{
    var command = {};
    command.s_commandName = "hl_setWebPsRootUrl";
    command.s_url         = url;

    command.s_sessionId = this.getSessionId();

    return this.sendCommand( command, 2 , resultCallback );
}


/**
 * @brief Query the DMP parameters to get the majority age
 *
 * IN
 * {
 *
 * }
 * OUT
 * {
 *    i_majorityAge
 * }
 */
DMPConnect.prototype.hl_getMajorityAge = function( resultCallback )
{
    var command = {};
    command.s_commandName = "hl_getMajorityAge" ;
    command.s_sessionId   = this.getSessionId() ;

    return this.sendCommand( command , 20 , resultCallback ) ;
}

/**
 * @brief Set confidentiality level for DMP transactions.
 *
 * @param {int} aConfidentialityEnabler     Set to 1 to enable secret connection, set to 0 to disable it.
 */
DMPConnect.prototype.hl_setConfidentialityLevel = function( aConfidentialityEnabler, resultCallback )
{
    var command = {};
    command.s_commandName            = "hl_setConfidentialityLevel";
    command.s_sessionId              = this.getSessionId();
    command.i_enableSecretConnection = aConfidentialityEnabler;

    return this.sendCommand(command, 15, resultCallback );
}

DMPConnect.DmpAuthorizationListType =
{
    AllAuthorizations: 1,
    AuthorizedUsers: 2,
    DeniedUsers: 3
};

/**
 * @brief Get HP authorization list for a DMP.
 *
 * @param {string} anIns                    INS of the DMP to test.
 * @param {int} anAuthorizationListType     Authorization list type (using values in DMPConnect.DmpAuthorizationListType)
 *
 * IN
 * {
 *    "s_ins"
 *    "i_authorizationType" (*)         // Default value is 1.
 * }
 * OUT
 * {
 *      AuthorizationList :
 *      [
 *          {
 *              s_nationalId            // National Identifier.
 *              s_nationalIdType        // Hp national identifier type.
 *              s_name                  // Hp name.
 *              s_given                 // Hp given.
 *              s_specialityCode        // Hp speciality code. Eg. "G15_10/SM36".
 *              s_speciality            // Hp speciality display. Eg. "Oncologie option médicale (SM)".
 *              s_authorizationStart    // Hp authorization start date (format YYYYMMDDHHmmss).
 *              s_lastActionDate        // Hp last action date (format YYYYMMDDHHmmss).
 *              i_authorizationType     // Hp authorization type. Values in enum HpAuthorizationType (ie: 1 => AuthorizedHp, 2 => DeniedHp).
 *              i_generalPractitionner  // Set to 1 if physician is "médecin traitant".
 *          }
 *          ,
 *      ]
 * }
 */
DMPConnect.prototype.hl_getDmpAuthorizationsList = function( anIns, anAuthorizationListType, resultCallback )
{
    var command = {};
    command.s_commandName           = "hl_getDmpAuthorizationsList" ;
    command.s_sessionId             = this.getSessionId();
    command.s_ins                   = anIns;
    command.i_authorizationType     = anAuthorizationListType;

    return this.sendCommand(command, 30, resultCallback);
}

/**
  * @brief Get DMP server parameters.
  *
  * IN
  * {
  *     
  * }
  * OUT 
  * { 
  *     i_hrMaxSearchPeriod         // (month).
  *     i_majorityAge               // (year).
  *     i_minorFunctionManagement   // (bool: 1/0)
  *     i_cumulMasquePsPatient      // (bool: 1/0)
  * }
  */
DMPConnect.prototype.hl_getDmpParameters = function( resultCallback )
{
    var command = {};
    command.s_commandName           = "hl_getDmpParameters" ;
    command.s_sessionId             = this.getSessionId();

    return this.sendCommand( command, 30, resultCallback );
}


DMPConnect.InsIdentityResult =
{
    InsIdentityResult_NoIdentityFound         : 1,
    InsIdentityResult_SingleIdentityFound     : 2,
    InsIdentityResult_MultipleIdentitiesFound : 3
};


/**
 * @brief INSi WS #1 transaction: Get the INS from Vitale card data.
 * 
 * The transaction is performed on the i-th patient read in vitale card.
 * The Vitale card must have been read before calling this function.
 * 
 * @param {int} aVitaleCardIndex        Index of the patient to use as input of the function.
 *  
 *  OUT
 *   {
 *       i_insIdentityResult :         // See enum InsIdentityResult above.
 *   
 *       if i_insIdentityResult is (2) InsIdentityResult_SingleIdentityFound, the following structure is also returned:
 *   
 *       Identity
 *       {
 *         Ins :
 *         {
 *           s_value
 *           s_key
 *           s_oid
 *           s_startDate         // Format YYYY-MM-DD
 *           s_endDate           // Format YYYY-MM-DD
 *         }
 *         s_birthName
 *         s_given
 *         s_birthGiven
 *         i_sex
 *         s_birthDate           // Format YYYY-MM-DD
 *         s_birthPlace          // Format COG ('Code Officiel Géographique' : https://www.insee.fr/fr/information/2560452) 
 *         s_lastInsiCallDate    // Format YYYY-MM-DD
 *         History :                 (*) Only present if there is an history
 *         [
 *           {
 *             s_value
 *             s_key
 *             s_oid
 *             s_startDate       // Format YYYY-MM-DD
 *             s_endDate         // Format YYYY-MM-DD
 *           },
 *           ...
 *         ]
 *       }
 *   }
 */
DMPConnect.prototype.hl_getInsFromVitaleCard = function( lpsParams, aVitaleCardIndex, resultCallBack )
{
    var command = 
    {
        LpsInfos               : lpsParams,
        "i_vitalePatientIndex" : aVitaleCardIndex,

        "s_commandName"        : "hl_getInsFromVitaleCard" ,
        "s_sessionId"          : this.getSessionId()
    };

    return this.sendCommand( command, 60, resultCallBack );
}

/**
  * @brief INSi WS #1 transaction : Get INS from raw Vitale data.
  * 
  * @param {object} lpsParams 
  * @param {object} aBeneficiaryVitaleData 
  * @param {object} aRightHolderVitaleData 
  * @param {string} aVitaleSerialNum 
  * @param {function} resultCallback 
  * 
  * IN
  * {
  *     LpsInfos :              // Software-related informations (authorization numbers, etc.)
  *     {
  *        s_idam    [59],      // Software authorization number. From the CNDA.
  *        s_numAM   [ 2],      // IDAM type. Currently must be set to "4" (Authorization number).
  *        s_version [59],      // Software version. No specific format. E.g. "01.00", or "1.0", or "2".
  *        s_instance[59],      // Software instance UUID in its canonical form. See ISO/IEC 9834‐8 et RFC 4122.
  *                             // Must be set, unique and constant per software instance/installation.
  *                             // E.g. "550e8400‐e29b‐41d4‐a716‐446655440000"
  *        s_name   [59],      // The software name.
  *        s_billingNumber[33]  // Billing situation. Either FINESS for institutions or AM number in all other cases. Mandatory for INSi.
  *     },
  *     BeneficiaryVitaleData : // Raw Vitale data of the patient.
  *     {
  *         s_name     [35],
  *         s_birthName[27],
  *         s_givenName[35],
  *         s_birthday [ 8],    // May be 6 digits-long. May be defined in lunar calendar.
  *         i_rank,
  *         i_qualBenef,
  *         NirData :
  *         {
  *             Nir :
  *             {
  *                 s_nir[13],
  *                 s_key[2]
  *             },
  *             CertifiedNir :
  *             {
  *                 s_nir[13],
  *                 s_key[2]
  *             },
  *             s_certificationDate[8]
  *         },
  *         HealthCoverage :
  *         {
  *             s_regime[2],    // E.g. "01"
  *             s_desk  [3],    // E.g. "349"  a.k.a "Organisme", "Caisse".
  *             s_center[4]     // E.g. "9881" a.k.a "Entité de gestion", "Centre".
  *         }
  *    },
  *    RightHolderVitaleData : // Raw Vitale data of the Vitale card holder.
  *    {
  *         s_birthName     [27],
  *         NirData :
  *         {
  *             Nir :
  *             {
  *                 s_nir[13],
  *                 s_key[2]
  *             },
  *         },
  *    },
  *    s_serialNumber
  * }
  * OUT
  * {
  *    i_insIdentityResult :         // enum InsIdentityResult
  *
  *  // if i_insIdentityResult is (2) InsIdentityResult_SingleIdentityFound:
  *
  *    Ins :
  *    {
  *      s_value
  *      s_key
  *      s_oid
  *      s_startDate         // Format YYYY-MM-DD
  *      s_endDate           // Format YYYY-MM-DD
  *    }
  *    s_birthName
  *    s_given
  *    s_birthGiven
  *    i_sex
  *    s_birthDate           // Format YYYY-MM-DD
  *    s_birthPlace          // Format COG ('Code Officiel Géographique' : https://www.insee.fr/fr/information/2560452)
  *    s_lastInsiCallDate    // Format YYYY-MM-DD
  *    History :                 (*) Only present if there is an history
  *    [
  *      {
  *        s_value
  *        s_key
  *        s_oid
  *        s_startDate       // Format YYYY-MM-DD
  *        s_endDate         // Format YYYY-MM-DD
  *      },
  *      ...
  *    ]
  * }
  * 
  */
DMPConnect.prototype.hl_getInsFromRawVitaleData = function( lpsParams, aBeneficiaryVitaleData, aRightHolderVitaleData, aVitaleSerialNum, resultCallback )
{
    var command = 
    {
        LpsInfos              : lpsParams,
        BeneficiaryVitaleData : aBeneficiaryVitaleData,
        RightHolderVitaleData : aRightHolderVitaleData,
        "s_serialNumber"      : aVitaleSerialNum,

        "s_commandName" : "hl_getInsFromRawVitaleData",
        "s_sessionId"          : this.getSessionId()
    };

    return this.sendCommand( command, 60, resultCallback );
}

/**
  * @brief INSi WS #2 transaction: Get the INS from manually defined patient data.
  * 
  * Input requirements:
  * 
  * The input strings of this transaction must:
  * - only contains capitals letters ;
  * - not contain symbols (no accent, no cedilla) ;
  * - only contains hyphen ('-') and space (' ') as special characters, and hyphen and space cannot be doubled.
  * 
  * The sex can not be "unknown".
  * 
  * The expected date format is YYYY-MM-DD.
  * 
  * The birth place format is COG (Code Officiel Géographique, @see https://www.insee.fr/fr/information/2560452 )
  * 
  * @param {string} name        (Birth) name of the patient
  * @param {string} given       Given of the patient 
  * @param {int}    sex         Sex of the patient (Values in DMPConnect.Sex enum)
  * @param {string} date        Birth date (format YYYY-MM-DD)
  * @param {string} place       (Optional) Birth place 
  * 
  *  OUT
  *   {
  *       i_insIdentityResult :         // enum InsIdentityResult
  *   
  *       if i_insIdentityResult is (2) InsIdentityResult_SingleIdentityFound, the following structure is also returned:
  *   
  *       Identity
  *       {
  *         Ins :
  *         {
  *           s_value
  *           s_key
  *           s_oid
  *           s_startDate         // Format YYYY-MM-DD
  *           s_endDate           // Format YYYY-MM-DD
  *         }
  *         s_birthName
  *         s_given
  *         s_birthGiven
  *         i_sex
  *         s_birthDate           // Format YYYY-MM-DD
  *         s_birthPlace          // Format COG ('Code Officiel Géographique' : https://www.insee.fr/fr/information/2560452)
  *         s_lastInsiCallDate    // Format YYYY-MM-DD
  *         History :                 (*) Only present if there is an history
  *         [
  *           {
  *             s_value
  *             s_key
  *             s_oid
  *             s_startDate       // Format YYYY-MM-DD
  *             s_endDate         // Format YYYY-MM-DD
  *           },
  *           ...
  *         ]
  *       }
  *   }
  */
 DMPConnect.prototype.hl_getInsFromIdentityInformation = function( lpsParams, name, given, sex, date, place, resultCallBack )
 {
    var command = 
    {
        LpsInfos               : lpsParams,

        "s_birthName"          : name ,
        "s_given"              : given ,
        "i_sex"                : sex ,
        "s_birthDate"          : date,
        "s_birthPlace"         : place,

        "s_commandName"        : "hl_getInsFromIdentityInformation" ,
        "s_sessionId"          : this.getSessionId()
    };

    return this.sendCommand( command, 60, resultCallBack );
 }



 /**
   * @brief INSi WS #3 transaction: Check the INS validity wrt the patient identity informations.
   *
   * IN 
   * {
   *    LpsInfos :              // Software-related informations (authorization numbers, etc.)
   *     {
   *        s_idam    [59],      // Software authorization number. From the CNDA.
   *        s_numAM   [ 2],      // IDAM type. Currently must be set to "4" (Authorization number).
   *        s_version [59],      // Software version. No specific format. E.g. "01.00", or "1.0", or "2".
   *        s_instance[59],      // Software instance UUID in its canonical form. See ISO/IEC 9834-8 et RFC 4122.
   *                             // Must be set, unique and constant per software instance/installation.
   *                             // E.g. "110e8400-e29b-11d4-a716-446655440000"
   *        s_name    [59],      // The software name.
   *        s_billingNumber[33]  // Billing situation. Either FINESS for institutions or AM number in all other cases. Mandatory for INSi.
   *     },
   *         s_ins[13],
   *         s_key[2],
   *         s_oid[32],
   *         s_birthName[80],
   *         s_given[61],
   *         i_sex,
   *         s_birthDate[10],          // Format YYYY-MM-DD
   *     (*) s_birthPlace[5],          // Format COG ('Code Officiel Géographique' : https://www.insee.fr/fr/information/2560452)
   *  } 
   * OUT 
   * {
   *     "Ok" if validation succeeded or ERROR if validation failed (check error detail for more infos)
   * }
   */
 DMPConnect.prototype.hl_checkInsIdentity = function( lpsParams, ins, key, oid, name, given, sex, date, place, resultCallBack )
 {
     var command =
     {
        LpsInfos        : lpsParams,

        "s_ins"         : ins,
        "s_key"         : key,
        "s_oid"         : oid,
        "s_birthName"   : name,
        "s_given"       : given,
        "i_sex"         : sex, 
        "s_birthDate"   : date,
        "s_birthPlace"  : place,
     
        "s_commandName" : "hl_checkInsIdentity",
        "s_sessionId"   : this.getSessionId()    
    };

    return this.sendCommand( command, 60, resultCallBack );
 }

 /**
 * hl_getCpxCard must have been called.
 *
 * Optional parameters that can be set in 'optionalParameters' parameter:
 *   HTTP proxy:
 *     "s_proxyIpOrUrl"
 *     "i_proxyPort"
 *     "s_proxyLogin"
 *     "s_proxyPassword"
 *  Custom NTP server:
 *     "s_ntpIpOrUrl"
 *     "i_ntpPort"
 *
 * Returns :
 * - s_instanceUniqueId the instance unique ID of the software. The number is unique per user account and do not change
 *                      between restarts or software updates. Not used here for TLSi transactions.
 * - i_connectionHandle the handle of the connection used for TLSi transactions
 *
 * { [...]
 *    "s_instanceUniqueId" : "2.25.186812548863990774547154938929646584355"
 *    "i_connectionHandle" : 262145
 * }
 *
 * @param {string}   pinCode                      E.g. "1234" for all test cards by default.
 * @param {string}   serverFullUrl                TLSi service URL.
 *                                                Set it to an empty string to use the production DMP.
 * @param {number}   transactionsTimeoutInSeconds Transactions timeout.
 * @param {string}   practiceSetting              E.g. "AMBULATOIRE". See spec.
 * @param {number}   cpsPracticeLocationIndice    Practice location to use. Indice in the CPx card list of practices locations.
 * @param {function} resultCallback               The callback that take the result from sendCommand
 */
DMPConnect.prototype.hl_createInsiConnector = function( pinCode, serverFullUrl, transactionsTimeoutInSeconds, practiceSetting, cpsPracticeLocationIndice, optionalParameters, resultCallback )
{
    var serverUrl = "";

    // Empty string => qualification server.
    if( serverFullUrl === "" )
        serverUrl = "https://qualiflps.services-ps.ameli.fr";
    else
        serverUrl = serverFullUrl;

    var command = {
        "s_commandName"               : "hl_createTlsiConnector",
        "s_tlsiUrl"                   : serverUrl,
        "i_transactionsTimeout"       : transactionsTimeoutInSeconds,
        "s_practiceSetting"           : practiceSetting,
        "i_cpsPracticeLocationIndice" : cpsPracticeLocationIndice,
        "i_getConnectionHandle"       : 1,
        "s_sessionId"                 : this.getSessionId()
    };

    if( pinCode.length == 4 )
    {
        command.s_pinCode = pinCode;
    }
if( pinCode.length > 4 )    {        command.s_authenticationToken = pinCode;    }


    // Merge the HTTP proxy parameters and/or the NTP proxy parameters set in optionalParameters.
    if( optionalParameters )
    {
        for( var p in optionalParameters )
        {
            command[ p ] = optionalParameters[ p ];
        }
    }

    // Get connection handle 
    return this.sendCommand( command, 20, resultCallback );
};




/** 
  * @brief Submit batch of verification ( WS_INS4 WebService)
  * 
  *  IN
  * {
  *      LpsInfos :              // Software-related informations (authorization numbers, etc.)
  *     {
  *        s_idam    [59],      // Software authorization number. From the CNDA.
  *        s_numAM   [ 2],      // IDAM type. Currently must be set to "4" (Authorization number).
  *        s_version [59],      // Software version. No specific format. E.g. "01.00", or "1.0", or "2".
  *        s_instance[59],      // Software instance UUID in its canonical form. See ISO/IEC 9834‐8 et RFC 4122.
  *                             // Must be set, unique and constant per software instance/installation.
  *                             // E.g. "550e8400‐e29b‐41d4‐a716‐446655440000"
  *        s_name    [59],      // The software name.
  *        s_billingNumber[33]  // Billing situation. Either FINESS for institutions or AM number in all other cases. Mandatory for INSi.
  *     },
  *    Patients:
  *    [
  *      {
  *        s_ins[13],
  *        s_key[2],
  *        s_oid[32],
  *        s_birthName[80],
  *        s_given[61],
  *        s_birthGiven[61],
  *        i_sex,
  *        s_birthDate[10],          // Format YYYY-MM-DD
  *    (*) s_birthPlace[5],          // Format COG ('Code Officiel Géographique' : https://www.insee.fr/fr/information/2560452)
  *      },
  *      {
  *             ...
  *      }
  *    ]
  * }
  * OUT
  * {
  *   s_batchId            // Identifier of the batch (needed to get the result - hl_getInsiBatchValidationSetResult).
  *   i_estimationDelay    // In seconds.
  *
  *   s_processingDate     // Date when the processing started.    
  *   s_submissionDate     // Date when the submission occured.
  *   i_requestSize        // Number of patient submitted.
  * }
  */
DMPConnect.prototype.hl_submitInsiBatchValidationSet = function( lpsParams, patientList, resultCallBack )
{
    var command =
    {
        "s_commandName" : "hl_submitInsiBatchValidationSet",
        "s_sessionId"   : this.getSessionId(),

        LpsInfos        : lpsParams,
        Patients        : patientList
    };

    return this.sendCommand( command, 20, resultCallBack );
}

/** 
  * @brief Get result of batch verification.
  * 
  *  IN
  *  {
  *    s_batchId           // Identifier of the batch we want the result of (batchId is in the result of hl_submitInsiBatchValidationSet)
  *  }
  *  OUT
  * {
  *    i_batchValidationResult     // 1,2,3,4.
  *    s_batchValidationResult  
  *    (*) i_estimationDelay       // New delay if computation is not yet finished.
  *    Result :
  *    [
  *      {
  *        i_validationResult      // 1 -> valid ; 2 -> invalid.
  *        "Ins":                  // Only present if validation is Ok
  *        {
  *            "s_ins"
  *            "s_key"
  *            "s_oid"
  *        }
  *      }
  *    ]
  * } 
  *  
  */
DMPConnect.prototype.hl_getInsiBatchValidationSetResult = function( lpsParams, batchId, resultCallBack )
{
    var command =
    {
        "s_commandName" : "hl_getInsiBatchValidationSetResult",
        "s_sessionId"   : this.getSessionId(),

        LpsInfos        : lpsParams,
        "s_batchId"     : batchId
    };

    return this.sendCommand( command, 20, resultCallBack );
}

/**
  * @brief get DataMatrix related to a patient.
  *
  * IN 
  * {
  *     (*) i_getBinary
  *     (*) i_getBmpImage       
  *     Identity                // result of INSI service.
  * }
  * OUT 
  * {
  *     DataMatrixBinaryArray  : []             // Only if i_getBinary is 1 (2D array, data is Row wise).
  *     i_dataMatrixBinaryHeight                // Only if i_getBinary is 1
  *     i_dataMatrixBinaryWidth                 // Only if i_getBinary is 1
  *     s_dataMatrixBmpImageContentInBase64     // Only if i_getBmpImage is 1
  * }
  * 
  */
DMPConnect.prototype.hl_getDataMatrix = function( Identity, i_getBinary, i_getBmpImage, i_getCode, resultCallBack )
{
    var command = {};

    command.s_commandName = "hl_getDataMatrix";
    command.s_sessionId   = this.getSessionId();
    command.Identity      = Identity;
    command.i_getBinary   = i_getBinary;        // Optional
    command.i_getBmpImage = i_getBmpImage;      // Optional
    command.i_getCode     = i_getCode;          // In future release.

    return this.sendCommand( command, 20, resultCallBack );
}

export default DMPConnect
